// ========================================
// ADMIN PORTAL - ADMIN ONLY - FULL PAGE
// Tabbed dashboard: Overview | Analytics | Recent Activity | API Keys | Reports
// Provides admin-level access to usage/cost analytics, API key management, and team administration
// ========================================

import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useMsal } from '@azure/msal-react';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as ReTooltip,
    Legend, ResponsiveContainer, PieChart, Pie, Cell,
    BarChart, Bar, AreaChart, Area
} from 'recharts';
import { getTeamSpend, getSpendLogs, getLiteLLMUsers, getHealthCheckSpend, getUsageCostData, getDepartments, triggerDepartmentSync, addAdminUser, removeAdminUser, getAllUsers, blockTeam, unblockTeam, updateTeamBudget, disableUserAccount, getAdminUsers } from '../services/api';
import { authenticatedFetch } from '../services/authService';
import KeyManagementTab from './KeyManagementTab';

// ── Formatters ────────────────────────────────────────────────────────────────

const fmt2 = v => {
    const n = Number(v || 0);
    if (n === 0) return '$0.00';
    if (n < 0.01) return '<$0.01';
    return `$${n.toFixed(2)}`;
};
const fmtSpendDetail = v => {
    const n = Number(v || 0);
    if (n === 0) return '$0.00';
    if (n < 0.01) return `$${n.toFixed(4)}`;
    return `$${n.toFixed(2)}`;
};
const fmtTokens = v => {
    v = v || 0;
    if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
    if (v >= 1_000) return `${(v / 1_000).toFixed(1)}K`;
    return String(v);
};
const fmtDuration = d => {
    if (!d) return 'No reset';
    if (d === '1d' || d === '24h') return 'Daily';
    if (d === '7d') return 'Weekly';
    if (d === '30d') return 'Monthly';
    if (d === '365d') return 'Yearly';
    return d;
};
const fmtDate = iso => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
    catch { return iso; }
};
const fmtDateShort = iso => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit' }); }
    catch { return iso; }
};
const fmtDateOnly = dateStr => {
    if (!dateStr) return '—';
    try {
        // Parse YYYY-MM-DD and format to DD/MM/YYYY
        const [year, month, day] = dateStr.split('-');
        return `${day}/${month}/${year}`;
    }
    catch { return dateStr; }
};

// ── Model name mapping ────────────────────────────────────────────────────────
// Map technical model names to user-friendly display names
const MODEL_DISPLAY_NAMES = {
    // Anthropic Claude models (with US Bedrock prefix)
    'us.anthropic.claude-sonnet-4-6': 'Claude Sonnet 4.6',
    'us.anthropic.claude-sonnet-4-5-20250929-v1:0': 'Claude Sonnet 4.5',
    'us.anthropic.claude-opus-4-1-20250805-v1:0': 'Claude Opus 4.1',
    // Anthropic Claude models (without prefix)
    'claude-sonnet-4-6': 'Claude Sonnet 4.6',
    'claude-sonnet-4-5-20250929-v1:0': 'Claude Sonnet 4.5',
    'claude-opus-4-1-20250805-v1:0': 'Claude Opus 4.1',
    'claude-sonnet-3-5': 'Claude Sonnet 3.5',
    'claude-opus-3': 'Claude Opus 3',
    'claude-haiku-3-5': 'Claude Haiku 3.5',
    // OpenAI models
    'gpt-4o': 'GPT-4o',
    'gpt-5': 'GPT-5',
    'gpt-5-nano': 'GPT-5 Nano',
    'gpt-5.6-luna': 'GPT-5.6 Luna',
    'gpt-5.6-terra': 'GPT-5.6 Terra',
    'gpt-4-turbo': 'GPT-4 Turbo',
    'gpt-4': 'GPT-4',
    'gpt-3.5-turbo': 'GPT-3.5 Turbo',
    'o3': 'O3',
    // Google Gemini models
    'gemini-3-pro-preview': 'Gemini 3 Pro Preview',
    'gemini-3-flash-preview': 'Gemini 3 Flash Preview',
};

// Format model names to be user-friendly
// Strips routing provider prefixes and maps to friendly names
// e.g. "converse/us.anthropic.claude-sonnet-4-6" → "Claude Sonnet 4.6"
//      "openai/gpt-4o" → "GPT-4o"
//      "gpt-5-nano" → "GPT-5 Nano"
const fmtModelName = m => {
    if (!m) return m;

    // Strip routing provider prefix (e.g., "converse/", "openai/", "azure_ai/")
    const parts = m.split('/');
    let baseName = parts[parts.length - 1];

    // Strip regional deployment suffix added by auto-router (e.g. "gpt-5.6-luna-sea" → "gpt-5.6-luna")
    baseName = baseName.replace(/-[a-z]{2,4}$/, (suffix) => {
        // Only strip if it looks like a regional code, not a meaningful suffix like "-turbo" or "-nano"
        const regionalCodes = ['-sea', '-us', '-eu', '-ap'];
        return regionalCodes.includes(suffix) ? '' : suffix;
    });

    // Direct mapping lookup
    if (MODEL_DISPLAY_NAMES[baseName]) {
        return MODEL_DISPLAY_NAMES[baseName];
    }

    // Smart parsing for Claude models not in the mapping
    if (baseName.includes('claude')) {
        let display = baseName
            .replace(/^us\.anthropic\./, '')
            .replace(/^anthropic\./, '')
            .replace(/claude-/, 'Claude ')
            .replace(/sonnet-/, 'Sonnet ')
            .replace(/opus-/, 'Opus ')
            .replace(/haiku-/, 'Haiku ')
            .replace(/-\d{8}-v\d+:\d+$/, '') // Remove date-version suffix
            .replace(/-v\d+:\d+$/, ''); // Remove version suffix

        // Capitalize first letter of each word
        return display.split(' ').map(w =>
            w.charAt(0).toUpperCase() + w.slice(1)
        ).join(' ');
    }

    // Smart parsing for GPT models not in the mapping
    if (baseName.startsWith('gpt-')) {
        let display = baseName.replace(/^gpt-/, 'GPT-');
        // Capitalize first letter of suffix words (turbo, nano, mini, etc.)
        display = display.replace(/-([a-z])/g, (match, letter) => ' ' + letter.charAt(0).toUpperCase() + letter.slice(1));
        return display;
    }

    // Fallback: return base name as-is
    return baseName;
};

// ── Budget period helper ───────────────────────────────────────────────────────
// Calculate budget period start and end dates for a team
// To match LiteLLM's display, we always show month-to-date spend
//
// @param {string} budgetDuration - Budget duration (e.g., "24h", "30d")
// @param {string} budgetResetAt - ISO timestamp when budget resets (not currently used)
// @returns {{ start: string, end: string, periodEnd: string }} Date range in YYYY-MM-DD format
//           start: first day of current month
//           end: today (for querying)
//           periodEnd: last day of current month (for display)

const getBudgetPeriod = (budgetDuration = '30d', budgetResetAt = null) => {
    const now = new Date();
    const today = now.toISOString().split('T')[0];

    // Always use calendar month to match LiteLLM's "month to date" display
    const year = now.getUTCFullYear();
    const month = now.getUTCMonth();
    const firstDayOfMonth = new Date(Date.UTC(year, month, 1));
    const lastDayOfMonth = new Date(Date.UTC(year, month + 1, 0));

    const start = firstDayOfMonth.toISOString().split('T')[0];
    const periodEnd = lastDayOfMonth.toISOString().split('T')[0];
    const end = today; // Query up to today

    return { start, end, periodEnd };
};

// ── Chart colours ─────────────────────────────────────────────────────────────

const CHART_COLORS = ['#3b82f6', '#10b981', '#8b5cf6', '#f59e0b', '#ef4444', '#06b6d4', '#ec4899', '#84cc16', '#f97316', '#6366f1'];

// ── Budget bar (inline) ───────────────────────────────────────────────────────

const BudgetBar = ({ spend, budget }) => {
    if (!budget || budget <= 0) return <span style={{ color: 'var(--muted)', fontSize: '0.8rem' }}>No budget</span>;
    const pct = Math.min((spend / budget) * 100, 999);
    const disp = Math.min(pct, 100);
    const color = pct >= 100 ? '#ef4444' : pct >= 80 ? '#f59e0b' : '#10b981';
    const remaining = Math.max(0, budget - spend);
    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                <div style={{ flex: 1, height: 6, background: 'var(--bg)', borderRadius: 3, overflow: 'hidden', minWidth: 60 }}>
                    <div style={{ width: `${disp}%`, height: '100%', background: color, borderRadius: 3, transition: 'width 0.3s' }} />
                </div>
                <span style={{ fontSize: '0.78rem', color, fontWeight: 600, minWidth: 38, textAlign: 'right' }}>{pct.toFixed(0)}%</span>
            </div>
            <div style={{ fontSize: '0.72rem', color: 'var(--fg-secondary)', whiteSpace: 'nowrap' }}>{fmt2(spend)} / {fmt2(budget)}</div>
            <div style={{ fontSize: '0.7rem', color: 'var(--muted)', whiteSpace: 'nowrap' }}>{fmt2(remaining)} remaining</div>
        </div>
    );
};

// ── Metric card ───────────────────────────────────────────────────────────────

const MetricCard = ({ label, value, sub, accent, icon }) => (
    <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 10, padding: '1.1rem 1.25rem', display: 'flex', flexDirection: 'column', gap: 4, borderTop: `3px solid ${accent || '#6366f1'}`, minHeight: '100px' }}>
        <div style={{ fontSize: '0.78rem', color: 'var(--fg-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'flex', gap: 6, alignItems: 'center', minHeight: '1rem' }}>
            {icon && <span>{icon}</span>}{label}
        </div>
        <div style={{ fontSize: '1.55rem', fontWeight: 700, color: 'var(--fg-primary)', transition: 'opacity 0.3s ease, transform 0.3s ease' }}>{value}</div>
        <div style={{ fontSize: '0.78rem', color: 'var(--muted)', minHeight: '1rem', opacity: sub ? 1 : 0 }}>{sub || '\u00A0'}</div>
    </div>
);

// ── Skeleton loader ───────────────────────────────────────────────────────────

const Skeleton = ({ height = 20, width = '100%', radius = 6, style = {} }) => (
    <div style={{ height, width, borderRadius: radius, background: 'linear-gradient(90deg,var(--border) 25%,var(--border-hover) 50%,var(--border) 75%)', backgroundSize: '200% 100%', animation: 'shimmer 1.5s infinite', ...style }} />
);

// ── Tab bar ───────────────────────────────────────────────────────────────────

const TABS = [
    { id: 'overview', label: 'Overview' },
    { id: 'analytics', label: 'Analytics' },
    { id: 'keys', label: 'API Keys' },
    { id: 'report', label: 'Reports' },
];

// ── Main component ────────────────────────────────────────────────────────────

const UsageCostDashboard = ({
    isAdmin,
    teams = [],
    groups = [],
    users = [],
    allModels = [],
    onClose,
    userEmail,
    hiddenTeamIds = [],
    onHiddenTeamIdsChange,
    initialTab = 'analytics',
    onRefresh,
    adminUsersData = null,
    analyticsSubTab: analyticsSubTabProp,
    onAnalyticsSubTabChange,
    onSwitchToReports = null,
    initialReportType = '',
    isManager = false,
    myDirectReports = [],
    onSyncDepartments = null,
}) => {
    const { instance } = useMsal();
    const [activeTab, setActiveTab] = useState(initialTab);
    const [myTeamSyncing, setMyTeamSyncing] = useState(false);
    const [myTeamSyncError, setMyTeamSyncError] = useState(null);
    const [myTeamSyncDone, setMyTeamSyncDone] = useState(false);

    // Helper function to get team name from team ID
    const getTeamName = useCallback((teamId) => {
        if (!teamId) return '—';
        const team = teams.find(t => t.team_id === teamId);
        return team?.team_alias || teamId;
    }, [teams]);

    // ── Spend data cache per team ──────────────────────────────────────────────
    const [spendCache, setSpendCache] = useState({}); // { [teamId]: { data, models, loading, error, source } }

    // ── Health check costs tracking ────────────────────────────────────────────
    const [healthCheckCosts, setHealthCheckCosts] = useState({
        spend: 0,
        requests: 0,
        loading: true,
        error: null
    });

    // ── Overview tab state ─────────────────────────────────────────────────────
    const [expandedTeamIds, setExpandedTeamIds] = useState(new Set());
    const [teamSearch, setTeamSearch] = useState('');
    const [modelFilter, setModelFilter] = useState('');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [includeHealthChecks, setIncludeHealthChecks] = useState(false); // Toggle to include health checks in totals

    // ── Analytics tab state ────────────────────────────────────────────────────
    // Controlled by parent (App.js) when analyticsSubTabProp is passed; otherwise internal
    const [analyticsSubTabInternal, setAnalyticsSubTabInternal] = useState('my-usage');
    const analyticsSubTab = analyticsSubTabProp !== undefined ? analyticsSubTabProp : analyticsSubTabInternal;
    const setAnalyticsSubTab = (tab) => {
        setAnalyticsSubTabInternal(tab);
        if (onAnalyticsSubTabChange) onAnalyticsSubTabChange(tab);
    };
    const [pieMetric, setPieMetric] = useState('spend'); // 'spend' | 'requests' | 'tokens'
    const [analyticsTimeRange, setAnalyticsTimeRange] = useState('last_7_days'); // 'current_month' | 'last_30_days' | 'last_7_days' | 'custom'
    const [analyticsDateFrom, setAnalyticsDateFrom] = useState('');
    const [analyticsDateTo, setAnalyticsDateTo] = useState('');
    const [analyticsLoaded, setAnalyticsLoaded] = useState(false);
    const [topUsersMetric, setTopUsersMetric] = useState('spend'); // 'spend' | 'requests' | 'tokens'
    const [userSpendMin, setUserSpendMin] = useState('');
    const [userSpendMax, setUserSpendMax] = useState('');
    const [apiKeyMetric, setApiKeyMetric] = useState('spend'); // 'spend' | 'requests' | 'tokens'
    const [selectedTeamForUsers, setSelectedTeamForUsers] = useState('all'); // Team ID to filter top users per team
    const [selectedTeamForKeysAnalytics, setSelectedTeamForKeysAnalytics] = useState('all'); // Team ID to filter API keys in Keys analytics
    const [teamSpendMetric, setTeamSpendMetric] = useState('spend'); // 'spend' | 'requests' | 'tokens' for Team Spend Overview chart
    const [timeSeriesView, setTimeSeriesView] = useState('total'); // 'total' | 'by_team' for time-series chart

    // ── Aggregated activity data (from /user/daily/activity/aggregated) ───────
    const [aggregatedActivity, setAggregatedActivity] = useState({
        data: [],
        loading: false,
        error: null,
        hasLoadedOnce: false // Track if data has been loaded at least once
    });
    const aggregatedActivityFetchingRef = useRef(false);
    const aggregatedActivityAbortControllerRef = useRef(null);

    // ── Recent activity tab state ──────────────────────────────────────────────
    const [activityLogs, setActivityLogs] = useState([]);
    // Per-model routing lookup for My Usage: { [resolvedModel]: requestedModel | null }
    const [myModelRouting, setMyModelRouting] = useState({});
    const [activityLoading, setActivityLoading] = useState(false);
    const [activityError, setActivityError] = useState('');
    // ── API Keys tab state ─────────────────────────────────────────────────────
    const [litellmUsers, setLitellmUsers] = useState([]); // LiteLLM internal users for key user_id resolution
    const [litellmUsersLoading, setLitellmUsersLoading] = useState(false);
    const [activityPage, setActivityPage] = useState(1);
    const [activityTotal, setActivityTotal] = useState(0);
    const [activityTotalPages, setActivityTotalPages] = useState(1);
    const [activitySummary, setActivitySummary] = useState(null);
    const [activityDateFrom, setActivityDateFrom] = useState('');
    const [activityDateTo, setActivityDateTo] = useState('');
    const [activityTeamFilter, setActivityTeamFilter] = useState('all'); // 'all' or specific team_id
    const [selectedModels, setSelectedModels] = useState([]); // Array of selected model names for multi-select filtering
    const [activityDateFilter, setActivityDateFilter] = useState('all'); // 'all' or specific date (YYYY-MM-DD)
    const [selectedApiKey, setSelectedApiKey] = useState(null); // Selected API key for filtering logs in Keys tab
    const ACTIVITY_PAGE_SIZE = 100;

    // Unique users metrics
    const [uniqueUsersTotal, setUniqueUsersTotal] = useState(0);
    const [peakDailyUsers, setPeakDailyUsers] = useState(0);

    // ── Department state ───────────────────────────────────────────────────────
    const [deptSyncLoading, setDeptSyncLoading] = useState(false);
    const [deptLastSync, setDeptLastSync] = useState(null);
    const [deptSyncError, setDeptSyncError] = useState(null);
    const [deptDrilldown, setDeptDrilldown] = useState(null); // department name to filter users by

    // Admin management state
    const [adminList, setAdminList] = useState(adminUsersData?.admins || []);
    const [adminOwner, setAdminOwner] = useState(adminUsersData?.owner || null);

    // Sync from parent when prop arrives (covers the case where fetch completes after mount)
    useEffect(() => {
        if (adminUsersData?.admins) setAdminList(adminUsersData.admins);
        if (adminUsersData?.owner)  setAdminOwner(adminUsersData.owner);
    }, [adminUsersData]);

    // Fetch admin list directly whenever the admins sub-tab is opened — authoritative, no race with parent
    useEffect(() => {
        if (analyticsSubTab !== 'admins') return;
        getAdminUsers().then(r => {
            if (r.success) {
                setAdminList(r.admins || []);
                setAdminOwner(r.owner || null);
            }
        }).catch(() => {});
    }, [analyticsSubTab]);
    const [adminAddEmail, setAdminAddEmail] = useState('');
    const [adminAddLoading, setAdminAddLoading] = useState(false);
    const [adminAddError, setAdminAddError] = useState('');
    const [adminRemovingEmail, setAdminRemovingEmail] = useState(null);
    // All-users list for searchable admin dropdown
    const [allUsersList, setAllUsersList] = useState([]);
    const [allUsersLoading, setAllUsersLoading] = useState(false);
    const [adminSearchQuery, setAdminSearchQuery] = useState('');
    const [adminDropdownOpen, setAdminDropdownOpen] = useState(false);

    // Header controls state
    const [headerSearch, setHeaderSearch] = useState('');
    const [notificationsOpen, setNotificationsOpen] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [notificationsLoaded, setNotificationsLoaded] = useState(false);
    const [hasUnread, setHasUnread] = useState(false);
    const [actionsOpen, setActionsOpen] = useState(false);

    // Helper functions for multi-model selection
    const toggleModelSelection = useCallback((model) => {
        setSelectedModels(prev => {
            if (prev.includes(model)) {
                // Remove model if already selected
                return prev.filter(m => m !== model);
            } else {
                // Add model if not selected
                return [...prev, model];
            }
        });
        setActivityPage(1); // Reset to page 1 when filter changes
    }, []);

    const clearModelSelection = useCallback(() => {
        setSelectedModels([]);
        setActivityPage(1);
    }, []);

    const isModelSelected = useCallback((model) => {
        return selectedModels.includes(model);
    }, [selectedModels]);

    // ── Report tab state ───────────────────────────────────────────────────────
    const [reportDateFrom, setReportDateFrom] = useState('');
    const [reportDateTo, setReportDateTo] = useState('');
    const [reportTeams, setReportTeams] = useState(new Set());
    const [generatingPdf, setGeneratingPdf] = useState(false);
    const [reportType, setReportType] = useState(initialReportType);
    React.useEffect(() => {
        if (initialReportType) setReportType(initialReportType);
    }, [initialReportType]);
    // When report type changes, default all valid teams to checked
    React.useEffect(() => {
        if (!reportType) return;
        const validIds = visibleTeams.filter(t => t.groupId !== null).map(t => t.team_id);
        setReportTeams(new Set(validIds));
    }, [reportType]); // eslint-disable-line react-hooks/exhaustive-deps
    const [reportPreviewData, setReportPreviewData] = useState(null);
    const [mgmtSections, setMgmtSections] = useState({
        execSummary: true,
        byOrgUnit: true,
        byDepartment: true,
        teamBudgets: true,
        topUsers: true,
        modelDistribution: true,
    });
    const [mgmtUserCap, setMgmtUserCap] = useState('50');
    const [mgmtModelCap, setMgmtModelCap] = useState('20');
    const [reportDepts, setReportDepts] = useState(new Set());
    const [reportOrgUnits, setReportOrgUnits] = useState(new Set());
    // Billing report state
    const [billingBillToDept, setBillingBillToDept] = useState('');
    const [billingReportDepts, setBillingReportDepts] = useState(new Set());
    const [billingReportOrgUnits, setBillingReportOrgUnits] = useState(new Set());
    const [billingChargeMethod, setBillingChargeMethod] = useState('none'); // 'none' | 'markup' | 'margin' | 'flat'
    const [billingChargeAmount, setBillingChargeAmount] = useState('');     // % for markup/margin, $ for flat

    // ── API Keys tab state ─────────────────────────────────────────────────────
    const [selectedTeamForKeys, setSelectedTeamForKeys] = useState(null);

    // Ref to prevent the Recent Activity auto-load from firing repeatedly
    const activityInitialized = useRef(false);

    // Ref to track which teams have been requested for fetching (prevents duplicate requests)
    const pendingFetchesRef = useRef(new Set());

    // Ref to track the last fetch parameters for Analytics tab to prevent unnecessary refetches
    const lastAnalyticsFetchRef = useRef({ start: null, end: null, teams: [] });

    // Lock body scroll when modal opens
    useEffect(() => {
        document.body.style.overflow = 'hidden';
        document.body.style.paddingRight = 'var(--scrollbar-width, 0px)';

        return () => {
            document.body.style.overflow = '';
            document.body.style.paddingRight = '';
        };
    }, []);

    // ── Fetch LiteLLM users when API Keys tab is opened ───────────────────────
    useEffect(() => {
        if (activeTab === 'keys' && litellmUsers.length === 0 && !litellmUsersLoading) {
            setLitellmUsersLoading(true);
            getLiteLLMUsers().then(result => {
                if (result.success) {
                    setLitellmUsers(result.data || []);
                }
                setLitellmUsersLoading(false);
            }).catch(() => {
                setLitellmUsersLoading(false);
            });
        }
    }, [activeTab, litellmUsers.length, litellmUsersLoading]);

    // ── Helper: Get team spend from cache ─────────────────────────────────────
    // Calculates team spend by summing user spends from cached data.
    // This ensures consistency with the per-user breakdown.

    const getTeamSpendFromCache = useCallback((teamId) => {
        const cache = spendCache[teamId];
        if (!cache || cache.loading || !cache.data) return null;
        return cache.data
            .reduce((sum, user) => sum + (user.spend || 0), 0);
    }, [spendCache]);

    // ── Build enriched team list ───────────────────────────────────────────────
    // Enriches teams with user counts, group IDs, and filtered spend data

    const enrichedTeams = useMemo(() => {
        return teams.map(team => {
            const matchingGroup = groups.find(
                g => g.name && g.name.trim().toLowerCase() === (team.team_alias || '').trim().toLowerCase()
            );
            const userCount = matchingGroup?.user_ids?.length || 0;
            const isHidden = hiddenTeamIds.includes(team.team_id);
            // Get filtered spend from cache (respects health check filter)
            // Only use cached spend if data is loaded, otherwise use 0 to avoid showing unfiltered data
            const cache = spendCache[team.team_id];
            const filteredSpend = (cache && !cache.loading && cache.data)
                ? getTeamSpendFromCache(team.team_id) ?? 0
                : 0; // Show 0 until cache loads to avoid showing unfiltered spend
            return {
                ...team,
                userCount,
                groupId: matchingGroup?.id || null,
                isHidden,
                spend: filteredSpend // Override with filtered spend
            };
        });
    }, [teams, groups, hiddenTeamIds, getTeamSpendFromCache, spendCache]);

    const visibleTeams = useMemo(() => {
        return enrichedTeams.filter(t => !t.isHidden);
    }, [enrichedTeams]);

    const filteredTeams = useMemo(() => {
        let list = visibleTeams;
        if (teamSearch) {
            list = list.filter(t => t.team_id === teamSearch);
        }
        return list;
    }, [visibleTeams, teamSearch]);

    // ── Totals ────────────────────────────────────────────────────────────────
    // Team spend is calculated from user-level spend logs for the selected date range.
    // This ensures consistency with the per-user breakdown shown when teams are expanded.

    const totals = useMemo(() => {
        return enrichedTeams.reduce((acc, t) => {
            const cachedSpend = getTeamSpendFromCache(t.team_id);
            return {
                spend: acc.spend + (cachedSpend ?? 0),
                max_budget: acc.max_budget + (t.max_budget || 0),
                userCount: acc.userCount + t.userCount,
                teams: acc.teams + 1,
            };
        }, { spend: 0, max_budget: 0, userCount: 0, teams: 0 });
    }, [enrichedTeams, getTeamSpendFromCache]);

    // ── Loading state for team spend data ─────────────────────────────────────

    const isLoadingAnyTeamSpend = useMemo(() => {
        return visibleTeams.some(t => {
            const cache = spendCache[t.team_id];
            return !cache || cache.loading;
        });
    }, [visibleTeams, spendCache]);

    // ── Helper: Calculate analytics date range ────────────────────────────────
    // Calculates start and end dates based on selected time range option
    // Changed to useMemo to prevent reference changes on every render

    const analyticsDateRange = useMemo(() => {
        const now = new Date();
        let startDate, endDate;

        switch (analyticsTimeRange) {
            case 'all_time':
                startDate = new Date(Date.UTC(2020, 0, 1));
                endDate = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
                break;
            case 'current_month':
                // First day of current month to today (using UTC to avoid timezone issues)
                const year = now.getUTCFullYear();
                const month = now.getUTCMonth();
                startDate = new Date(Date.UTC(year, month, 1));
                endDate = new Date(Date.UTC(year, month, now.getUTCDate()));
                break;
            case 'last_30_days':
                endDate = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
                startDate = new Date(endDate);
                startDate.setUTCDate(startDate.getUTCDate() - 30);
                break;
            case 'last_7_days':
                endDate = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
                startDate = new Date(endDate);
                startDate.setUTCDate(startDate.getUTCDate() - 7);
                break;
            case 'custom':
                if (analyticsDateFrom && analyticsDateTo) {
                    startDate = new Date(analyticsDateFrom + 'T00:00:00Z');
                    endDate = new Date(analyticsDateTo + 'T23:59:59Z');

                    // Validate dates - if invalid, fallback to current month
                    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
                        console.warn('[UsageCost] Invalid custom date range, using current month');
                        const year = now.getUTCFullYear();
                        const month = now.getUTCMonth();
                        startDate = new Date(Date.UTC(year, month, 1));
                        endDate = new Date(Date.UTC(year, month, now.getUTCDate()));
                    }
                } else {
                    // Fallback to current month if custom dates not set
                    const year = now.getUTCFullYear();
                    const month = now.getUTCMonth();
                    startDate = new Date(Date.UTC(year, month, 1));
                    endDate = new Date(Date.UTC(year, month, now.getUTCDate()));
                }
                break;
            default:
                const yr = now.getUTCFullYear();
                const mn = now.getUTCMonth();
                startDate = new Date(Date.UTC(yr, mn, 1));
                endDate = new Date(Date.UTC(yr, mn, now.getUTCDate()));
        }

        // Final validation before returning
        if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
            console.error('[UsageCost] Invalid dates after processing, using safe defaults');
            const year = now.getUTCFullYear();
            const month = now.getUTCMonth();
            startDate = new Date(Date.UTC(year, month, 1));
            endDate = new Date(Date.UTC(year, month, now.getUTCDate()));
        }

        return {
            start: startDate.toISOString().split('T')[0],
            end: endDate.toISOString().split('T')[0]
        };
    }, [analyticsTimeRange, analyticsDateFrom, analyticsDateTo]);

    // ── Cost prediction ───────────────────────────────────────────────────────

    const prediction = useMemo(() => {
        const now = new Date();
        const dayOfMonth = now.getDate();
        const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
        const daysRemaining = daysInMonth - dayOfMonth;

        const rangeStart = analyticsDateRange?.start ? new Date(analyticsDateRange.start + 'T00:00:00Z') : null;
        const rangeEnd   = analyticsDateRange?.end   ? new Date(analyticsDateRange.end   + 'T00:00:00Z') : null;
        const elapsedDays = (rangeStart && rangeEnd)
            ? Math.max(1, Math.round((rangeEnd - rangeStart) / 86400000) + 1)
            : dayOfMonth;
        const dailyAvg = elapsedDays > 0 ? totals.spend / elapsedDays : 0;
        const predictedEOM = dailyAvg * daysInMonth;
        return { dayOfMonth, daysInMonth, daysRemaining, dailyAvg, predictedEOM, currentSpend: totals.spend, elapsedDays };
    }, [totals.spend, analyticsDateRange]);

    // ── Fetch spend for a team ─────────────────────────────────────────────────

    const fetchTeamSpend = useCallback(async (team, customStartDate = null, customEndDate = null) => {
        const teamId = team.team_id;

        // Create a unique key for this fetch request
        const fetchKey = `${teamId}_${customStartDate || 'default'}_${customEndDate || 'default'}`;

        // Skip if already loading or already in pending queue
        if (spendCache[teamId]?.loading || pendingFetchesRef.current.has(fetchKey)) {
            return;
        }

        // Determine date range based on context
        let startDate, endDate;
        if (customStartDate && customEndDate) {
            // Use custom dates (for Analytics tab)
            startDate = customStartDate;
            endDate = customEndDate;
        } else {
            // Use team's actual budget period (based on reset timestamp) unless user has set custom date range (for Overview tab)
            const { start, end } = getBudgetPeriod(team.budget_duration, team.budget_reset_at);
            startDate = dateFrom || start;
            endDate = dateTo || end;
        }

        console.log(`[UsageCost] Fetching spend for ${team.team_alias || teamId}: ${startDate} to ${endDate} (budget: ${team.budget_duration}, reset: ${team.budget_reset_at})`);

        // Mark this fetch as pending
        pendingFetchesRef.current.add(fetchKey);

        setSpendCache(prev => ({ ...prev, [teamId]: { ...prev[teamId], loading: true, error: null } }));

        try {
            // Pass list of all known team IDs to backend for health check detection
            const knownTeamIds = teams.map(t => t.team_id).filter(Boolean);
            const result = await getTeamSpend(teamId, startDate, endDate, knownTeamIds);

            if (result.success) {
                setSpendCache(prev => ({
                    ...prev,
                    [teamId]: { data: result.data || [], models: [], departments: result.departments || [], orgUnits: result.orgUnits || [], loading: false, error: null, source: 'logs' }
                }));
            } else {
                // Fallback to keys spend
                const keysData = (team.keys || []).map(key => ({
                    email: (key.user_id || key.user || '').toLowerCase(),
                    spend: key.spend || 0,
                    tokens: 0,
                    requests: 0,
                    models: {}
                })).filter(u => u.email);
                setSpendCache(prev => ({
                    ...prev,
                    [teamId]: { data: keysData, models: [], loading: false, error: result.error, source: 'keys' }
                }));
            }
        } finally {
            // Remove from pending queue after a short delay to prevent immediate re-fetching
            setTimeout(() => {
                pendingFetchesRef.current.delete(fetchKey);
            }, 1000);
        }
    }, [spendCache, dateFrom, dateTo, teams]);

    // ── Aggregate department data across all teams (for Analytics) ───────────

    const aggregatedDepartments = useMemo(() => {
        const deptMap = {};
        // Use Sets to deduplicate users who appear in multiple teams
        const deptUsers = {};
        Object.values(spendCache).forEach(cache => {
            (cache.departments || []).forEach(d => {
                if (!deptMap[d.department]) {
                    deptMap[d.department] = { department: d.department, spend: 0, tokens: 0, requests: 0, users: 0 };
                    deptUsers[d.department] = new Set();
                }
                deptMap[d.department].spend += d.spend || 0;
                deptMap[d.department].tokens += d.tokens || 0;
                deptMap[d.department].requests += d.requests || 0;
            });
            // Count unique users per dept from the raw user data
            (cache.data || []).forEach(u => {
                const dept = u.department || 'Unknown';
                if (!deptUsers[dept]) {
                    deptUsers[dept] = new Set();
                    if (!deptMap[dept]) deptMap[dept] = { department: dept, spend: 0, tokens: 0, requests: 0, users: 0 };
                }
                if (u.email && u.email !== '__unknown__') deptUsers[dept].add(u.email);
            });
        });
        Object.keys(deptUsers).forEach(dept => {
            if (deptMap[dept]) deptMap[dept].users = deptUsers[dept].size;
        });
        return Object.values(deptMap).sort((a, b) => b.spend - a.spend);
    }, [spendCache]);

    // ── Aggregate org unit data across all teams (for Analytics) ────────────────

    const aggregatedOrgUnits = useMemo(() => {
        const ouMap = {};
        const ouUsers = {};
        Object.values(spendCache).forEach(cache => {
            (cache.orgUnits || []).forEach(o => {
                if (!ouMap[o.orgUnit]) {
                    ouMap[o.orgUnit] = { orgUnit: o.orgUnit, spend: 0, tokens: 0, requests: 0, users: 0 };
                    ouUsers[o.orgUnit] = new Set();
                }
                ouMap[o.orgUnit].spend += o.spend || 0;
                ouMap[o.orgUnit].tokens += o.tokens || 0;
                ouMap[o.orgUnit].requests += o.requests || 0;
            });
            // Count unique users per org unit from the raw user data
            (cache.data || []).forEach(u => {
                const ou = u.orgUnit;
                if (!ou) return;
                if (!ouUsers[ou]) {
                    ouUsers[ou] = new Set();
                    if (!ouMap[ou]) ouMap[ou] = { orgUnit: ou, spend: 0, tokens: 0, requests: 0, users: 0 };
                }
                if (u.email && u.email !== '__unknown__') ouUsers[ou].add(u.email);
            });
        });
        Object.keys(ouUsers).forEach(ou => {
            if (ouMap[ou]) ouMap[ou].users = ouUsers[ou].size;
        });
        return Object.values(ouMap).sort((a, b) => b.spend - a.spend);
    }, [spendCache]);

    // ── OpenWebUI model name lookup ───────────────────────────────────────────
    // Maps LiteLLM base model name (e.g. "gpt-5-mini") to the best-matching
    // OpenWebUI model ID (e.g. "PLUS.gpt-5-mini-sea") so spend data shows the
    // same model names users see in the chat interface.

    const owModelNameMap = useMemo(() => {
        const map = {};
        (allModels || []).forEach(m => {
            const id = m.id || '';
            // Strip team prefix (PLUS. / CNX. / BKK.)
            const dotIdx = id.indexOf('.');
            const base = dotIdx !== -1 ? id.slice(dotIdx + 1) : id;
            if (!base) return;
            // Index by every suffix of the base name so "gpt-5-mini" matches "gpt-5-mini-sea"
            // Store the shortest matching OW id to prefer exact matches over longer ones
            if (!map[base] || id.length < map[base].length) map[base] = id;
            // Also index by progressively shorter prefixes so partial matches work
            const parts = base.split('-');
            for (let i = parts.length - 1; i >= 2; i--) {
                const prefix = parts.slice(0, i).join('-');
                if (!map[prefix] || id.length < map[prefix].length) map[prefix] = id;
            }
        });
        return map;
    }, [allModels]);

    // Resolves a LiteLLM model name to its OpenWebUI display ID when possible,
    // otherwise falls back to the existing fmtModelName formatting.
    const resolveModelName = useCallback((m) => {
        if (!m) return m;
        const slash = m.lastIndexOf('/');
        const base = slash !== -1 ? m.slice(slash + 1) : m;
        if (owModelNameMap[base]) return owModelNameMap[base];
        // Try progressively shorter prefixes of the base name
        const parts = base.split('-');
        for (let i = parts.length - 1; i >= 2; i--) {
            const prefix = parts.slice(0, i).join('-');
            if (owModelNameMap[prefix]) return owModelNameMap[prefix];
        }
        return fmtModelName(m);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [owModelNameMap]);

    // ── Aggregate model data across all teams (for Analytics) ─────────────────

    const aggregatedModels = useMemo(() => {
        const modelMap = {};
        Object.values(spendCache).forEach(cache => {
            (cache.data || [])
                .forEach(user => {
                    Object.entries(user.models || {}).forEach(([model, stats]) => {
                        if (!modelMap[model]) modelMap[model] = { model, spend: 0, requests: 0, tokens: 0 };
                        modelMap[model].spend += stats.spend || 0;
                        modelMap[model].requests += stats.requests || 0;
                        modelMap[model].tokens += stats.tokens || 0;
                    });
                });
        });
        return Object.values(modelMap).sort((a, b) => b.spend - a.spend);
    }, [spendCache]);

    // ── Model-filtered spend per team (for model dropdown) ────────────────────
    // When a model is selected, compute each team's spend for just that model
    // from the per-user cache data (user.models[modelFilter].spend).

    const modelFilteredTeamSpend = useMemo(() => {
        if (!modelFilter) return null;
        const result = {};
        visibleTeams.forEach(team => {
            const cache = spendCache[team.team_id];
            if (!cache || cache.loading || !cache.data) {
                result[team.team_id] = null; // still loading
                return;
            }
            const spend = cache.data
                .reduce((sum, u) => sum + ((u.models?.[modelFilter]?.spend) || 0), 0);
            result[team.team_id] = spend;
        });
        return result;
    }, [modelFilter, spendCache, visibleTeams]);

    // ── Team-scoped daily aggregates ──────────────────────────────────────────
    // Re-aggregates per-day metrics from the key-level breakdown, filtering to
    // only the visible teams. day.metrics contains global totals (all LiteLLM
    // keys) — using that directly would include spend from keys like john-claude
    // that are not in any watched team.

    const filteredDailyData = useMemo(() => {
        if (!aggregatedActivity.data || aggregatedActivity.data.length === 0) return [];
        const visibleTeamIds = visibleTeams.map(t => t.team_id);
        return aggregatedActivity.data
            .slice()
            .sort((a, b) => new Date(a.date) - new Date(b.date))
            .map(day => {
                let spend = 0, requests = 0, successfulRequests = 0, failedRequests = 0, tokens = 0, promptTokens = 0, completionTokens = 0;
                const models = day.breakdown?.models || {};
                Object.values(models).forEach(modelData => {
                    Object.entries(modelData.api_key_breakdown || {}).forEach(([keyHash, keyData]) => {
                        if (keyHash === 'litellm-internal-health-check') return;
                        const teamId = keyData.metadata?.team_id;
                        if (!teamId || !visibleTeamIds.includes(teamId)) return;
                        spend              += keyData.metrics?.spend                 || 0;
                        requests           += keyData.metrics?.api_requests          || 0;
                        successfulRequests += keyData.metrics?.successful_requests   || 0;
                        failedRequests     += keyData.metrics?.failed_requests       || 0;
                        tokens             += keyData.metrics?.total_tokens          || 0;
                        promptTokens       += keyData.metrics?.prompt_tokens         || 0;
                        completionTokens   += keyData.metrics?.completion_tokens     || 0;
                    });
                });
                return { date: day.date, spend, requests, successfulRequests, failedRequests, tokens, promptTokens, completionTokens };
            });
    }, [aggregatedActivity.data, visibleTeams]);

    // ── Daily spend for line chart ─────────────────────────────────────────────
    // Shows actual daily data from /user/daily/activity/aggregated endpoint
    // This provides real daily breakdowns with accurate spend/request metrics

    const lineChartData = useMemo(() => {
        if (activeTab !== 'analytics') {
            // Return empty if not on analytics tab
            return [];
        }

        // Use team-scoped daily data if available
        if (filteredDailyData.length > 0) {
            return filteredDailyData.map(day => ({
                    date: fmtDateShort(day.date),
                    fullDate: day.date,
                    cost: day.spend,
                    requests: day.successfulRequests,
                    failedRequests: day.failedRequests,
                    totalRequests: day.requests,
                    tokens: day.tokens,
                    promptTokens: day.promptTokens,
                    completionTokens: day.completionTokens
                }));
        }

        // Fallback: Create empty buckets if aggregated data not loaded yet
        const days = {};
        const { start, end } = analyticsDateRange;
        const startDate = new Date(start);
        const endDate = new Date(end);

        const currentDate = new Date(startDate);
        while (currentDate <= endDate) {
            const key = currentDate.toISOString().split('T')[0];
            days[key] = { date: fmtDateShort(key + 'T00:00:00'), fullDate: key, cost: 0, requests: 0 };
            currentDate.setDate(currentDate.getDate() + 1);
        }

        return Object.values(days);
    }, [activeTab, filteredDailyData, analyticsDateRange]);

    // ── API Key Performance ────────────────────────────────────────────────────
    // Aggregates spend/requests/tokens by API key from the daily activity data
    // Shows top performing API keys with their team associations

    const apiKeyPerformance = useMemo(() => {
        if (!aggregatedActivity.data || aggregatedActivity.data.length === 0) {
            return [];
        }

        const keyMap = {};

        // Get visible team IDs for filtering
        const visibleTeamIds = visibleTeams.map(t => t.team_id);

        // Aggregate across all days and models
        aggregatedActivity.data.forEach(day => {
            const models = day.breakdown?.models || {};

            Object.values(models).forEach(modelData => {
                const apiKeyBreakdown = modelData.api_key_breakdown || {};

                Object.entries(apiKeyBreakdown).forEach(([keyHash, keyData]) => {
                    const alias = keyData.metadata?.key_alias || keyHash.substring(0, 12) + '...';
                    const teamId = keyData.metadata?.team_id;

                    // Skip health check keys
                    if (keyHash === 'litellm-internal-health-check') {
                        return;
                    }

                    // Filter by visible teams - only show keys from teams user has selected
                    // If key has a team_id, it must be in the visible teams list
                    // If key has no team_id, skip it (unassigned keys)
                    if (teamId) {
                        if (!visibleTeamIds.includes(teamId)) {
                            return; // Skip keys from hidden teams
                        }
                    } else {
                        return; // Skip keys with no team assignment
                    }

                    // Filter by selected team in Keys analytics dropdown (if not 'all')
                    if (selectedTeamForKeysAnalytics !== 'all' && teamId !== selectedTeamForKeysAnalytics) {
                        return; // Skip keys from other teams when specific team is selected
                    }

                    if (!keyMap[keyHash]) {
                        keyMap[keyHash] = {
                            keyHash,
                            alias,
                            teamId,
                            spend: 0,
                            requests: 0,
                            successfulRequests: 0,
                            failedRequests: 0,
                            tokens: 0,
                            promptTokens: 0,
                            completionTokens: 0
                        };
                    }

                    keyMap[keyHash].spend += keyData.metrics?.spend || 0;
                    keyMap[keyHash].requests += keyData.metrics?.api_requests || 0;
                    keyMap[keyHash].successfulRequests += keyData.metrics?.successful_requests || 0;
                    keyMap[keyHash].failedRequests += keyData.metrics?.failed_requests || 0;
                    keyMap[keyHash].tokens += keyData.metrics?.total_tokens || 0;
                    keyMap[keyHash].promptTokens += keyData.metrics?.prompt_tokens || 0;
                    keyMap[keyHash].completionTokens += keyData.metrics?.completion_tokens || 0;
                });
            });
        });

        return Object.values(keyMap)
            .sort((a, b) => b.spend - a.spend)
            .slice(0, 10) // Top 10 API keys
            .map((k, i) => ({
                ...k,
                color: CHART_COLORS[i % CHART_COLORS.length]
            }));
    }, [aggregatedActivity.data, visibleTeams, selectedTeamForKeysAnalytics]);

    // ── Team API Key Spend - Aggregates API key spend by team ────────────────

    const teamApiKeySpend = useMemo(() => {
        if (!aggregatedActivity.data || aggregatedActivity.data.length === 0) {
            return [];
        }

        const teamMap = {};
        const visibleTeamIds = visibleTeams.map(t => t.team_id);

        // Aggregate across all days and models
        aggregatedActivity.data.forEach(day => {
            const models = day.breakdown?.models || {};

            Object.values(models).forEach(modelData => {
                const apiKeyBreakdown = modelData.api_key_breakdown || {};

                Object.entries(apiKeyBreakdown).forEach(([keyHash, keyData]) => {
                    const teamId = keyData.metadata?.team_id;

                    // Skip health check keys
                    if (keyHash === 'litellm-internal-health-check') {
                        return;
                    }

                    // Filter by visible teams
                    if (teamId) {
                        if (!visibleTeamIds.includes(teamId)) {
                            return;
                        }
                    } else {
                        return; // Skip keys with no team assignment
                    }

                    if (!teamMap[teamId]) {
                        const team = visibleTeams.find(t => t.team_id === teamId);
                        teamMap[teamId] = {
                            teamId,
                            teamName: team?.team_alias || teamId,
                            maxBudget: team?.max_budget || 0,
                            budgetDuration: team?.budget_duration || '',
                            spend: 0,
                            requests: 0,
                            tokens: 0
                        };
                    }

                    teamMap[teamId].spend += keyData.metrics?.spend || 0;
                    teamMap[teamId].requests += keyData.metrics?.api_requests || 0;
                    teamMap[teamId].tokens += keyData.metrics?.total_tokens || 0;
                });
            });
        });

        return Object.values(teamMap)
            .sort((a, b) => b.spend - a.spend)
            .map((t, i) => ({
                ...t,
                color: CHART_COLORS[i % CHART_COLORS.length]
            }));
    }, [aggregatedActivity.data, visibleTeams]);

    // ── API Key Spend Time Series - Daily spend breakdown by team ─────────────

    const apiKeySpendTimeSeries = useMemo(() => {
        if (!aggregatedActivity.data || aggregatedActivity.data.length === 0) {
            return [];
        }

        const visibleTeamIds = visibleTeams.map(t => t.team_id);

        return aggregatedActivity.data.map(day => {
            const result = { date: day.date, total: 0 };

            const models = day.breakdown?.models || {};
            Object.values(models).forEach(modelData => {
                const apiKeyBreakdown = modelData.api_key_breakdown || {};
                Object.entries(apiKeyBreakdown).forEach(([keyHash, keyData]) => {
                    const teamId = keyData.metadata?.team_id;

                    // Skip health check keys
                    if (keyHash === 'litellm-internal-health-check') {
                        return;
                    }

                    // Filter by visible teams
                    if (teamId && visibleTeamIds.includes(teamId)) {
                        const spend = keyData.metrics?.spend || 0;
                        result.total += spend;

                        // Add team-specific spend for "by team" view
                        if (!result[teamId]) {
                            result[teamId] = 0;
                        }
                        result[teamId] += spend;
                    }
                });
            });

            return result;
        });
    }, [aggregatedActivity.data, visibleTeams]);

    // ── Selected API Key Model Breakdown - Shows model breakdown for selected key ──

    const selectedKeyModelBreakdown = useMemo(() => {
        if (!selectedApiKey || !aggregatedActivity.data || aggregatedActivity.data.length === 0) {
            return [];
        }

        const modelMap = {};

        aggregatedActivity.data.forEach(day => {
            const models = day.breakdown?.models || {};
            Object.entries(models).forEach(([modelName, modelData]) => {
                const apiKeyBreakdown = modelData.api_key_breakdown || {};
                if (!apiKeyBreakdown[selectedApiKey]) return;

                const keyData = apiKeyBreakdown[selectedApiKey];
                if (!modelMap[modelName]) {
                    modelMap[modelName] = {
                        model: modelName,
                        spend: 0,
                        requests: 0,
                        tokens: 0
                    };
                }

                modelMap[modelName].spend += keyData.metrics?.spend || 0;
                modelMap[modelName].requests += keyData.metrics?.api_requests || 0;
                modelMap[modelName].tokens += keyData.metrics?.total_tokens || 0;
            });
        });

        const breakdown = Object.values(modelMap).sort((a, b) => b.spend - a.spend);
        const totalSpend = breakdown.reduce((sum, m) => sum + m.spend, 0);

        // Add percentage of total spend
        return breakdown.map(m => ({
            ...m,
            percentage: totalSpend > 0 ? (m.spend / totalSpend) * 100 : 0
        }));
    }, [selectedApiKey, aggregatedActivity.data]);

    // ── Team comparison bar data ───────────────────────────────────────────────

    const barChartData = useMemo(() => {
        return visibleTeams
            .filter(t => t.max_budget > 0 || t.spend > 0)
            .map((t, index) => ({
                name: (t.team_alias || t.team_id || '').length > 12
                    ? (t.team_alias || t.team_id).slice(0, 12) + '…'
                    : (t.team_alias || t.team_id),
                fullName: t.team_alias || t.team_id,
                spend: parseFloat((t.spend || 0).toFixed(4)),
                budget: parseFloat((t.max_budget || 0).toFixed(2)),
                pct: t.max_budget > 0 ? Math.min((t.spend / t.max_budget) * 100, 100) : 0,
                color: CHART_COLORS[index % CHART_COLORS.length], // Assign color from palette
            }))
            .sort((a, b) => b.pct - a.pct);
    }, [visibleTeams]);

    // ── Pie chart data ────────────────────────────────────────────────────────

    const pieData = useMemo(() => {
        return aggregatedModels.slice(0, 10).map(m => ({
            name: m.model,
            value: parseFloat((m[pieMetric] || 0).toFixed(4)),
        }));
    }, [aggregatedModels, pieMetric]);

    // ── All models list for filter ─────────────────────────────────────────────
    // Use only model names seen in actual spend data (from LiteLLM via teamSpend cache).
    // OpenWebUI model names have team prefixes (e.g. "PLUS.gpt-5") that don't match
    // the raw LiteLLM model names (e.g. "gpt-5") stored in the spend cache, so we
    // do NOT include OpenWebUI model names in this list.

    const allModelNames = useMemo(() => {
        return aggregatedModels.map(m => m.model).filter(Boolean).sort();
    }, [aggregatedModels]);

    // ── Top users by spend (for Top Users chart) ──────────────────────────────────
    const topUsersBySpend = useMemo(() => {
        const userMap = {};

        // Build a map of user email -> their actual team memberships from OpenWebUI groups
        const userToTeamsMap = {};
        groups.forEach(group => {
            (group.user_ids || []).forEach(userId => {
                const user = users.find(u => u.id === userId || u.user_id === userId);
                const userEmail = (user?.email || user?.user_email || '').toLowerCase();
                if (userEmail) {
                    if (!userToTeamsMap[userEmail]) {
                        userToTeamsMap[userEmail] = new Set();
                    }
                    // Map group name to team alias - use ALL teams not just visible ones
                    const matchingTeam = teams.find(t =>
                        (t.team_alias && t.team_alias.toLowerCase() === group.name.toLowerCase()) ||
                        t.team_id === group.name
                    );
                    if (matchingTeam) {
                        userToTeamsMap[userEmail].add(matchingTeam.team_alias || matchingTeam.team_id);
                    }
                }
            });
        });

        // Aggregate spend data per user
        visibleTeams.forEach(team => {
            const cache = spendCache[team.team_id];
            if (!cache || !cache.data) return;

            cache.data.forEach(user => {
                if (!userMap[user.email]) {
                    userMap[user.email] = { email: user.email, spend: 0, requests: 0, tokens: 0, department: user.department || null, orgUnit: user.orgUnit || null, models: {} };
                }
                userMap[user.email].spend += user.spend || 0;
                userMap[user.email].requests += user.requests || 0;
                userMap[user.email].tokens += user.tokens || 0;
                if (!userMap[user.email].department && user.department) {
                    userMap[user.email].department = user.department;
                }
                if (!userMap[user.email].orgUnit && user.orgUnit) {
                    userMap[user.email].orgUnit = user.orgUnit;
                }
                // Aggregate per-model spend
                if (user.models && typeof user.models === 'object') {
                    Object.entries(user.models).forEach(([modelName, m]) => {
                        if (!userMap[user.email].models[modelName]) {
                            userMap[user.email].models[modelName] = { spend: 0, tokens: 0, requests: 0 };
                        }
                        userMap[user.email].models[modelName].spend += m.spend || 0;
                        userMap[user.email].models[modelName].tokens += m.tokens || 0;
                        userMap[user.email].models[modelName].requests += m.requests || 0;
                    });
                }
            });
        });

        return Object.values(userMap)
            .sort((a, b) => b.spend - a.spend)
            .slice(0, 10)
            .map((u, i) => {
                // Get the user's actual team memberships from OpenWebUI groups
                const userActualTeams = userToTeamsMap[(u.email || '').toLowerCase()] || new Set();
                return {
                    ...u,
                    teamCount: userActualTeams.size,
                    teams: Array.from(userActualTeams).join(', ') || 'No team',
                    color: CHART_COLORS[i % CHART_COLORS.length]
                };
            });
    }, [visibleTeams, spendCache, groups, users, teams]);

    // ── Header quick actions per sub-tab ──────────────────────────────────────
    const headerActions = useMemo(() => {
        const tab = analyticsSubTab;
        const switchReports = (type) => { if (onSwitchToReports) onSwitchToReports(type); else setAnalyticsSubTab('reports'); };
        if (tab === 'my-usage') {
            const acts = [{ label: 'Go to My Usage Report', onClick: () => switchReports('my-usage') }];
            const myEmail = (userEmail || '').toLowerCase();
            const meFromTop = topUsersBySpend.find(u => (u.email || '').toLowerCase() === myEmail);
            if (meFromTop?.teams) acts.push({ label: 'View My Teams', onClick: () => setAnalyticsSubTab('teams') });
            acts.push({
                label: 'Download My Usage CSV',
                onClick: () => {
                    const myEmail2 = (userEmail || '').toLowerCase();
                    const myModelsMap = {};
                    Object.values(spendCache).forEach(cache => {
                        (cache.data || []).forEach(u => {
                            if ((u.email || '').toLowerCase() !== myEmail2) return;
                            if (u.models) Object.entries(u.models).forEach(([name, m]) => {
                                if (!myModelsMap[name]) myModelsMap[name] = { spend: 0, tokens: 0, requests: 0 };
                                myModelsMap[name].spend += m.spend || 0;
                                myModelsMap[name].tokens += m.tokens || 0;
                                myModelsMap[name].requests += m.requests || 0;
                            });
                        });
                    });
                    const rows = [['Model', 'Requests', 'Tokens', 'Spend']];
                    Object.entries(myModelsMap).sort((a, b) => b[1].spend - a[1].spend).forEach(([name, m]) => rows.push([name, m.requests, m.tokens, m.spend.toFixed(4)]));
                    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
                    const blob = new Blob([csv], { type: 'text/csv' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a'); a.href = url;
                    const now = new Date(); a.download = `my-usage-${String(now.getDate()).padStart(2,'0')}-${String(now.getMonth()+1).padStart(2,'0')}-${now.getFullYear()}.csv`;
                    a.click(); URL.revokeObjectURL(url);
                }
            });
            return acts;
        }
        if (tab === 'activity') return [
            { label: 'View Models Breakdown', onClick: () => setAnalyticsSubTab('models') },
            { label: 'View Users', onClick: () => setAnalyticsSubTab('users') },
            { label: 'Go to Reports', onClick: () => switchReports('management') },
        ];
        if (tab === 'models') return [
            { label: 'View Users Breakdown', onClick: () => setAnalyticsSubTab('users') },
            { label: 'View Recent Activity', onClick: () => setAnalyticsSubTab('activity') },
            { label: 'Go to Reports', onClick: () => switchReports('management') },
        ];
        if (tab === 'users' && isAdmin) {
            const acts = [{ label: 'View Teams', onClick: () => setAnalyticsSubTab('teams') }];
            const topSpender = topUsersBySpend[0];
            if (topSpender) acts.push({ label: 'View Top Department', onClick: () => { setDeptDrilldown(topSpender.department); setAnalyticsSubTab('departments'); } });
            acts.push({ label: 'Go to Reports', onClick: () => switchReports('per-user') });
            return acts;
        }
        if (tab === 'teams' && isAdmin) return [
            { label: 'View Overview', onClick: () => setAnalyticsSubTab('overview') },
        ];
        if (tab === 'departments' && isAdmin) {
            const acts = [];
            const topDept = (aggregatedDepartments || [])[0];
            if (topDept) acts.push({ label: `View Users in ${topDept.department}`, onClick: () => { setDeptDrilldown(topDept.department); setAnalyticsSubTab('users'); } });
            acts.push({ label: 'View All Users', onClick: () => setAnalyticsSubTab('users') });
            acts.push({ label: 'Go to Reports', onClick: () => switchReports('cost-by-dept') });
            return acts;
        }
        if (tab === 'organisations') return [
            { label: 'View Departments Breakdown', onClick: () => setAnalyticsSubTab('departments') },
            { label: 'View Users by Org Unit', onClick: () => setAnalyticsSubTab('users') },
            { label: 'Go to Reports', onClick: () => switchReports('cost-by-org') },
            { label: 'Download Organisations CSV', onClick: () => {
                const total = (aggregatedOrgUnits || []).reduce((s, o) => s + o.spend, 0);
                const rows = [['Organisation', 'Users', 'Requests', 'Tokens', 'Spend', '% of Total']];
                [...(aggregatedOrgUnits || [])].sort((a, b) => b.spend - a.spend).forEach(o => rows.push([o.orgUnit || 'Unknown', o.users, o.requests, o.tokens, (o.spend || 0).toFixed(4), total > 0 ? ((o.spend / total) * 100).toFixed(1) + '%' : '0%']));
                const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
                const blob = new Blob([csv], { type: 'text/csv' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a'); a.href = url;
                const now = new Date(); a.download = `organisations-breakdown-${String(now.getDate()).padStart(2,'0')}-${String(now.getMonth()+1).padStart(2,'0')}-${now.getFullYear()}.csv`;
                a.click(); URL.revokeObjectURL(url);
            }},
        ];
        if (tab === 'overview' && isAdmin) return [
            { label: 'Download Report', onClick: () => switchReports('management') },
        ];
        return [];
    }, [analyticsSubTab, isAdmin, onSwitchToReports, topUsersBySpend, aggregatedDepartments, aggregatedOrgUnits, spendCache, userEmail]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Cross-section search results ──────────────────────────────────────────
    const searchResults = useMemo(() => {
        const q = headerSearch.trim().toLowerCase();
        if (!q) return null;
        const results = [];
        // Activity logs
        activityLogs.filter(r => {
            const model = (r.model || r.modelId || '').toLowerCase();
            const dept = (r.department || r.team || '').toLowerCase();
            const user = (r.endUser || r.user || '').toLowerCase();
            return model.includes(q) || dept.includes(q) || user.includes(q);
        }).slice(0, 5).forEach(r => results.push({ section: 'activity', label: resolveModelName(r.model || r.modelId || '—'), sub: r.endUser || r.department || '', value: fmt2(r.cost ?? r.spend ?? 0) }));
        // Models
        aggregatedModels.filter(m => resolveModelName(m.name).toLowerCase().includes(q) || m.name.toLowerCase().includes(q)).slice(0, 3).forEach(m => results.push({ section: 'models', label: resolveModelName(m.name), sub: `${m.requests} requests`, value: fmt2(m.spend) }));
        // Users
        topUsersBySpend.filter(u => (u.email || '').toLowerCase().includes(q)).slice(0, 3).forEach(u => results.push({ section: 'users', label: u.email, sub: u.department || u.teams || '', value: fmt2(u.spend) }));
        // Teams
        visibleTeams.filter(t => (t.team_alias || t.team_id || '').toLowerCase().includes(q)).slice(0, 3).forEach(t => results.push({ section: 'teams', label: t.team_alias || t.team_id, sub: `${t.userCount || 0} users`, value: fmt2(t.spend || 0) }));
        return results;
    }, [headerSearch, activityLogs, aggregatedModels, topUsersBySpend, visibleTeams]);

    // ── Top users per team (for Teams tab) ────────────────────────────────────────
    const topUsersPerTeam = useMemo(() => {
        // If no team selected or "all" is selected, return null (not loading, just no selection)
        if (!selectedTeamForUsers || selectedTeamForUsers === 'all') {
            return null;
        }

        // Find the selected team
        const selectedTeam = visibleTeams.find(t => t.team_id === selectedTeamForUsers);
        if (!selectedTeam) {
            return null;
        }

        // Get spend data for this team
        const cache = spendCache[selectedTeam.team_id];
        if (!cache) {
            // No cache at all - still loading
            return undefined;
        }

        if (!cache.data || cache.data.length === 0) {
            // Cache exists but no data - return empty array (not loading, just no data)
            return [];
        }

        // Build a map of user email -> team memberships
        const userToTeamsMap = {};
        groups.forEach(group => {
            (group.user_ids || []).forEach(userId => {
                const user = users.find(u => u.id === userId || u.user_id === userId);
                const userEmail = (user?.email || user?.user_email || '').toLowerCase();
                if (userEmail) {
                    if (!userToTeamsMap[userEmail]) {
                        userToTeamsMap[userEmail] = new Set();
                    }
                    const matchingTeam = teams.find(t =>
                        (t.team_alias && t.team_alias.toLowerCase() === group.name.toLowerCase()) ||
                        t.team_id === group.name
                    );
                    if (matchingTeam) {
                        userToTeamsMap[userEmail].add(matchingTeam.team_alias || matchingTeam.team_id);
                    }
                }
            });
        });

        // Get top 10 users by spend for this team
        return cache.data
            .filter(u => u.email && u.spend > 0)
            .sort((a, b) => b.spend - a.spend)
            .slice(0, 10)
            .map((u, i) => {
                const userTeams = userToTeamsMap[(u.email || '').toLowerCase()] || new Set();
                const budgetPercent = selectedTeam.max_budget > 0
                    ? (u.spend / selectedTeam.max_budget) * 100
                    : 0;
                return {
                    email: u.email,
                    spend: u.spend || 0,
                    requests: u.requests || 0,
                    tokens: u.tokens || 0,
                    budgetPercent: budgetPercent,
                    teams: Array.from(userTeams).join(', ') || 'No team',
                    color: CHART_COLORS[i % CHART_COLORS.length]
                };
            });
    }, [selectedTeamForUsers, visibleTeams, spendCache, groups, users, teams]);

    // ── Daily usage heatmap data (uses aggregated data for fast loading) ─────────
    const heatmapData = useMemo(() => {
        if (activeTab !== 'analytics' || !aggregatedActivity.data || aggregatedActivity.data.length === 0) {
            return [];
        }

        // Use aggregated daily data (fast, no pagination needed)
        // Build a map of date -> data for quick lookup
        const dataByDate = {};

        aggregatedActivity.data.forEach(day => {
            const date = new Date(day.date);
            const dayOfWeek = date.getDay(); // 0-6 (Sun-Sat)
            const dateKey = day.date; // YYYY-MM-DD format

            let requests = 0;
            let cost = 0;

            const models = day.breakdown?.models || {};

            // Apply model filter if models are selected
            const modelsToProcess = selectedModels.length > 0
                ? Object.keys(models).filter(m => selectedModels.includes(m))
                : Object.keys(models);

            // Sum requests and cost, filtering out health checks
            modelsToProcess.forEach(modelName => {
                const modelData = models[modelName];
                const apiKeyBreakdown = modelData?.api_key_breakdown || {};

                Object.entries(apiKeyBreakdown).forEach(([apiKeyHash, keyData]) => {
                    // Skip health checks unless explicitly enabled
                    // Use comprehensive detection logic matching backend
                    if (!includeHealthChecks) {
                        // 1. Check if API key hash indicates health check
                        if (apiKeyHash === 'litellm-internal-health-check' ||
                            apiKeyHash.toLowerCase().includes('health') ||
                            apiKeyHash.toLowerCase().includes('ping') ||
                            apiKeyHash.toLowerCase().includes('status')) {
                            return;
                        }

                        // 2. Check if team_id indicates health check
                        const teamId = keyData.metadata?.team_id || '';
                        if (teamId === 'litellm-internal-health-check' ||
                            teamId.toLowerCase().includes('health') ||
                            teamId.toLowerCase().includes('ping') ||
                            teamId.toLowerCase().includes('status')) {
                            return;
                        }

                        // 3. Check if end_user indicates health check
                        const endUser = keyData.metadata?.end_user || '';
                        if (endUser.toLowerCase().includes('health') ||
                            endUser.toLowerCase().includes('ping')) {
                            return;
                        }

                        // 4. Check if model name indicates health check
                        if (modelName === 'health-check' ||
                            modelName === 'health' ||
                            modelName === 'ping' ||
                            modelName === 'status') {
                            return;
                        }
                    }

                    // If team filter is active, only count data from that team
                    if (activityTeamFilter !== 'all') {
                        if (keyData.metadata?.team_id === activityTeamFilter) {
                            requests += keyData.metrics?.successful_requests || 0;
                            cost += keyData.metrics?.spend || 0;
                        }
                    } else {
                        // Count data from all VISIBLE teams only (exclude requests with team_id: null or unknown teams)
                        const teamId = keyData.metadata?.team_id || '';
                        const visibleTeamIds = visibleTeams.map(t => t.team_id);
                        if (teamId && visibleTeamIds.includes(teamId)) {
                            requests += keyData.metrics?.successful_requests || 0;
                            cost += keyData.metrics?.spend || 0;
                        }
                    }
                });
            });

            dataByDate[dateKey] = {
                day: dayOfWeek,
                date: dateKey,
                requests,
                cost,
                users: new Set() // Track unique users for this date
            };
        });

        // Track unique users per day by examining the aggregated data
        // Note: aggregated data may not have granular user data, so we'll calculate this from activityLogs instead
        return Object.values(dataByDate);
    }, [activeTab, aggregatedActivity.data, activityTeamFilter, selectedModels, includeHealthChecks]);

    // ── Calculate unique users per day from activity logs for heatmap overlay ──────
    const dailyUniqueUsers = useMemo(() => {
        if (!activityLogs || activityLogs.length === 0) {
            return {};
        }

        const usersByDate = {};

        activityLogs.forEach(log => {
            // Apply model filter if active
            if (selectedModels.length > 0 && !selectedModels.includes(log.model)) {
                return;
            }

            const logDate = (log.endTime || log.startTime || '').split('T')[0];
            const endUser = log.endUser;

            if (logDate && endUser && endUser !== '' && endUser !== '—') {
                if (!usersByDate[logDate]) {
                    usersByDate[logDate] = new Set();
                }
                usersByDate[logDate].add(endUser);
            }
        });

        // Convert Sets to counts
        const userCountsByDate = {};
        Object.keys(usersByDate).forEach(date => {
            userCountsByDate[date] = usersByDate[date].size;
        });

        return userCountsByDate;
    }, [activityLogs, selectedModels]);

    // ── Cost forecast with linear regression ──────────────────────────────────────
    const forecastData = useMemo(() => {
        if (!lineChartData || lineChartData.length < 3) return { actual: [], forecast: [] };

        // Perform linear regression on existing data
        const n = lineChartData.length;
        const xValues = lineChartData.map((_, i) => i);
        const yValues = lineChartData.map(d => d.cost);

        const sumX = xValues.reduce((a, b) => a + b, 0);
        const sumY = yValues.reduce((a, b) => a + b, 0);
        const sumXY = xValues.reduce((sum, x, i) => sum + x * yValues[i], 0);
        const sumX2 = xValues.reduce((sum, x) => sum + x * x, 0);

        const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
        const intercept = (sumY - slope * sumX) / n;

        // Generate forecast for next 7 days
        const forecast = [];
        // Get the last date from lineChartData to calculate future dates
        const lastDataPoint = lineChartData[lineChartData.length - 1];

        // Validate that fullDate exists and is valid
        if (!lastDataPoint.fullDate) {
            console.warn('[UsageCost] Last data point missing fullDate, using fallback dates');
            // Fallback to "+Xd" format if fullDate is missing
            for (let i = 0; i < 7; i++) {
                const forecastIndex = n + i;
                const forecastCost = Math.max(0, slope * forecastIndex + intercept);
                forecast.push({
                    date: `+${i + 1}d`,
                    actualCost: null,
                    forecastCost: forecastCost
                });
            }
        } else {
            const lastDate = new Date(lastDataPoint.fullDate);

            // Check if date is valid
            if (isNaN(lastDate.getTime())) {
                console.warn('[UsageCost] Invalid lastDate, using fallback dates');
                // Fallback to "+Xd" format if date is invalid
                for (let i = 0; i < 7; i++) {
                    const forecastIndex = n + i;
                    const forecastCost = Math.max(0, slope * forecastIndex + intercept);
                    forecast.push({
                        date: `+${i + 1}d`,
                        actualCost: null,
                        forecastCost: forecastCost
                    });
                }
            } else {
                // Valid date - calculate actual future dates
                for (let i = 0; i < 7; i++) {
                    const forecastIndex = n + i;
                    const forecastCost = Math.max(0, slope * forecastIndex + intercept);

                    // Calculate the actual future date
                    const futureDate = new Date(lastDate);
                    futureDate.setDate(futureDate.getDate() + i + 1);
                    const futureDateStr = futureDate.toISOString().split('T')[0];

                    forecast.push({
                        date: fmtDateShort(futureDateStr + 'T00:00:00'),
                        actualCost: null,
                        forecastCost: forecastCost
                    });
                }
            }
        }

        // Separate actual and forecast data
        const actual = lineChartData.map(d => ({
            date: d.date,
            actualCost: d.cost,
            forecastCost: null
        }));

        return { actual, forecast };
    }, [lineChartData]);

    // ── Model performance metrics (uses aggregated data for fast loading) ────────
    const modelPerformance = useMemo(() => {
        if (!aggregatedActivity.data || aggregatedActivity.data.length === 0) {
            return [];
        }

        // Helper function to check if a model or API key is a health check
        const isHealthCheckModel = (model) => {
            const healthCheckModels = ['health-check', 'health', 'ping', 'status'];
            return healthCheckModels.includes(model?.toLowerCase());
        };

        const isHealthCheckApiKey = (apiKeyHash) => {
            // Health check API keys are identified by this specific hash
            return apiKeyHash === 'litellm-internal-health-check';
        };

        const modelStats = {};

        // Get visible team IDs from Team Management
        const visibleTeamIds = visibleTeams.map(t => t.team_id);

        // Aggregate across all days and models
        aggregatedActivity.data.forEach(day => {
            const models = day.breakdown?.models || day.models || {};
            Object.entries(models).forEach(([model, data]) => {
                // Skip health check models unless explicitly enabled
                if (!includeHealthChecks && isHealthCheckModel(model)) {
                    return;
                }

                const apiKeyBreakdown = data.api_key_breakdown || {};

                // Iterate through each API key's data
                Object.entries(apiKeyBreakdown).forEach(([apiKeyHash, keyData]) => {
                    // Skip health check API keys unless explicitly enabled
                    if (!includeHealthChecks && isHealthCheckApiKey(apiKeyHash)) {
                        return;
                    }

                    const keyTeamId = keyData.metadata?.team_id;

                    // First, filter by visible teams from Team Management
                    if (!visibleTeamIds.includes(keyTeamId)) {
                        return;
                    }

                    // Then, if team filter dropdown is active, only include data from that team
                    if (activityTeamFilter !== 'all' && keyTeamId !== activityTeamFilter) {
                        return;
                    }

                    // Aggregate the metrics
                    if (!modelStats[model]) {
                        modelStats[model] = {
                            model,
                            totalCost: 0,
                            totalRequests: 0,
                            totalLatency: 0,
                            latencyCount: 0
                        };
                    }
                    modelStats[model].totalCost += keyData.metrics?.spend || 0;
                    modelStats[model].totalRequests += (keyData.metrics?.successful_requests || 0);
                    if (keyData.metrics?.total_latency && keyData.metrics?.successful_requests > 0) {
                        modelStats[model].totalLatency += keyData.metrics.total_latency;
                        modelStats[model].latencyCount += keyData.metrics.successful_requests;
                    }
                });
            });
        });

        return Object.values(modelStats)
            .map((m, i) => ({
                model: m.model, // Keep raw model name for filtering
                displayName: resolveModelName(m.model), // Formatted name for display
                avgLatency: m.latencyCount > 0 ? Math.round(m.totalLatency / m.latencyCount) : 0,
                costPer1K: m.totalRequests > 0 ? (m.totalCost / m.totalRequests) * 1000 : 0,
                successRate: 100, // Aggregated data doesn't track failures, assume 100%
                requests: m.totalRequests,
                color: CHART_COLORS[i % CHART_COLORS.length]
            }))
            .filter(m => m.requests > 5) // Only show models with >5 requests
            .sort((a, b) => b.requests - a.requests)
            .slice(0, 8);
    }, [aggregatedActivity.data, activityTeamFilter, includeHealthChecks, visibleTeams]);


    // ── CONSOLIDATED: Fetch team spend when needed (Analytics, Overview, Report tabs) ──
    // This single effect replaces three separate effects to prevent rate limiting
    // by coordinating all fetch requests and preventing duplicate calls

    useEffect(() => {
        // Debounce timer to batch requests
        const timerId = setTimeout(() => {
            if (activeTab === 'analytics') {
                // For Analytics tab, use analytics-specific date range
                const { start, end } = analyticsDateRange;

                // Check if we need to refetch (date range or teams changed)
                const teamsKey = visibleTeams.map(t => t.team_id).sort().join(',');
                const lastFetch = lastAnalyticsFetchRef.current;
                const needsRefetch = lastFetch.start !== start || lastFetch.end !== end || lastFetch.teams !== teamsKey;

                if (needsRefetch) {
                    console.log('[UsageCost] Analytics tab: Fetching spend for date range:', start, 'to', end);
                    visibleTeams.forEach(team => {
                        fetchTeamSpend(team, start, end);
                    });

                    // Update last fetch tracking
                    lastAnalyticsFetchRef.current = { start, end, teams: teamsKey };
                }
            } else if (activeTab === 'overview' || activeTab === 'report') {
                // For Overview/Report tabs, use team budget periods
                // Only fetch teams that don't have data yet
                visibleTeams.forEach(team => {
                    const cache = spendCache[team.team_id];
                    if (!cache || (!cache.loading && !cache.data)) {
                        fetchTeamSpend(team);
                    }
                });
            }

            // Also handle model filter: ensure all visible teams have data when filter is applied
            if (modelFilter && (activeTab === 'overview' || activeTab === 'analytics')) {
                visibleTeams.forEach(team => {
                    const cache = spendCache[team.team_id];
                    if (!cache || (!cache.loading && !cache.data)) {
                        fetchTeamSpend(team);
                    }
                });
            }
        }, 300); // 300ms debounce to batch rapid changes

        return () => clearTimeout(timerId);
    }, [activeTab, visibleTeams, modelFilter, analyticsTimeRange, analyticsDateFrom, analyticsDateTo, spendCache, fetchTeamSpend, analyticsDateRange]);

    // ── Fetch health check costs ───────────────────────────────────────────────
    // Fetches spend for LiteLLM's internal health checks via /global/spend/tags endpoint
    // Filters for the 'litellm-internal-health-check' tag

    const fetchHealthCheckCosts = useCallback(async () => {
        setHealthCheckCosts(prev => ({ ...prev, loading: true, error: null }));

        try {
            // Calculate date range for current month (use local timezone to avoid UTC conversion issues)
            const now = new Date();
            const year = now.getFullYear();
            const month = String(now.getMonth() + 1).padStart(2, '0');
            const day = String(now.getDate()).padStart(2, '0');

            const startDate = `${year}-${month}-01`;
            const endDate = `${year}-${month}-${day}`;

            // Fetch health check spend from /global/spend/tags endpoint
            const result = await getHealthCheckSpend(startDate, endDate);

            if (!result.success) {
                setHealthCheckCosts({
                    spend: 0,
                    requests: 0,
                    loading: false,
                    error: result.error || 'Failed to fetch health check spend'
                });
                return;
            }

            setHealthCheckCosts({
                spend: result.data.spend || 0,
                requests: result.data.requests || 0,
                loading: false,
                error: null
            });
        } catch (error) {
            console.error('[HealthCheck] Error fetching costs:', error);
            setHealthCheckCosts({
                spend: 0,
                requests: 0,
                loading: false,
                error: error.message
            });
        }
    }, []);

    // ── Fetch aggregated activity data ────────────────────────────────────────
    // Fetches daily aggregated data from /user/daily/activity/aggregated
    // This provides real daily breakdowns instead of placeholder evenly-distributed data

    const fetchAggregatedActivity = useCallback(async () => {
        // Prevent multiple simultaneous calls
        if (aggregatedActivityFetchingRef.current) {
            return; // Already fetching, skip
        }

        // Cancel any in-flight requests
        if (aggregatedActivityAbortControllerRef.current) {
            aggregatedActivityAbortControllerRef.current.abort();
        }

        aggregatedActivityFetchingRef.current = true;
        aggregatedActivityAbortControllerRef.current = new AbortController();

        const { start, end } = analyticsDateRange;

        // Always show loading state when fetching new data
        setAggregatedActivity(prev => ({
            ...prev,
            loading: true,
            error: null
        }));

        try {
            const result = await getUsageCostData(start, end);

            // Check if request was aborted
            if (aggregatedActivityAbortControllerRef.current?.signal.aborted) {
                return;
            }

            if (result.success) {
                const results = result.data?.results || [];

                setAggregatedActivity({
                    data: results,
                    loading: false,
                    error: null,
                    hasLoadedOnce: true
                });
            } else {
                setAggregatedActivity({
                    data: [],
                    loading: false,
                    error: result.error || 'Failed to load aggregated activity data',
                    hasLoadedOnce: true
                });
            }
        } catch (error) {
            // Ignore abort errors
            if (error.name === 'AbortError') {
                return;
            }

            setAggregatedActivity({
                data: [],
                loading: false,
                error: error.message,
                hasLoadedOnce: true
            });
        } finally {
            aggregatedActivityFetchingRef.current = false;
            aggregatedActivityAbortControllerRef.current = null;
        }
    }, [analyticsDateRange]);

    // ── Comprehensive refresh functions ───────────────────────────────────────

    // Refresh Overview tab data
    const refreshOverview = () => {
        console.log('[UsageCost] Refreshing Overview data...');
        visibleTeams.forEach(team => {
            fetchTeamSpend(team);
        });
        fetchHealthCheckCosts();
    };

    // Refresh Analytics tab data
    const refreshAnalytics = () => {
        console.log('[UsageCost] Refreshing all Analytics data...');

        // Refresh activity logs (table data)
        if (analyticsSubTab === 'activity' || analyticsSubTab === 'models' || analyticsSubTab === 'keys') {
            fetchActivity(1);
        }

        // Refresh aggregated data (charts, heatmap, model performance)
        if (analyticsSubTab === 'activity' || analyticsSubTab === 'models' || analyticsSubTab === 'keys') {
            fetchAggregatedActivity();
        }

        // Refresh team spend data for teams, users, departments, my-usage, and my-team tabs
        if (analyticsSubTab === 'teams' || analyticsSubTab === 'users' || analyticsSubTab === 'departments' || analyticsSubTab === 'my-usage' || analyticsSubTab === 'my-team') {
            const { start, end } = analyticsDateRange;
            visibleTeams.forEach(team => {
                fetchTeamSpend(team, start, end);
            });
        }
    };

    // Export Keys Analytics to CSV
    const exportKeysAnalytics = () => {
        const csvData = [];

        // Header row
        csvData.push(['API Key', 'Team', 'Spend', 'Requests', 'Tokens', 'Success Rate', 'Date Range']);

        // Escape CSV values (handle commas and quotes)
        const escapeCSV = (value) => {
            if (value == null) return '';
            const stringValue = String(value);
            if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
                return `"${stringValue.replace(/"/g, '""')}"`;
            }
            return stringValue;
        };

        // Data rows
        apiKeyPerformance.forEach(key => {
            const teamName = visibleTeams.find(t => t.team_id === key.teamId)?.team_alias || key.teamId || 'N/A';
            const successRate = key.requests > 0 ? ((key.successfulRequests / key.requests) * 100).toFixed(1) + '%' : '0%';

            csvData.push([
                escapeCSV(key.alias),
                escapeCSV(teamName),
                fmt2(key.spend),
                key.requests,
                key.tokens,
                successRate,
                `${analyticsDateRange.start} to ${analyticsDateRange.end}`
            ]);
        });

        // Convert to CSV string
        const csvContent = csvData.map(row => row.join(',')).join('\n');

        // Download with UK date format (DD-MM-YYYY)
        const now = new Date();
        const day = String(now.getDate()).padStart(2, '0');
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const year = now.getFullYear();
        const ukDate = `${day}-${month}-${year}`;

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `api-keys-analytics-${ukDate}.csv`;
        link.click();
    };

    // ── Fetch recent activity ──────────────────────────────────────────────────

    const fetchActivity = useCallback(async (page = 1) => {
        setActivityLoading(true);
        setActivityError('');
        setActivitySummary(null);

        // Get visible team IDs for filtering based on Team Management selection
        const visibleTeamIds = visibleTeams.map(t => t.team_id);

        // Apply additional team filter from Recent Activity dropdown
        // Always filter by visible teams to exclude requests not associated with managed teams
        let filteredTeamIds = visibleTeamIds;
        if (activityTeamFilter !== 'all') {
            filteredTeamIds = [activityTeamFilter];
        }

        // Pass list of all known team IDs to backend for health check detection
        const knownTeamIds = teams.map(t => t.team_id).filter(Boolean);

        // Use analytics date range, but if a specific date is selected from heatmap, use that
        const { start, end } = analyticsDateRange;
        const effectiveStartDate = activityDateFilter !== 'all' ? activityDateFilter : start;
        const effectiveEndDate = activityDateFilter !== 'all' ? activityDateFilter : end;

        const result = await getSpendLogs({
            page,
            pageSize: ACTIVITY_PAGE_SIZE,
            startDate: effectiveStartDate,
            endDate: effectiveEndDate,
            users: users, // Pass users for hash-to-email lookup
            teamIds: filteredTeamIds, // Filter by selected teams
            knownTeamIds: knownTeamIds, // Pass known team IDs for health check detection
            models: selectedModels, // Pass selected models for filtering
        });
        if (result.success) {
            setActivityLogs(result.data || []);
            setActivityTotal(result.total || 0);
            setActivityTotalPages(result.total_pages || 1);
            setActivityPage(page);
            setActivitySummary(result.summary || null);
        } else {
            setActivityError(result.error || 'Failed to load activity logs.');
        }
        setActivityLoading(false);
    }, [users, visibleTeams, teams, analyticsDateRange, activityTeamFilter, selectedModels, activityDateFilter]);

    // Fetch aggregated and activity data when Analytics tab is opened or date range changes
    useEffect(() => {
        if (activeTab === 'analytics') {
            // Fetch activity data for activity, models, keys, and overview sub-tabs
            if (analyticsSubTab === 'activity' || analyticsSubTab === 'models' || analyticsSubTab === 'keys' || analyticsSubTab === 'overview') {
                fetchActivity(1);
            }
            // Fetch aggregated data for activity, models, keys, and overview sub-tabs (for charts)
            if (analyticsSubTab === 'activity' || analyticsSubTab === 'models' || analyticsSubTab === 'keys' || analyticsSubTab === 'overview') {
                fetchAggregatedActivity();
            }
            // Fetch team spend for teams, users, departments, my-usage, my-team, and overview sub-tabs
            if (analyticsSubTab === 'teams' || analyticsSubTab === 'users' || analyticsSubTab === 'departments' || analyticsSubTab === 'organisations' || analyticsSubTab === 'my-usage' || analyticsSubTab === 'my-team' || analyticsSubTab === 'overview') {
                const { start, end } = analyticsDateRange;
                visibleTeams.forEach(team => {
                    fetchTeamSpend(team, start, end);
                });
            }
            // Fetch routing info for My Usage model table
            if (analyticsSubTab === 'my-usage' && userEmail) {
                const { start, end } = analyticsDateRange;
                const myVisibleTeamIds = visibleTeams.map(t => t.team_id).filter(Boolean);
                const knownTeamIds = teams.map(t => t.team_id).filter(Boolean);
                getSpendLogs({
                    pageSize: 500,
                    startDate: start,
                    endDate: end,
                    users,
                    teamIds: myVisibleTeamIds,
                    knownTeamIds,
                    endUser: userEmail,
                }).then(result => {
                    if (!result.success) return;
                    const routing = {};
                    (result.data || []).forEach(r => {
                        if (!routing[r.model]) {
                            routing[r.model] = r.autoRouted ? (r.requestedModel || null) : null;
                        }
                    });
                    setMyModelRouting(routing);
                }).catch(() => {});
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, analyticsSubTab, analyticsDateRange.start, analyticsDateRange.end, activityDateFilter, selectedModels, activityTeamFilter]);

    // ── Load health check costs when Overview tab opens ────────────────────────
    // Fetches health check spend via /global/spend/tags endpoint

    useEffect(() => {
        if (activeTab === 'overview' && healthCheckCosts.loading) {
            fetchHealthCheckCosts();
        }
    }, [activeTab, healthCheckCosts.loading, fetchHealthCheckCosts]);

    // ── Calculate unique users from activity logs ──────────────────────────────
    useEffect(() => {
        // Prefer backend summary — it aggregates across all pages, not just the displayed page
        if (activitySummary) {
            setUniqueUsersTotal(activitySummary.unique_users ?? 0);
            setPeakDailyUsers(activitySummary.peak_daily_users ?? 0);
            return;
        }
        if (activityLogs && activityLogs.length > 0) {
            const uniqueEmails = new Set(
                activityLogs
                    .map(log => log.endUser)
                    .filter(email => email && email !== '' && email !== '—')
            );
            setUniqueUsersTotal(uniqueEmails.size);

            const usersByDate = {};
            activityLogs.forEach(log => {
                const logDate = (log.endTime || log.startTime || '').split('T')[0];
                const endUser = log.endUser;
                if (logDate && endUser && endUser !== '' && endUser !== '—') {
                    if (!usersByDate[logDate]) usersByDate[logDate] = new Set();
                    usersByDate[logDate].add(endUser);
                }
            });
            const maxDailyUsers = Math.max(0, ...Object.values(usersByDate).map(set => set.size));
            setPeakDailyUsers(maxDailyUsers);
        } else {
            setUniqueUsersTotal(0);
            setPeakDailyUsers(0);
        }
    }, [activityLogs, activitySummary]);

    // ── Load notifications from audit log on mount ────────────────────────────
    useEffect(() => {
        if (notificationsLoaded) return;
        setNotificationsLoaded(true);
        const ALERT_ACTIONS = ['team_blocked', 'user_blocked', 'team_budget_updated', 'team_unblocked'];
        authenticatedFetch('/api/audit?limit=50')
            .then(r => r.ok ? r.json() : null)
            .then(data => {
                if (!data) return;
                const entries = Array.isArray(data) ? data : (data.logs || data.entries || []);
                const alerts = entries
                    .filter(e => ALERT_ACTIONS.includes(e.action))
                    .slice(0, 20)
                    .map(e => ({
                        id: e.timestamp || Math.random(),
                        action: e.action,
                        details: e.details || '',
                        email: e.email || '',
                        timestamp: e.timestamp || e.server_timestamp || '',
                    }));
                setNotifications(alerts);
                if (alerts.length > 0) setHasUnread(true);
            })
            .catch(() => {});
    }, [notificationsLoaded]);

    // ── Expand team row & load spend ──────────────────────────────────────────

    const handleExpandTeam = (team) => {
        if (!team) return;
        setExpandedTeamIds(prev => {
            const next = new Set(prev);
            if (next.has(team.team_id)) {
                next.delete(team.team_id);
            } else {
                next.add(team.team_id);
            }
            return next;
        });
        if (!spendCache[team.team_id]) fetchTeamSpend(team);
    };

    // ── Refresh spend when date range changes ─────────────────────────────────

    const handleDateRangeApply = () => {
        setSpendCache({});
        setAnalyticsLoaded(false);
    };

    // ── CSV export ────────────────────────────────────────────────────────────

    const downloadCSV = () => {
        const rows = [['Team', 'Period Spend', 'Budget', 'Used %', 'Users', 'Reset']];
        const teamsToExport = reportTeams.size > 0
            ? visibleTeams.filter(t => reportTeams.has(t.team_id))
            : visibleTeams;
        teamsToExport.forEach(t => {
            const pct = t.max_budget > 0 ? ((t.spend / t.max_budget) * 100).toFixed(1) : 'N/A';
            rows.push([t.team_alias || t.team_id, t.spend.toFixed(2), t.max_budget.toFixed(2), pct, t.userCount, fmtDuration(t.budget_duration)]);
        });
        // Also add per-user breakdown
        rows.push([]);
        rows.push(['Team', 'User', 'Spend', 'Tokens', 'Requests', 'Models']);
        teamsToExport.forEach(t => {
            const cache = spendCache[t.team_id];
            (cache?.data || []).forEach(u => {
                const modelList = Object.keys(u.models || {}).map(m => resolveModelName(m)).join('; ');
                rows.push([t.team_alias || t.team_id, u.email, u.spend.toFixed(2), u.tokens, u.requests, modelList]);
            });
        });
        const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        // Format filename with UK date format (DD-MM-YYYY)
        const now = new Date();
        const ukDate = `${String(now.getDate()).padStart(2, '0')}-${String(now.getMonth() + 1).padStart(2, '0')}-${now.getFullYear()}`;
        a.download = `ask-aware-usage-report-${ukDate}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    // ── PDF export ────────────────────────────────────────────────────────────

    const downloadPDF = async () => {
        setGeneratingPdf(true);
        try {
            const { default: jsPDF } = await import('jspdf');
            const { default: autoTable } = await import('jspdf-autotable');
            const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
            const pageW = doc.internal.pageSize.getWidth();
            const margin = 14;

            // Try to load the AwareAI logo as base64
            let logoBase64 = null;
            try {
                const logoResp = await fetch('/AwareAI-logo.png');
                if (logoResp.ok) {
                    const blob = await logoResp.blob();
                    logoBase64 = await new Promise(resolve => {
                        const reader = new FileReader();
                        reader.onloadend = () => resolve(reader.result);
                        reader.readAsDataURL(blob);
                    });
                }
            } catch (_) { /* logo unavailable, skip */ }

            // Header background
            doc.setFillColor(15, 23, 42);
            doc.rect(0, 0, pageW, 32, 'F');

            // Add logo if available (right-aligned, contained within header)
            if (logoBase64) {
                try {
                    // Load image to get actual dimensions for aspect ratio
                    const img = new Image();
                    img.src = logoBase64;
                    await new Promise((resolve) => { img.onload = resolve; });
                    const aspectRatio = img.width / img.height;
                    // Logo at 15mm height to fit better in header, maintain aspect ratio
                    const logoH = 15;
                    const logoW = logoH * aspectRatio;
                    // Position logo on right side of header with some margin
                    doc.addImage(logoBase64, 'PNG', pageW - logoW - margin, 8.5, logoW, logoH);
                } catch (_) { /* skip if image fails */ }
            }

            doc.setTextColor(241, 245, 249);
            doc.setFontSize(14);
            doc.setFont('helvetica', 'bold');
            doc.text('Ask Aware — Usage & Cost Report', margin, 12);
            doc.setFontSize(8.5);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(148, 163, 184);
            // Format date range in UK format (DD/MM/YYYY)
            const formatDateUK = (dateStr) => {
                if (!dateStr) return 'today';
                const d = new Date(dateStr);
                return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
            };
            const dateRange = (reportDateFrom || activityDateFrom)
                ? `${formatDateUK(reportDateFrom || activityDateFrom)} to ${formatDateUK(reportDateTo || activityDateTo)}`
                : 'Current period';
            doc.text(`Date range: ${dateRange}`, margin, 20);
            doc.text(`Generated: ${new Date().toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}`, margin, 27);

            let y = 40;

            // Cost prediction section
            doc.setTextColor(99, 102, 241);
            doc.setFontSize(11);
            doc.setFont('helvetica', 'bold');
            doc.text('Cost Prediction', margin, y);
            y += 6;
            autoTable(doc, {
                startY: y,
                margin: { left: margin, right: margin },
                head: [['Metric', 'Value']],
                body: [
                    ['Current Month Spend', fmt2(prediction.currentSpend)],
                    ['Predicted End of Month', fmt2(prediction.predictedEOM)],
                    ['Daily Average', fmt2(prediction.dailyAvg)],
                    ['Days Remaining', String(prediction.daysRemaining)],
                ],
                styles: { fontSize: 9, cellPadding: 3 },
                headStyles: { fillColor: [30, 41, 59], textColor: [241, 245, 249] },
                alternateRowStyles: { fillColor: [241, 245, 249] },
            });
            y = doc.lastAutoTable.finalY + 10;

            // Team budget table
            doc.setTextColor(99, 102, 241);
            doc.setFontSize(11);
            doc.setFont('helvetica', 'bold');
            doc.text('Team Budget Summary', margin, y);
            y += 6;
            const teamsToExport = reportTeams.size > 0
                ? visibleTeams.filter(t => reportTeams.has(t.team_id))
                : visibleTeams;
            autoTable(doc, {
                startY: y,
                margin: { left: margin, right: margin },
                head: [['Team', 'Users', 'Period Spend', 'Budget', 'Used %', 'Reset']],
                body: teamsToExport.map(t => {
                    const pct = t.max_budget > 0 ? ((t.spend / t.max_budget) * 100).toFixed(1) + '%' : 'N/A';
                    return [t.team_alias || t.team_id, t.userCount, fmt2(t.spend), fmt2(t.max_budget), pct, fmtDuration(t.budget_duration)];
                }),
                styles: { fontSize: 8, cellPadding: 2.5 },
                headStyles: { fillColor: [30, 41, 59], textColor: [241, 245, 249] },
                alternateRowStyles: { fillColor: [248, 250, 252] },
            });
            y = doc.lastAutoTable.finalY + 10;

            // Per-user breakdown
            const teamsWithUserData = teamsToExport.filter(t => {
                const c = spendCache[t.team_id];
                return c && !c.loading && c.data && c.data.length > 0;
            });
            if (teamsWithUserData.length > 0) {
                doc.setTextColor(99, 102, 241);
                doc.setFontSize(11);
                doc.setFont('helvetica', 'bold');
                doc.text('Per-User Spend Breakdown', margin, y);
                y += 6;
                const userRows = [];
                teamsWithUserData.forEach(t => {
                    const cache = spendCache[t.team_id];
                    cache.data.forEach(u => {
                        const modelList = Object.keys(u.models || {}).map(m => resolveModelName(m)).join(', ');
                        userRows.push([t.team_alias || t.team_id, u.email, fmt2(u.spend), fmtTokens(u.tokens), u.requests, modelList]);
                    });
                });
                autoTable(doc, {
                    startY: y,
                    margin: { left: margin, right: margin },
                    head: [['Team', 'User', 'Spend', 'Tokens', 'Requests', 'Models']],
                    body: userRows,
                    styles: { fontSize: 7, cellPadding: 2 },
                    headStyles: { fillColor: [30, 41, 59], textColor: [241, 245, 249] },
                    alternateRowStyles: { fillColor: [248, 250, 252] },
                    columnStyles: { 0: { cellWidth: 28 }, 1: { cellWidth: 42 }, 5: { cellWidth: 'auto' } },
                });
                y = doc.lastAutoTable.finalY + 10;
            }

            // Model distribution
            if (aggregatedModels.length > 0) {
                doc.setTextColor(99, 102, 241);
                doc.setFontSize(11);
                doc.setFont('helvetica', 'bold');
                doc.text('Model Usage Distribution', margin, y);
                y += 6;
                autoTable(doc, {
                    startY: y,
                    margin: { left: margin, right: margin },
                    head: [['Model', 'Spend', 'Requests', 'Tokens']],
                    body: aggregatedModels.map(m => [resolveModelName(m.model), fmt2(m.spend), m.requests, fmtTokens(m.tokens)]),
                    styles: { fontSize: 8, cellPadding: 2.5 },
                    headStyles: { fillColor: [30, 41, 59], textColor: [241, 245, 249] },
                    alternateRowStyles: { fillColor: [248, 250, 252] },
                });
                y = doc.lastAutoTable.finalY + 10;
            }

            // Recent activity (up to 200 rows)
            if (activityLogs.length > 0) {
                doc.setTextColor(99, 102, 241);
                doc.setFontSize(11);
                doc.setFont('helvetica', 'bold');
                doc.text('Recent Activity', margin, y);
                y += 6;
                autoTable(doc, {
                    startY: y,
                    margin: { left: margin, right: margin },
                    head: [['Date/Time', 'User', 'Model', 'Cost', 'Tokens']],
                    body: activityLogs.slice(0, 200).map(r => [
                        fmtDate(r.startTime || r.endTime),
                        r.endUser || '—',
                        resolveModelName(r.model) || '—',
                        fmt2(r.spend),
                        fmtTokens(r.totalTokens),
                    ]),
                    styles: { fontSize: 7.5, cellPadding: 2 },
                    headStyles: { fillColor: [30, 41, 59], textColor: [241, 245, 249] },
                    alternateRowStyles: { fillColor: [248, 250, 252] },
                });
            }

            // Format PDF filename with UK date format (DD-MM-YYYY)
            const now = new Date();
            const ukDate = `${String(now.getDate()).padStart(2, '0')}-${String(now.getMonth() + 1).padStart(2, '0')}-${now.getFullYear()}`;
            doc.save(`ask-aware-usage-report-${ukDate}.pdf`);
        } catch (err) {
            console.error('[PDF] Error generating report:', err);
            alert('Failed to generate PDF: ' + err.message);
        } finally {
            setGeneratingPdf(false);
        }
    };

    // ── Render helpers ────────────────────────────────────────────────────────

    // ── Analytics Dashboard (new KPI overview) ────────────────────────────────
    const renderAnalyticsDashboard = () => {
        // KPI calculations — use filteredDailyData (team-scoped) not aggregatedActivity.data (global)
        const totalRequests = filteredDailyData.reduce((s, d) => s + (d.requests || 0), 0);
        const totalCost = filteredDailyData.reduce((s, d) => s + (d.spend || 0), 0);
        // Count from apiKeyPerformance which aggregates all days, not just the current page of activityLogs
        const activeKeys = apiKeyPerformance.length || new Set(activityLogs.map(l => l.apiKey).filter(Boolean)).size;
        const mcpCallCount = activityLogs.filter(l => l.isMCP).length;

        // Cost prediction — use totals.spend (from real team spend cache) for accuracy
        const now = new Date();
        const dayOfMonth = now.getDate();
        const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
        const daysRemaining = daysInMonth - dayOfMonth;
        const dailyAvg = dayOfMonth > 0 ? totalCost / dayOfMonth : 0;
        const predictedEOM = dailyAvg * daysInMonth;
        const firstHalf = filteredDailyData.slice(0, Math.floor(filteredDailyData.length / 2));
        const secondHalf = filteredDailyData.slice(Math.floor(filteredDailyData.length / 2));
        const firstHalfAvg = firstHalf.length > 0 ? firstHalf.reduce((s, d) => s + (d.spend || 0), 0) / firstHalf.length : 0;
        const secondHalfAvg = secondHalf.length > 0 ? secondHalf.reduce((s, d) => s + (d.spend || 0), 0) / secondHalf.length : 0;
        const trend = secondHalfAvg > firstHalfAvg * 1.05 ? 'increasing' : secondHalfAvg < firstHalfAvg * 0.95 ? 'decreasing' : 'stable';

        // Usage over time — dual-axis (cost + requests)
        // lineChartData maps api_requests → totalRequests, spend → cost
        const usageChartData = lineChartData.map(d => ({
            date: d.date,
            cost: parseFloat((d.cost || 0).toFixed(4)),
            requests: d.totalRequests || 0,
        }));

        // Model distribution donut — aggregatedModels uses .model not .name
        const donutData = aggregatedModels.slice(0, 6).map((m, i) => ({
            name: m.model,
            value: m.spend || 0,
            requests: m.requests || 0,
            color: CHART_COLORS[i % CHART_COLORS.length],
        }));
        const donutTotal = donutData.reduce((s, d) => s + d.value, 0);

        // Recent activity — last 8 rows
        const recentRows = activityLogs.slice(0, 8);

        const kpiCards = [
            {
                label: 'Total Requests',
                value: totalRequests.toLocaleString(),
                delta: null,
                color: '#6366f1',
                icon: (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 9h18" /><path d="M9 21V9" />
                    </svg>
                ),
            },
            {
                label: 'Total Cost',
                value: fmt2(totalCost),
                delta: null,
                color: '#22c55e',
                icon: (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
                    </svg>
                ),
            },
            {
                label: 'Active API Keys',
                value: activeKeys,
                delta: null,
                color: '#a855f7',
                icon: (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="7.5" cy="15.5" r="5.5" /><path d="M21 2l-9.6 9.6" /><path d="M15.5 7.5l3 3L22 7l-3-3" />
                    </svg>
                ),
            },
            {
                label: 'Predicted Monthly',
                value: fmt2(predictedEOM),
                delta: `${fmt2(dailyAvg)}/day avg`,
                color: '#f59e0b',
                icon: (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" />
                    </svg>
                ),
            },
            {
                label: 'MCP Calls',
                value: mcpCallCount.toLocaleString(),
                delta: null,
                color: '#f59e0b',
                icon: (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 2L2 7l10 5 10-5-10-5z" /><path d="M2 17l10 5 10-5" /><path d="M2 12l10 5 10-5" />
                    </svg>
                ),
            },
        ];

        const loading = aggregatedActivity.loading && !aggregatedActivity.hasLoadedOnce;

        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

                {/* KPI cards row */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 16 }}>
                    {kpiCards.map(card => (
                        <div key={card.label} style={{
                            background: 'var(--surface)',
                            borderRadius: 14,
                            padding: '20px 20px 16px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 8,
                            position: 'relative',
                            border: '1px solid var(--border)',
                        }}>
                            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                                <div style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--fg-secondary)' }}>{card.label}</div>
                                <div style={{ width: 36, height: 36, borderRadius: 10, background: card.color + '20', display: 'flex', alignItems: 'center', justifyContent: 'center', color: card.color, flexShrink: 0 }}>
                                    {card.icon}
                                </div>
                            </div>
                            <div style={{ fontSize: '1.9rem', fontWeight: 800, color: 'var(--fg-primary)', lineHeight: 1, fontFamily: 'var(--font-display, inherit)' }}>
                                {loading ? <Skeleton height={32} width={80} /> : card.value}
                            </div>
                            {card.delta && (
                                <div style={{ fontSize: '0.75rem', color: 'var(--fg-secondary)' }}>{card.delta}</div>
                            )}
                        </div>
                    ))}
                </div>

                {/* Charts row — 60/40 */}
                <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 16 }}>
                    {/* Usage Over Time */}
                    <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: '20px 20px 12px' }}>
                        <div style={{ fontSize: '0.88rem', fontWeight: 700, color: 'var(--fg-primary)', marginBottom: 16 }}>Usage Over Time</div>
                        {loading
                            ? <Skeleton height={250} />
                            : usageChartData.length === 0
                                ? <div style={{ color: 'var(--muted)', fontSize: '0.85rem', textAlign: 'center', paddingTop: 80 }}>No data for selected range</div>
                                : (
                                    <ResponsiveContainer width="100%" height={250}>
                                        <LineChart data={usageChartData} margin={{ top: 5, right: 16, bottom: 5, left: 0 }}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" strokeOpacity={0.8} />
                                            <XAxis dataKey="date" tick={{ fontSize: 10, fill: 'var(--muted)' }} tickLine={false} axisLine={false} />
                                            <YAxis yAxisId="cost" tick={{ fontSize: 10, fill: 'var(--muted)' }} tickFormatter={v => `$${v.toFixed(2)}`} width={52} axisLine={false} tickLine={false} />
                                            <YAxis yAxisId="req" orientation="right" tick={{ fontSize: 10, fill: 'var(--muted)' }} tickFormatter={v => Math.round(v)} width={36} axisLine={false} tickLine={false} />
                                            <ReTooltip
                                                contentStyle={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12, color: 'var(--fg-primary)' }}
                                                labelStyle={{ color: 'var(--fg-secondary)' }}
                                                formatter={(v, name) => name === 'cost' ? [fmt2(v), 'Cost'] : [Math.round(v), 'Requests']}
                                            />
                                            <Legend wrapperStyle={{ fontSize: 11, color: 'var(--fg-secondary)', paddingTop: 8 }} formatter={v => v === 'cost' ? 'Cost' : 'Requests'} />
                                            <Line isAnimationActive={false}yAxisId="cost" type="monotone" dataKey="cost" stroke="#14b8a6" strokeWidth={2} dot={{ r: 3, fill: '#14b8a6' }} name="cost" isAnimationActive={false} />
                                            <Line isAnimationActive={false}yAxisId="req" type="monotone" dataKey="requests" stroke="#60A5FA" strokeWidth={2} dot={{ r: 3, fill: '#60A5FA' }} name="requests" isAnimationActive={false} />
                                        </LineChart>
                                    </ResponsiveContainer>
                                )}
                    </div>

                    {/* Model Usage Distribution donut */}
                    <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: '20px 20px 12px' }}>
                        <div style={{ fontSize: '0.88rem', fontWeight: 700, color: 'var(--fg-primary)', marginBottom: 16 }}>Model Usage Distribution</div>
                        {loading
                            ? <Skeleton height={250} />
                            : donutData.length === 0
                                ? <div style={{ color: 'var(--muted)', fontSize: '0.85rem', textAlign: 'center', paddingTop: 80 }}>No model data for selected range</div>
                                : (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                                        <div style={{ flexShrink: 0 }}>
                                            <PieChart width={160} height={160}>
                                                <Pie isAnimationActive={false}data={donutData} cx={75} cy={75} innerRadius={48} outerRadius={72} paddingAngle={2} dataKey="value" isAnimationActive={false}>
                                                    {donutData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                                                </Pie>
                                                <ReTooltip
                                                    contentStyle={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 11, color: 'var(--fg-primary)' }}
                                                    formatter={(v) => [fmt2(v), 'Spend']}
                                                />
                                            </PieChart>
                                        </div>
                                        <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', gap: 8 }}>
                                            {donutData.map(m => (
                                                <div key={m.name} style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                                                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: m.color, flexShrink: 0, marginTop: 4 }} />
                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                        <div style={{ fontSize: '0.72rem', color: 'var(--fg-primary)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={m.name}>{resolveModelName(m.name)}</div>
                                                        <div style={{ fontSize: '0.68rem', color: 'var(--fg-secondary)' }}>{m.requests.toLocaleString()} req</div>
                                                    </div>
                                                    <div style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--fg-primary)', flexShrink: 0 }}>{fmt2(m.value)}</div>
                                                </div>
                                            ))}
                                            {donutTotal > 0 && (
                                                <div style={{ fontSize: '0.68rem', color: 'var(--muted)', marginTop: 4 }}>
                                                    {donutData.length < aggregatedModels.length && `+${aggregatedModels.length - donutData.length} more models`}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                    </div>
                </div>

                {/* Bottom row — 65/35 */}
                <div style={{ display: 'grid', gridTemplateColumns: '1.85fr 1fr', gap: 16 }}>
                    {/* Recent Activity */}
                    <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: '20px 20px 0', overflow: 'hidden' }}>
                        <div style={{ fontSize: '0.88rem', fontWeight: 700, color: 'var(--fg-primary)', marginBottom: 12 }}>Recent Activity</div>
                        {activityLoading
                            ? <Skeleton height={280} />
                            : recentRows.length === 0
                                ? <div style={{ color: 'var(--muted)', fontSize: '0.85rem', paddingBottom: 20 }}>No recent activity</div>
                                : (
                                    <div style={{ overflowY: 'auto', maxHeight: 320 }}>
                                        {recentRows.map((row, i) => {
                                            const modelName = resolveModelName(row.model || row.modelId || '—');
                                            const ts = row.endTime || row.startTime || '';
                                            const tsDisplay = ts ? new Date(ts).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—';
                                            const dept = row.department || row.team || '—';
                                            const cost = row.cost ?? row.spend ?? 0;
                                            const tokens = row.totalTokens || row.tokens || 0;
                                            const dotColor = CHART_COLORS[i % CHART_COLORS.length];
                                            return (
                                                <div key={i} style={{
                                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                                    padding: '10px 0',
                                                    borderBottom: i < recentRows.length - 1 ? '1px solid var(--border)' : 'none',
                                                    gap: 12,
                                                }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                                                        <div style={{ width: 7, height: 7, borderRadius: '50%', background: dotColor, flexShrink: 0 }} />
                                                        <div style={{ minWidth: 0 }}>
                                                            <div style={{ fontSize: '0.82rem', color: 'var(--fg-primary)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{modelName}</div>
                                                            <div style={{ fontSize: '0.7rem', color: 'var(--fg-secondary)' }}>{tsDisplay} &bull; {dept}</div>
                                                        </div>
                                                    </div>
                                                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                                                        <div style={{ fontSize: '0.82rem', color: 'var(--fg-primary)', fontWeight: 700 }}>{fmt2(cost)}</div>
                                                        <div style={{ fontSize: '0.68rem', color: 'var(--fg-secondary)' }}>{fmtTokens(tokens)} tokens</div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                    </div>

                    {/* Right column: Cost Prediction */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                        {/* Cost Prediction */}
                        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: '20px' }}>
                            <div style={{ fontSize: '0.88rem', fontWeight: 700, color: 'var(--fg-primary)', marginBottom: 14 }}>Cost Prediction</div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                {[
                                    { label: 'Current Month', value: fmt2(totalCost) },
                                    { label: 'Predicted End of Month', value: fmt2(predictedEOM), highlight: true },
                                    { label: 'Daily Average', value: fmt2(dailyAvg) },
                                    { label: 'Days Remaining', value: String(daysRemaining) },
                                ].map(row => (
                                    <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <span style={{ fontSize: '0.8rem', color: 'var(--fg-secondary)' }}>{row.label}</span>
                                        <span style={{ fontSize: '0.88rem', fontWeight: 700, color: row.highlight ? '#f59e0b' : 'var(--fg-primary)' }}>
                                            {loading ? '...' : row.value}
                                        </span>
                                    </div>
                                ))}
                                <div style={{ marginTop: 4 }}>
                                    <span style={{
                                        fontSize: '0.72rem',
                                        fontWeight: 600,
                                        padding: '3px 10px',
                                        borderRadius: 999,
                                        background: trend === 'increasing' ? 'var(--critical-bg-hover)' : trend === 'decreasing' ? 'rgba(74,222,128,0.10)' : 'var(--surface-alt)',
                                        color: trend === 'increasing' ? '#F87171' : trend === 'decreasing' ? '#4ADE80' : 'var(--fg-secondary)',
                                        textTransform: 'capitalize',
                                    }}>
                                        Trend: {trend}
                                    </span>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        );
    };

    const renderOverview = () => (
        <div>
            {/* Top-level Refresh button */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
                <button
                    style={btnPrimary}
                    onClick={refreshOverview}
                    title="Refresh all data on this page"
                >
                    Refresh
                </button>
            </div>

            {/* Loading state for spend data */}
            {isLoadingAnyTeamSpend && (
                <div style={{ background: '#1e3a5f', border: '1px solid #1e40af', borderRadius: 8, padding: '0.75rem 1rem', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ display: 'inline-block', width: 14, height: 14, borderRadius: '50%', border: '2px solid #93c5fd', borderTopColor: 'transparent', animation: 'spin 0.8s linear infinite' }} />
                    <span style={{ color: '#93c5fd', fontSize: '0.85rem' }}>Loading team spend data...</span>
                </div>
            )}

            {/* Cost Prediction Widget */}
            <div style={{ marginBottom: '1.5rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <SectionHeading>Cost Prediction — {new Date().toLocaleString('default', { month: 'long', year: 'numeric' })}</SectionHeading>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.85rem', color: 'var(--fg-secondary)' }}>
                        <input
                            type="checkbox"
                            checked={includeHealthChecks}
                            onChange={(e) => setIncludeHealthChecks(e.target.checked)}
                            style={{ cursor: 'pointer' }}
                        />
                        Include health checks
                    </label>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(180px,1fr))', gap: '0.9rem' }}>
                    <MetricCard
                        label="Total Spend (MTD)"
                        value={fmt2(includeHealthChecks ? prediction.currentSpend + healthCheckCosts.spend : prediction.currentSpend)}
                        accent="var(--border)"
                        sub={`Day ${prediction.dayOfMonth} of ${prediction.daysInMonth}`}
                    />
                    <MetricCard
                        label="Predicted EOM"
                        value={fmt2(includeHealthChecks ? prediction.predictedEOM + (healthCheckCosts.spend / new Date().getDate()) * new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).getDate() : prediction.predictedEOM)}
                        accent="var(--border)"
                        sub="Linear projection"
                    />
                    <MetricCard
                        label="Daily Average"
                        value={fmt2(includeHealthChecks ? (prediction.currentSpend + healthCheckCosts.spend) / prediction.dayOfMonth : prediction.dailyAvg)}
                        accent="var(--border)"
                        sub={`Day ${prediction.dayOfMonth} of ${prediction.daysInMonth}`}
                    />
                    <MetricCard label="Days Remaining" value={String(prediction.daysRemaining)} accent="var(--border)" sub={`of ${prediction.daysInMonth}`} />
                    <MetricCard
                        label="Trend"
                        value={prediction.predictedEOM > prediction.currentSpend * 1.05 ? 'Increasing' : prediction.predictedEOM < prediction.currentSpend * 0.95 ? 'Decreasing' : 'Stable'}
                        accent="var(--border)"
                    />
                </div>
            </div>

            {/* Summary row */}
            <div style={{ marginBottom: '1.5rem' }}>
                <SectionHeading>Budget Overview</SectionHeading>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(160px,1fr))', gap: '0.9rem' }}>
                    <MetricCard label="Total Teams" value={totals.teams} accent="var(--border)" />
                    <MetricCard label="Total Users" value={totals.userCount} accent="var(--border)" />
                    <MetricCard
                        label="MTD Spend"
                        value={
                            modelFilter && modelFilteredTeamSpend
                                ? fmt2(Object.values(modelFilteredTeamSpend).reduce((s, v) => s + (v || 0), 0) + (includeHealthChecks ? healthCheckCosts.spend : 0))
                                : fmt2(totals.spend + (includeHealthChecks ? healthCheckCosts.spend : 0))
                        }
                        accent="var(--border)"
                        sub={modelFilter ? `Model: ${resolveModelName(modelFilter)}` : includeHealthChecks ? 'Users + health checks' : 'Month-to-date total'}
                    />
                    <MetricCard label="Total Budget" value={fmt2(totals.max_budget)} accent="var(--border)" />
                </div>
            </div>

            {/* System Operations (Health Checks) */}
            <div style={{ marginBottom: '1.5rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <SectionHeading>System Operations</SectionHeading>
                    <button
                        onClick={fetchHealthCheckCosts}
                        disabled={healthCheckCosts.loading}
                        style={{
                            padding: '0.4rem 0.8rem',
                            fontSize: '0.8rem',
                            background: healthCheckCosts.loading ? 'var(--bg)' : 'var(--border)',
                            color: healthCheckCosts.loading ? 'var(--muted)' : '#93c5fd',
                            border: '1px solid var(--muted)',
                            borderRadius: 6,
                            cursor: healthCheckCosts.loading ? 'not-allowed' : 'pointer',
                            transition: 'all 0.15s',
                        }}
                    >
                        {healthCheckCosts.loading ? 'Refreshing...' : 'Refresh'}
                    </button>
                </div>
                <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, padding: '1rem' }}>
                    {healthCheckCosts.loading ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <span style={{ display: 'inline-block', width: 14, height: 14, borderRadius: '50%', border: '2px solid var(--fg-tertiary)', borderTopColor: 'transparent', animation: 'spin 0.8s linear infinite' }} />
                            <span style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>Loading health check costs...</span>
                        </div>
                    ) : healthCheckCosts.error ? (
                        <div style={{ color: '#f59e0b', fontSize: '0.85rem' }}>{healthCheckCosts.error}</div>
                    ) : (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(200px,1fr))', gap: '0.9rem' }}>
                            <MetricCard
                                label="Health Check Spend"
                                value={fmt2(healthCheckCosts.spend)}
                                accent="var(--border)"
                                sub={`${new Date().toLocaleString('default', { month: 'short' })} (month to date)`}
                            />
                            <MetricCard
                                label="Health Check Requests"
                                value={healthCheckCosts.requests.toLocaleString()}
                                accent="var(--border)"
                                sub="This month"
                            />
                            <MetricCard
                                label="Est. Monthly Cost"
                                value={fmt2((healthCheckCosts.spend / new Date().getDate()) * new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).getDate())}
                                accent="var(--border)"
                                sub="Projected EOM"
                            />
                            <MetricCard
                                label="Total Projected Bill"
                                value={fmt2(prediction.predictedEOM + (healthCheckCosts.spend / new Date().getDate()) * new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).getDate())}
                                accent="var(--border)"
                                sub="Users + Health Checks"
                            />
                        </div>
                    )}
                </div>
            </div>

            {/* Filters */}
            <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'flex-end', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, padding: '0.65rem 1rem' }}>
                {/* View filters */}
                <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                    <div>
                        <div style={filterLabel}>Filter by team</div>
                        <select
                            style={{ ...filterInput, minWidth: 180 }}
                            value={teamSearch}
                            onChange={e => setTeamSearch(e.target.value)}
                        >
                            <option value="">All teams</option>
                            {visibleTeams.map(t => (
                                <option key={t.team_id} value={t.team_id}>{t.team_alias}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <div style={filterLabel}>Filter by model</div>
                        <select style={{ ...filterInput, minWidth: 160 }} value={modelFilter} onChange={e => setModelFilter(e.target.value)}>
                            <option value="">All models</option>
                            {allModelNames.length === 0
                                ? <option disabled>Loading model data…</option>
                                : allModelNames.map(m => <option key={m} value={m}>{resolveModelName(m)}</option>)
                            }
                        </select>
                    </div>
                    {(teamSearch || modelFilter) && (
                        <button style={btnGhost} onClick={() => { setTeamSearch(''); setModelFilter(''); }}>Clear filters</button>
                    )}
                </div>

                {/* Divider */}
                <div style={{ width: 1, background: 'var(--border)', alignSelf: 'stretch', flexShrink: 0 }} />

                {/* Date range — re-fetches data */}
                <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                    <div>
                        <div style={filterLabel}>Date range (re-fetches data)</div>
                        <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                            <input type="date" style={filterInput} value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
                            <span style={{ color: 'var(--border)', fontSize: '0.8rem' }}>to</span>
                            <input type="date" style={filterInput} value={dateTo} onChange={e => setDateTo(e.target.value)} />
                        </div>
                    </div>
                    {(dateFrom || dateTo) && (
                        <button style={{ ...btnPrimary, background: '#10b981' }} onClick={handleDateRangeApply}>Apply</button>
                    )}
                    {(dateFrom || dateTo) && (
                        <button style={btnGhost} onClick={() => { setDateFrom(''); setDateTo(''); }}>Clear</button>
                    )}
                </div>
            </div>

            {/* Teams table */}
            <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 60px 110px 110px minmax(180px,1fr) 44px', gap: 0, padding: '0.6rem 1rem', background: 'var(--bg)', borderBottom: '1px solid var(--border)' }}>
                    {['Team', 'Users', 'Period Spend', 'Budget', 'Used', ''].map((h, i) => (
                        <div key={i} style={{ fontSize: '0.73rem', fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</div>
                    ))}
                </div>
                {filteredTeams.length === 0 && (
                    <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--border)' }}>No teams match your filters.</div>
                )}
                {filteredTeams.map(team => {
                    const isExpanded = expandedTeamIds.has(team.team_id);
                    const cache = spendCache[team.team_id];
                    const modelSpend = modelFilteredTeamSpend ? modelFilteredTeamSpend[team.team_id] : null;
                    const cachedTeamSpend = getTeamSpendFromCache(team.team_id);
                    const baseSpend = cachedTeamSpend ?? team.spend ?? 0;
                    const displaySpend = modelFilter && modelSpend !== null ? modelSpend : baseSpend;
                    const actualSpend = cachedTeamSpend ?? team.spend ?? 0;
                    const pct = team.max_budget > 0 ? (actualSpend / team.max_budget) * 100 : 0;
                    const statusColor = team.blocked ? '#ef4444' : pct >= 90 ? '#f59e0b' : '#10b981';

                    return (
                        <div key={team.team_id} style={{ borderBottom: '1px solid var(--surface-alt)' }}>
                            <div
                                style={{ display: 'grid', gridTemplateColumns: '2fr 60px 110px 110px minmax(180px,1fr) 44px', gap: 0, padding: '0.75rem 1rem', alignItems: 'center', cursor: 'pointer', background: isExpanded ? 'var(--surface-alt)' : 'transparent', transition: 'background 0.15s' }}
                                onClick={() => handleExpandTeam(team)}
                                onMouseEnter={e => { if (!isExpanded) e.currentTarget.style.background = 'var(--surface-hover)'; }}
                                onMouseLeave={e => { if (!isExpanded) e.currentTarget.style.background = 'transparent'; }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    {/* Expand arrow on left */}
                                    <span style={{
                                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                        width: 20, height: 20, borderRadius: 4, flexShrink: 0,
                                        background: isExpanded ? '#6366f1' : 'var(--bg)',
                                        color: isExpanded ? '#fff' : 'var(--muted)',
                                        fontSize: '0.65rem', transition: 'all 0.15s',
                                        border: '1px solid', borderColor: isExpanded ? '#6366f1' : 'var(--border)',
                                    }}>
                                        {isExpanded ? '▲' : '▼'}
                                    </span>
                                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: statusColor, display: 'inline-block', flexShrink: 0 }} />
                                    <span style={{ fontWeight: 600, color: 'var(--fg-primary)', fontSize: '0.9rem' }}>{team.team_alias || team.team_id}</span>
                                    {/* Budget reset period badge */}
                                    <span style={{ fontSize: '0.68rem', background: '#1e3a5f', color: '#93c5fd', padding: '2px 6px', borderRadius: 4, border: '1px solid #1e40af' }} title={`Budget resets every ${fmtDuration(team.budget_duration)}`}>
                                        {fmtDuration(team.budget_duration)}
                                    </span>
                                    {team.blocked && <span style={{ fontSize: '0.68rem', background: '#7f1d1d', color: '#fca5a5', padding: '1px 6px', borderRadius: 4 }}>Blocked</span>}
                                </div>
                                <div style={cellStyle}>{team.userCount}</div>
                                <div style={{ ...cellStyle, color: 'var(--fg-primary)', fontWeight: 600 }}>
                                    {modelFilter
                                        ? (modelSpend === null
                                            ? <span style={{ color: 'var(--border)', fontSize: '0.78rem' }}>loading…</span>
                                            : <span>{fmt2(modelSpend)}<div style={{ fontSize: '0.7rem', color: 'var(--muted)', fontWeight: 400 }}>{resolveModelName(modelFilter)}</div></span>)
                                        : fmt2(team.spend)}
                                </div>
                                <div style={cellStyle}>{team.max_budget > 0 ? fmt2(team.max_budget) : '—'}</div>
                                <div style={{ fontSize: '0.85rem' }}>
                                    <BudgetBar spend={modelFilter && modelSpend !== null ? modelSpend : team.spend} budget={team.max_budget} />
                                </div>
                                {/* Last column empty — arrow is now on the left */}
                                <div />
                            </div>

                            {/* Expanded per-user detail */}
                            {isExpanded && (() => {
                                const { start: periodStart, periodEnd: calculatedPeriodEnd } = getBudgetPeriod(team.budget_duration, team.budget_reset_at);
                                const actualStart = dateFrom || periodStart;
                                const actualEnd = dateTo || calculatedPeriodEnd;
                                return (
                                    <div style={{ background: 'var(--bg)', padding: '0.75rem 1rem 1rem', borderTop: '1px solid var(--surface-alt)' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.6rem' }}>
                                            <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--fg-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                                Per-user breakdown — {team.team_alias || team.team_id}
                                            </div>
                                            <div style={{ fontSize: '0.7rem', color: 'var(--muted)', fontStyle: 'italic' }}>
                                                {fmtDateShort(actualStart)} – {fmtDateShort(actualEnd)}
                                            </div>
                                        </div>
                                        {cache?.loading && (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                                {[1, 2, 3].map(i => <Skeleton key={i} height={28} />)}
                                            </div>
                                        )}
                                        {!cache?.loading && !cache?.data && (
                                            <div style={{ color: 'var(--border)', fontSize: '0.85rem' }}>No spend data available.</div>
                                        )}
                                        {!cache?.loading && cache?.data && cache.data.length === 0 && (
                                            <div style={{ color: 'var(--border)', fontSize: '0.85rem' }}>No spend recorded this period.</div>
                                        )}
                                        {!cache?.loading && cache?.data && cache.data.length > 0 && (() => {
                                            // Filter by model
                                            const filteredUsers = cache.data.filter(u => {
                                                // Filter by model if selected
                                                if (modelFilter && !u.models?.[modelFilter]) return false;
                                                return true;
                                            });
                                            const teamTotalSpend = filteredUsers.reduce((sum, u) => sum + (u.spend || 0), 0);

                                            if (filteredUsers.length === 0) {
                                                return <div style={{ color: 'var(--border)', fontSize: '0.85rem' }}>No users match the current filters.</div>;
                                            }

                                            return (
                                                <>
                                                    {cache.source === 'keys' && (
                                                        <div style={{ fontSize: '0.78rem', color: 'var(--muted)', marginBottom: '0.5rem' }}>
                                                            Showing cumulative spend (period data unavailable — figures are all-time totals)
                                                        </div>
                                                    )}
                                                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 90px 60px 80px 70px 1.2fr', gap: 0, padding: '0.3rem 0.5rem', borderBottom: '1px solid var(--surface-alt)', marginBottom: '0.25rem' }}>
                                                        {['User', 'Spend', '% Team', 'Tokens', 'Requests', 'Top Model'].map(h => (
                                                            <div key={h} style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase' }}>{h}</div>
                                                        ))}
                                                    </div>
                                                    {filteredUsers.map((u, i) => {
                                                        const pct = teamTotalSpend > 0 ? (u.spend / teamTotalSpend) * 100 : 0;
                                                        // Find the most used model by requests
                                                        let topModel = '—';
                                                        if (u.models && Object.keys(u.models).length > 0) {
                                                            const modelEntries = Object.entries(u.models);
                                                            const sortedByRequests = modelEntries.sort((a, b) => (b[1].requests || 0) - (a[1].requests || 0));
                                                            topModel = resolveModelName(sortedByRequests[0][0]);
                                                        }
                                                        return (
                                                            <div key={u.email} style={{ display: 'grid', gridTemplateColumns: '2fr 90px 60px 80px 70px 1.2fr', gap: 0, padding: '0.35rem 0.5rem', background: i % 2 === 0 ? 'var(--bg)' : '#111827', borderRadius: 4 }}>
                                                                <div style={{ fontSize: '0.83rem', color: 'var(--fg-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={u.email}>{u.email}</div>
                                                                <div style={{ fontSize: '0.83rem', color: 'var(--fg-primary)', fontWeight: 600 }}>{fmt2(u.spend)}</div>
                                                                <div style={{ fontSize: '0.83rem', color: 'var(--fg-secondary)' }}>{Math.round(pct)}%</div>
                                                                <div style={{ fontSize: '0.83rem', color: 'var(--fg-secondary)' }}>{fmtTokens(u.tokens)}</div>
                                                                <div style={{ fontSize: '0.83rem', color: 'var(--fg-secondary)' }}>{u.requests}</div>
                                                                <div style={{ fontSize: '0.83rem', color: 'var(--fg-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={topModel}>{topModel}</div>
                                                            </div>
                                                        );
                                                    })}
                                                </>
                                            );
                                        })()}
                                    </div>
                                );
                            })()}
                        </div>
                    );
                })}
            </div>
        </div>
    );

    // ── Shared Activity Table Component ──────────────────────────────────────────
    const renderActivityTable = () => (
        <div style={chartCard}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '1rem' }}>
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
                    <div style={chartTitle}>Activity Records</div>
                    <select
                        style={{ ...filterInput, minWidth: '180px', padding: '0.4rem 0.8rem', fontSize: '0.82rem' }}
                        value={activityTeamFilter}
                        onChange={(e) => {
                            setActivityTeamFilter(e.target.value);
                            setActivityPage(1);
                        }}
                    >
                        <option value="all">All Teams ({visibleTeams.length})</option>
                        {visibleTeams.map(team => (
                            <option key={team.team_id} value={team.team_id}>
                                {team.team_alias || team.team_id}
                            </option>
                        ))}
                    </select>
                    {selectedModels.length > 0 && selectedModels.map((model) => (
                        <div key={model} style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            padding: '0.4rem 0.8rem',
                            background: '#1e3a8a',
                            borderRadius: 6,
                            fontSize: '0.82rem',
                            color: 'var(--fg-secondary)'
                        }}>
                            <span>Model: {resolveModelName(model)}</span>
                            <button
                                onClick={() => {
                                    toggleModelSelection(model);
                                }}
                                style={{
                                    background: 'transparent',
                                    border: 'none',
                                    color: '#93c5fd',
                                    cursor: 'pointer',
                                    padding: 0,
                                    fontSize: '1rem',
                                    lineHeight: 1
                                }}
                                title="Remove model filter"
                            >
                                ×
                            </button>
                        </div>
                    ))}
                    {activityDateFilter !== 'all' && (
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            padding: '0.4rem 0.8rem',
                            background: '#166534',
                            borderRadius: 6,
                            fontSize: '0.82rem',
                            color: 'var(--fg-secondary)'
                        }}>
                            <span>Date: {activityDateFilter}</span>
                            <button
                                onClick={() => {
                                    setActivityDateFilter('all');
                                    setActivityPage(1);
                                }}
                                style={{
                                    background: 'transparent',
                                    border: 'none',
                                    color: '#86efac',
                                    cursor: 'pointer',
                                    padding: 0,
                                    fontSize: '1rem',
                                    lineHeight: 1
                                }}
                                title="Clear date filter"
                            >
                                ×
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {activityLoading && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {[1, 2, 3, 4, 5].map(i => <Skeleton key={i} height={32} />)}
                </div>
            )}
            {activityError && <div style={{ color: '#f87171', fontSize: '0.85rem' }}>{activityError}</div>}

            {!activityLoading && activityLogs.length > 0 && (
                <>
                    <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr 1.5fr 70px 90px 80px', padding: '0.5rem 1rem', background: 'var(--bg)', borderBottom: '1px solid var(--border)' }}>
                            {['Date / Time', 'User', 'Model', 'Auto-Router', 'Cost', 'Tokens'].map(h => (
                                <div key={h} style={{ fontSize: '0.73rem', fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase' }}>{h}</div>
                            ))}
                        </div>
                        <div style={{ maxHeight: '55vh', overflowY: 'auto' }}>
                            {activityLogs
                                .filter(r => {
                                    // Filter by selected models (if any) - client-side for real-time UI filtering
                                    if (selectedModels.length > 0 && !selectedModels.includes(r.model)) return false;
                                    // Date filtering is handled by backend via start_date/end_date parameters
                                    // No need to filter again here - causes double-filtering bug
                                    return true;
                                })
                                .map((r, i) => {
                                    const isBlocked = r.model === 'unknown' && r.spend === 0 && r.totalTokens === 0;
                                    const blockedTeam = isBlocked ? (r.teamAlias || getTeamName(r.teamId)) : null;
                                    return (
                                    <div key={r.id || i} style={{ display: 'grid', gridTemplateColumns: '160px 1fr 1.5fr 70px 90px 80px', padding: '0.45rem 1rem', background: isBlocked ? 'var(--critical-bg)' : i % 2 === 0 ? 'var(--bg)' : 'transparent', alignItems: 'center', opacity: isBlocked ? 0.75 : 1 }}>
                                        <div style={{ fontSize: '0.78rem', color: 'var(--muted)' }}>{fmtDate(r.startTime || r.endTime)}</div>
                                        <div style={{ fontSize: '0.82rem', color: 'var(--fg-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.endUser || '—'}</div>
                                        <div style={{ fontSize: '0.78rem', color: 'var(--fg-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                                            {isBlocked ? (
                                                <span style={{ fontSize: '0.65rem', fontWeight: 700, background: 'var(--critical-bg-hover)', color: 'var(--critical)', border: '1px solid rgba(229,72,77,0.35)', borderRadius: 4, padding: '1px 5px', flexShrink: 0 }}>
                                                    Blocked{blockedTeam && blockedTeam !== '—' ? ` · ${blockedTeam}` : ''}
                                                </span>
                                            ) : r.isMCP ? (
                                                <span style={{ fontSize: '0.65rem', fontWeight: 700, background: 'rgba(245,158,11,0.15)', color: '#f59e0b', border: '1px solid rgba(245,158,11,0.4)', borderRadius: 4, padding: '1px 5px', flexShrink: 0 }}>MCP</span>
                                            ) : null}
                                            {!isBlocked && (
                                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {r.isMCP ? (r.mcpOperation || r.apiKeyAlias || resolveModelName(r.model) || '—') : (resolveModelName(r.model) || '—')}
                                                </span>
                                            )}
                                        </div>
                                        <div style={{ fontSize: '0.78rem' }}>
                                            {r.autoRouted ? (
                                                <span style={{ fontSize: '0.65rem', fontWeight: 700, background: 'var(--accent-bg)', color: '#818cf8', border: '1px solid rgba(99,102,241,0.35)', borderRadius: 4, padding: '1px 5px' }}>
                                                    {r.routerTier || 'Yes'}
                                                </span>
                                            ) : (
                                                <span style={{ fontSize: '0.78rem', color: 'var(--muted)' }}>—</span>
                                            )}
                                        </div>
                                        <div style={{ fontSize: '0.82rem', color: isBlocked ? 'var(--muted)' : 'var(--fg-primary)', fontWeight: 600 }}>{isBlocked ? '—' : fmt2(r.spend)}</div>
                                        <div style={{ fontSize: '0.82rem', color: 'var(--fg-secondary)' }}>{isBlocked ? '—' : fmtTokens(r.totalTokens)}</div>
                                    </div>
                                    );
                                })}
                        </div>
                    </div>
                    {/* Pagination removed - backend returns all filtered records, table is scrollable */}
                </>
            )}
            {!activityLoading && activityLogs.length === 0 && !activityError && (
                <div style={{ textAlign: 'center', color: 'var(--border)', padding: '3rem' }}>
                    <div style={{ marginBottom: '1rem' }}>
                        {selectedModels.length > 0 || activityDateFilter !== 'all'
                            ? 'No records match the current filters. Try clearing filters or expanding the date range.'
                            : 'No activity records found for the selected date range.'}
                    </div>
                    {(selectedModels.length > 0 || activityDateFilter !== 'all') && (
                        <button
                            style={btnPrimary}
                            onClick={() => {
                                toggleModelSelection('all');
                                setActivityDateFilter('all');
                                setActivityPage(1);
                            }}
                        >
                            Clear All Filters
                        </button>
                    )}
                </div>
            )}
        </div>
    );

    const renderAnalytics = () => {
        const hasData = visibleTeams.some(t => spendCache[t.team_id] && !spendCache[t.team_id].loading);
        const allLoading = visibleTeams.some(t => spendCache[t.team_id]?.loading);

        const setActivityPresetRange = (preset) => {
            const today = new Date();
            let startDate;

            switch (preset) {
                case 'today':
                    startDate = today;
                    break;
                case 'last_7_days':
                    startDate = new Date(today);
                    startDate.setDate(startDate.getDate() - 7);
                    break;
                case 'last_30_days':
                    startDate = new Date(today);
                    startDate.setDate(startDate.getDate() - 30);
                    break;
                case 'current_month':
                    startDate = new Date(today.getFullYear(), today.getMonth(), 1);
                    break;
                default:
                    return;
            }

            setActivityDateFrom(startDate.toISOString().split('T')[0]);
            setActivityDateTo(today.toISOString().split('T')[0]);
        };

        const subTabButton = (key, label) => (
            <button
                key={key}
                onClick={() => { setAnalyticsSubTab(key); if (key !== 'users') setDeptDrilldown(null); }}
                style={{
                    padding: '0.5rem 1.25rem',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    border: 'none',
                    borderRadius: '6px 6px 0 0',
                    cursor: 'pointer',
                    background: analyticsSubTab === key ? '#6366f1' : 'var(--bg)',
                    color: analyticsSubTab === key ? '#fff' : 'var(--fg-secondary)',
                    transition: 'all 0.2s',
                    borderBottom: analyticsSubTab === key ? '2px solid #6366f1' : '2px solid transparent',
                    marginBottom: '-2px'
                }}
            >
                {label}
            </button>
        );

        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                {/* Date Range Selector */}
                {(analyticsSubTab === 'activity' || analyticsSubTab === 'models' || analyticsSubTab === 'keys' || analyticsSubTab === 'my-usage' || analyticsSubTab === 'my-team' || analyticsSubTab === 'users' || analyticsSubTab === 'teams' || analyticsSubTab === 'departments' || analyticsSubTab === 'organisations' || analyticsSubTab === 'overview') && (
                <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, padding: '0.75rem 1rem', display: 'flex', gap: '1rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                    <div style={{ flex: '0 0 auto' }}>
                        <label style={{ ...filterLabel, marginBottom: '0.5rem', display: 'block' }}>Date Range</label>
                        <select
                            style={{ ...filterInput, minWidth: '180px' }}
                            value={analyticsTimeRange}
                            onChange={(e) => setAnalyticsTimeRange(e.target.value)}
                        >
                            <option value="last_7_days">Last 7 Days</option>
                            <option value="last_30_days">Last 30 Days</option>
                            <option value="current_month">Current Month</option>
                            <option value="all_time">All Time</option>
                            <option value="custom">Custom Range</option>
                        </select>
                    </div>

                    {analyticsTimeRange === 'custom' && (
                        <>
                            <div style={{ flex: '0 0 auto' }}>
                                <label style={{ ...filterLabel, marginBottom: '0.5rem', display: 'block' }}>From</label>
                                <input
                                    type="date"
                                    style={filterInput}
                                    value={analyticsDateFrom}
                                    onChange={(e) => setAnalyticsDateFrom(e.target.value)}
                                />
                            </div>
                            <div style={{ flex: '0 0 auto' }}>
                                <label style={{ ...filterLabel, marginBottom: '0.5rem', display: 'block' }}>To</label>
                                <input
                                    type="date"
                                    style={filterInput}
                                    value={analyticsDateTo}
                                    onChange={(e) => setAnalyticsDateTo(e.target.value)}
                                />
                            </div>
                        </>
                    )}
                </div>
                )}

                {analyticsSubTab === 'activity' && allLoading && (
                    <div style={{ color: 'var(--fg-secondary)', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: '50%', border: '2px solid #6366f1', borderTopColor: 'transparent', animation: 'spin 0.8s linear infinite' }} />
                        Loading spend data…
                    </div>
                )}

                {/* ACTIVITY SUB-TAB */}
                {analyticsSubTab === 'activity' && isAdmin && (
                <>
                {/* Usage over time */}
                <div style={chartCard}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                        <div style={chartTitle}>Usage Over Time</div>
                        {aggregatedActivity.loading && (
                            <div style={{ color: 'var(--fg-secondary)', fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', border: '2px solid #6366f1', borderTopColor: 'transparent', animation: 'spin 0.8s linear infinite' }} />
                                Loading daily data...
                            </div>
                        )}
                        {aggregatedActivity.error && (
                            <div style={{ color: '#f87171', fontSize: '0.75rem' }}>
                                {aggregatedActivity.error}
                            </div>
                        )}
                    </div>
                    {(!aggregatedActivity.hasLoadedOnce && aggregatedActivity.loading)
                        ? <Skeleton height={240} />
                        : lineChartData.length === 0
                            ? <div style={{ color: 'var(--border)', fontSize: '0.85rem', textAlign: 'center', paddingTop: 80 }}>No daily data available for selected range</div>
                            : (
                                <ResponsiveContainer width="100%" height={240}>
                                    <LineChart
                                        data={lineChartData}
                                        margin={{ top: 5, right: 20, bottom: 5, left: 0 }}
                                        onClick={(data) => {
                                            if (data && data.activePayload && data.activePayload[0]) {
                                                const clickedDate = data.activePayload[0].payload.fullDate;
                                                if (clickedDate) {
                                                    setActivityDateFilter(activityDateFilter === clickedDate ? 'all' : clickedDate);
                                                    setActivityPage(1);
                                                }
                                            }
                                        }}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" stroke="var(--bg)" />
                                        <XAxis dataKey="date" tick={{ fontSize: 11, fill: 'var(--muted)' }} />
                                        <YAxis yAxisId="cost" tick={{ fontSize: 11, fill: 'var(--muted)' }} tickFormatter={fmt2} width={55} />
                                        <YAxis yAxisId="req" orientation="right" tick={{ fontSize: 11, fill: 'var(--muted)' }} tickFormatter={v => Math.round(v)} width={40} />
                                        <ReTooltip
                                            contentStyle={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12, color: 'var(--fg-primary)' }}
                                            labelStyle={{ color: 'var(--fg-secondary)' }}
                                            itemStyle={{ color: 'var(--fg-primary)' }}
                                            formatter={(v, name) => {
                                                if (name === 'Cost') return [fmt2(v), 'Cost'];
                                                if (name === 'Requests') return [Math.round(v), 'Successful Requests'];
                                                if (name === 'Failed') return [Math.round(v), 'Failed Requests'];
                                                return [Math.round(v), name];
                                            }}
                                            labelFormatter={(label, payload) => {
                                                const fullDate = payload?.[0]?.payload?.fullDate;
                                                return fullDate || label;
                                            }}
                                        />
                                        <Legend wrapperStyle={{ fontSize: 12, color: 'var(--fg-secondary)' }} />
                                        <Line
                                            yAxisId="cost"
                                            type="monotone"
                                            dataKey="cost"
                                            stroke="#6366f1"
                                            strokeWidth={2}
                                            dot={(props) => {
                                                const isSelected = props.payload.fullDate === activityDateFilter;
                                                return (
                                                    <circle
                                                        cx={props.cx}
                                                        cy={props.cy}
                                                        r={isSelected ? 6 : 3}
                                                        fill={isSelected ? '#60a5fa' : '#6366f1'}
                                                        stroke={isSelected ? '#1e3a8a' : 'none'}
                                                        strokeWidth={isSelected ? 2 : 0}
                                                    />
                                                );
                                            }}
                                            name="Cost"
                                            isAnimationActive={false}
                                        />
                                        <Line isAnimationActive={false}yAxisId="req" type="monotone" dataKey="requests" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} name="Requests" isAnimationActive={false} />
                                        <Line isAnimationActive={false}yAxisId="req" type="monotone" dataKey="failedRequests" stroke="#ef4444" strokeWidth={2} dot={{ r: 3 }} name="Failed" strokeDasharray="4 2" isAnimationActive={false} />
                                    </LineChart>
                                </ResponsiveContainer>
                            )}
                </div>

                {/* Cost Forecast */}
                <div style={chartCard}>
                        <div style={chartTitle}>Cost Forecast (7 Days)</div>
                        {aggregatedActivity.loading
                            ? <Skeleton height={220} />
                            : lineChartData.length < 3
                                ? <div style={{ color: 'var(--border)', fontSize: '0.85rem', textAlign: 'center', paddingTop: 80 }}>
                                    Need at least 3 days of data for cost forecasting
                                  </div>
                                : (
                                <ResponsiveContainer width="100%" height={220}>
                                    <LineChart data={[...forecastData.actual, ...forecastData.forecast]} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="var(--bg)" />
                                        <XAxis dataKey="date" tick={{ fontSize: 10, fill: 'var(--muted)' }} />
                                        <YAxis tick={{ fontSize: 11, fill: 'var(--muted)' }} tickFormatter={fmt2} width={55} />
                                        <ReTooltip
                                            contentStyle={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12, color: 'var(--fg-primary)' }}
                                            labelStyle={{ color: 'var(--fg-secondary)' }}
                                            itemStyle={{ color: 'var(--fg-primary)' }}
                                            formatter={(v, name) => [v !== null ? fmt2(v) : 'N/A', name]}
                                        />
                                        <Legend wrapperStyle={{ fontSize: 12, color: 'var(--fg-secondary)' }} />
                                        <Line
                                            type="monotone"
                                            dataKey="actualCost"
                                            stroke="#10b981"
                                            strokeWidth={2}
                                            dot={{ r: 3, fill: '#10b981', stroke: 'var(--bg)', strokeWidth: 1 }}
                                            name="Actual Cost"
                                            connectNulls={false}
                                        />
                                        <Line
                                            type="monotone"
                                            dataKey="forecastCost"
                                            stroke="#f59e0b"
                                            strokeWidth={2}
                                            strokeDasharray="5 5"
                                            dot={{ r: 3, fill: '#f59e0b', stroke: 'var(--bg)', strokeWidth: 1 }}
                                            name="Forecast"
                                            connectNulls={false}
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            )}
                </div>
                </>
                )}

                {/* MODELS SUB-TAB */}
                {analyticsSubTab === 'models' && isAdmin && (
                <>
                {/* Model-focused charts */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                    {/* Model Distribution */}
                    <div style={chartCard}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                            <div style={chartTitle}>Model Distribution</div>
                            <div style={{ display: 'flex', gap: 4 }}>
                                {[['spend', 'Cost'], ['requests', 'Reqs'], ['tokens', 'Tokens']].map(([k, lbl]) => (
                                    <button key={k} onClick={() => setPieMetric(k)} style={{ fontSize: '0.72rem', padding: '2px 8px', borderRadius: 4, border: 'none', cursor: 'pointer', background: pieMetric === k ? '#6366f1' : 'var(--bg)', color: pieMetric === k ? '#fff' : 'var(--fg-secondary)' }}>{lbl}</button>
                                ))}
                            </div>
                        </div>
                        {!hasData
                            ? <Skeleton height={320} />
                            : pieData.length === 0
                                ? <div style={{ color: 'var(--border)', fontSize: '0.85rem', textAlign: 'center', paddingTop: 60 }}>No model data yet.</div>
                                : (
                                    <div style={{ display: 'flex', gap: '1rem' }}>
                                        <ResponsiveContainer width="50%" height={300}>
                                            <PieChart>
                                                <Pie
                                                    data={pieData}
                                                    cx="50%"
                                                    cy="50%"
                                                    outerRadius={85}
                                                    dataKey="value"
                                                    nameKey="name"
                                                    label={false}
                                                    labelLine={false}
                                                    isAnimationActive={false}
                                                    onClick={(data) => {
                                                        if (data && data.name) {
                                                            toggleModelSelection(data.name);
                                                        }
                                                    }}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    {pieData.map((entry, i) => (
                                                        <Cell
                                                            key={i}
                                                            fill={isModelSelected(entry.name) ? '#1e3a8a' : CHART_COLORS[i % CHART_COLORS.length]}
                                                            stroke={isModelSelected(entry.name) ? '#60a5fa' : 'none'}
                                                            strokeWidth={isModelSelected(entry.name) ? 3 : 0}
                                                        />
                                                    ))}
                                                </Pie>
                                                <ReTooltip
                                                    contentStyle={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 11, color: 'var(--fg-primary)' }}
                                                    labelStyle={{ color: 'var(--fg-secondary)' }}
                                                    itemStyle={{ color: 'var(--fg-primary)' }}
                                                    formatter={(v, name) => [pieMetric === 'spend' ? fmt2(v) : pieMetric === 'tokens' ? fmtTokens(v) : v.toLocaleString(), resolveModelName(name)]}
                                                />
                                            </PieChart>
                                        </ResponsiveContainer>
                                        <div style={{ flex: 1, maxHeight: 300, overflowY: 'auto', fontSize: '0.78rem' }}>
                                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                                <thead style={{ position: 'sticky', top: 0, background: 'var(--bg)', zIndex: 1 }}>
                                                    <tr style={{ borderBottom: '1px solid var(--border)' }}>
                                                        <th style={{ textAlign: 'left', padding: '0.35rem 0.5rem', color: 'var(--muted)', fontWeight: 600, fontSize: '0.7rem' }}>Model</th>
                                                        <th style={{ textAlign: 'right', padding: '0.35rem 0.5rem', color: 'var(--muted)', fontWeight: 600, fontSize: '0.7rem' }}>
                                                            {pieMetric === 'spend' ? 'Cost' : pieMetric === 'tokens' ? 'Tokens' : 'Reqs'}
                                                        </th>
                                                        <th style={{ textAlign: 'right', padding: '0.35rem 0.5rem', color: 'var(--muted)', fontWeight: 600, fontSize: '0.7rem' }}>%</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {pieData.map((item, i) => {
                                                        const total = pieData.reduce((sum, d) => sum + d.value, 0);
                                                        const pct = total > 0 ? ((item.value / total) * 100).toFixed(1) : 0;
                                                        const isSelected = isModelSelected(item.name);
                                                        return (
                                                            <tr
                                                                key={i}
                                                                onClick={() => {
                                                                    toggleModelSelection(item.name);
                                                                }}
                                                                style={{
                                                                    borderBottom: '1px solid var(--border)',
                                                                    background: isSelected ? '#1e3a8a' : 'transparent',
                                                                    cursor: 'pointer',
                                                                    transition: 'background 0.2s'
                                                                }}
                                                                onMouseEnter={(e) => {
                                                                    if (!isSelected) {
                                                                        e.currentTarget.style.background = 'var(--bg)';
                                                                    }
                                                                }}
                                                                onMouseLeave={(e) => {
                                                                    if (!isSelected) {
                                                                        e.currentTarget.style.background = 'transparent';
                                                                    }
                                                                }}
                                                            >
                                                                <td style={{ padding: '0.35rem 0.5rem', display: 'flex', alignItems: 'center', gap: 6 }}>
                                                                    <span style={{ width: 10, height: 10, borderRadius: 2, background: CHART_COLORS[i % CHART_COLORS.length], flexShrink: 0 }}></span>
                                                                    <span style={{ color: 'var(--fg-secondary)', fontSize: '0.75rem' }} title={item.name}>{resolveModelName(item.name)}</span>
                                                                    {isSelected && <span style={{ marginLeft: 'auto', color: '#60a5fa', fontSize: '0.7rem' }}>✓</span>}
                                                                </td>
                                                                <td style={{ padding: '0.35rem 0.5rem', color: 'var(--fg-primary)', textAlign: 'right', fontWeight: 600, fontSize: '0.75rem' }}>
                                                                    {pieMetric === 'spend' ? fmt2(item.value) : pieMetric === 'tokens' ? fmtTokens(item.value) : item.value.toLocaleString()}
                                                                </td>
                                                                <td style={{ padding: '0.35rem 0.5rem', color: 'var(--fg-secondary)', textAlign: 'right', fontSize: '0.75rem' }}>{pct}%</td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}
                    </div>

                    {/* Model Performance */}
                    <div style={chartCard}>
                        <div style={chartTitle}>Model Performance</div>
                        {aggregatedActivity.loading
                            ? <div style={{ color: 'var(--fg-secondary)', fontSize: '0.85rem', textAlign: 'center', paddingTop: 60 }}>
                                <div style={{ display: 'inline-block', width: 20, height: 20, border: '2px solid #6366f1', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                                <div style={{ marginTop: '0.5rem' }}>Loading data...</div>
                              </div>
                            : modelPerformance.length === 0
                            ? <div style={{ color: 'var(--border)', fontSize: '0.85rem', textAlign: 'center', paddingTop: 60 }}>Not enough data (minimum 5 requests per model required)</div>
                            : (
                                <div style={{ overflow: 'auto', maxHeight: 300 }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                                        <thead>
                                            <tr style={{ borderBottom: '1px solid var(--border)' }}>
                                                {['Model', 'Avg Latency', 'Cost/1K', 'Success'].map(h => (
                                                    <th key={h} style={{ textAlign: 'left', padding: '0.35rem 0.5rem', color: 'var(--muted)', fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase' }}>{h}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {modelPerformance.map((m, i) => {
                                                const isSelected = isModelSelected(m.model);
                                                return (
                                                    <tr
                                                        key={m.model}
                                                        onClick={() => {
                                                            toggleModelSelection(m.model);
                                                            setActivityPage(1);
                                                        }}
                                                        style={{
                                                            background: isSelected ? '#1e3a8a' : (i % 2 === 0 ? 'var(--bg)' : 'transparent'),
                                                            cursor: 'pointer',
                                                            transition: 'background 0.2s',
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            if (!isSelected) {
                                                                e.currentTarget.style.background = 'var(--bg)';
                                                            }
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            if (!isSelected) {
                                                                e.currentTarget.style.background = i % 2 === 0 ? 'var(--bg)' : 'transparent';
                                                            }
                                                        }}
                                                    >
                                                        <td style={{ padding: '0.35rem 0.5rem', color: 'var(--fg-secondary)' }}>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                                <div style={{ width: 8, height: 8, borderRadius: '50%', background: m.color }} />
                                                                {m.displayName}
                                                                {isSelected && <span style={{ marginLeft: 'auto', color: '#60a5fa', fontSize: '0.7rem' }}>✓</span>}
                                                            </div>
                                                        </td>
                                                        <td style={{ padding: '0.35rem 0.5rem', color: 'var(--fg-secondary)' }}>{m.avgLatency}ms</td>
                                                        <td style={{ padding: '0.35rem 0.5rem', color: 'var(--fg-primary)', fontWeight: 600 }}>{fmt2(m.costPer1K)}</td>
                                                        <td style={{ padding: '0.35rem 0.5rem', color: m.successRate >= 95 ? '#10b981' : m.successRate >= 80 ? '#f59e0b' : '#ef4444' }}>{m.successRate.toFixed(1)}%</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                    </div>
                </div>
                </>
                )}

                {/* Recent Activity Section */}
                {analyticsSubTab === 'activity' && isAdmin && (
                <>
                <div style={chartCard}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                            <div style={chartTitle}>Recent Activity</div>
                            <select
                                style={{ ...filterInput, minWidth: '180px', padding: '0.4rem 0.8rem', fontSize: '0.82rem' }}
                                value={activityTeamFilter}
                                onChange={(e) => {
                                    setActivityTeamFilter(e.target.value);
                                    setActivityPage(1); // Reset to page 1 when filter changes
                                }}
                            >
                                <option value="all">All Teams ({visibleTeams.length})</option>
                                {visibleTeams.map(team => (
                                    <option key={team.team_id} value={team.team_id}>
                                        {team.team_alias || team.team_id}
                                    </option>
                                ))}
                            </select>
                            {selectedModels.length > 0 && selectedModels.map((model) => (
                                <div key={model} style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    padding: '0.4rem 0.8rem',
                                    background: '#1e3a8a',
                                    borderRadius: 6,
                                    fontSize: '0.82rem',
                                    color: 'var(--fg-secondary)'
                                }}>
                                    <span>Model: {resolveModelName(model)}</span>
                                    <button
                                        onClick={() => {
                                            toggleModelSelection(model);
                                        }}
                                        style={{
                                            background: 'transparent',
                                            border: 'none',
                                            color: '#93c5fd',
                                            cursor: 'pointer',
                                            padding: 0,
                                            fontSize: '1rem',
                                            lineHeight: 1
                                        }}
                                        title="Clear model filter"
                                    >
                                        ×
                                    </button>
                                </div>
                            ))}
                            {activityDateFilter !== 'all' && (
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    padding: '0.4rem 0.8rem',
                                    background: '#166534',
                                    borderRadius: 6,
                                    fontSize: '0.82rem',
                                    color: 'var(--fg-secondary)'
                                }}>
                                    <span>Date: {activityDateFilter}</span>
                                    <button
                                        onClick={() => {
                                            setActivityDateFilter('all');
                                            setActivityPage(1);
                                        }}
                                        style={{
                                            background: 'transparent',
                                            border: 'none',
                                            color: '#86efac',
                                            cursor: 'pointer',
                                            padding: 0,
                                            fontSize: '1rem',
                                            lineHeight: 1
                                        }}
                                        title="Clear date filter"
                                    >
                                        ×
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Summary Metrics */}
                    {!activityLoading && activityLogs.length > 0 && (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
                            <div style={{
                                background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
                                border: '1px solid #3b82f6',
                                borderRadius: 8,
                                padding: '1rem',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '0.5rem'
                            }}>
                                <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#93c5fd', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                    Total Requests
                                </div>
                                <div style={{ fontSize: '1.75rem', fontWeight: 700, color: '#fff' }}>
                                    {activitySummary ? activitySummary.total_requests : activityLogs.filter(r => selectedModels.length === 0 || selectedModels.includes(r.model)).length}
                                </div>
                                <div style={{ fontSize: '0.65rem', color: '#93c5fd', opacity: 0.7, marginTop: 'auto' }}>
                                    {activityDateFilter !== 'all' ? activityDateFilter : `${analyticsDateRange.start} to ${analyticsDateRange.end}`}
                                </div>
                            </div>

                            <div style={{
                                background: 'linear-gradient(135deg, #166534 0%, #10b981 100%)',
                                border: '1px solid #10b981',
                                borderRadius: 8,
                                padding: '1rem',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '0.5rem'
                            }}>
                                <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#6ee7b7', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                    Total Unique Users
                                </div>
                                <div style={{ fontSize: '1.75rem', fontWeight: 700, color: '#fff' }}>
                                    {activitySummary ? activitySummary.unique_users : uniqueUsersTotal}
                                </div>
                                <div style={{ fontSize: '0.65rem', color: '#6ee7b7', opacity: 0.7, marginTop: 'auto' }}>
                                    Across entire period
                                </div>
                            </div>

                            <div style={{
                                background: 'linear-gradient(135deg, #15803d 0%, #22c55e 100%)',
                                border: '1px solid #22c55e',
                                borderRadius: 8,
                                padding: '1rem',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '0.5rem'
                            }}>
                                <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#86efac', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                    Peak Daily Users
                                </div>
                                <div style={{ fontSize: '1.75rem', fontWeight: 700, color: '#fff' }}>
                                    {activitySummary ? activitySummary.peak_daily_users : peakDailyUsers}
                                </div>
                                <div style={{ fontSize: '0.65rem', color: '#86efac', opacity: 0.7, marginTop: 'auto' }}>
                                    Max on any single day
                                </div>
                            </div>

                            <div style={{
                                background: 'linear-gradient(135deg, #581c87 0%, #a855f7 100%)',
                                border: '1px solid #a855f7',
                                borderRadius: 8,
                                padding: '1rem',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '0.5rem'
                            }}>
                                <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#d8b4fe', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                    Total Cost
                                </div>
                                <div style={{ fontSize: '1.75rem', fontWeight: 700, color: '#fff' }}>
                                    {activitySummary ? fmt2(activitySummary.total_cost) : fmt2(activityLogs.filter(r => selectedModels.length === 0 || selectedModels.includes(r.model)).reduce((sum, r) => sum + (r.spend || 0), 0))}
                                </div>
                                <div style={{ fontSize: '0.65rem', color: '#d8b4fe', opacity: 0.7, marginTop: 'auto' }}>
                                    {activityDateFilter !== 'all' ? activityDateFilter : `${analyticsDateRange.start} to ${analyticsDateRange.end}`}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Usage Heatmap */}
                    <div style={chartCard}>
                            <div style={chartTitle}>Usage Heatmap</div>
                            {aggregatedActivity.loading
                                ? <div style={{ color: 'var(--fg-secondary)', fontSize: '0.85rem', textAlign: 'center', paddingTop: 60 }}>
                                    <div style={{ display: 'inline-block', width: 20, height: 20, border: '2px solid #6366f1', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                                    <div style={{ marginTop: '0.5rem' }}>Loading data...</div>
                                  </div>
                                : heatmapData.length === 0
                                ? <div style={{ color: 'var(--border)', fontSize: '0.85rem', textAlign: 'center', paddingTop: 60 }}>No activity data for the selected period</div>
                                : (() => {
                                    // Daily heatmap (calendar view)
                                    // Filter to only show dates within the selected analytics date range
                                    const { start: rangeStart, end: rangeEnd } = analyticsDateRange;
                                    const filteredHeatmapData = heatmapData.filter(d => {
                                        return d.date >= rangeStart && d.date <= rangeEnd;
                                    });

                                    const maxRequests = Math.max(...filteredHeatmapData.map(d => d.requests), 1);
                                    const getHeatColor = (requests) => {
                                        const intensity = requests / maxRequests;
                                        if (intensity === 0) return 'var(--bg)';
                                        if (intensity < 0.25) return '#3b82f6';
                                        if (intensity < 0.5) return '#8b5cf6';
                                        if (intensity < 0.75) return '#ec4899';
                                        return '#f43f5e';
                                    };
                                    const dayLabels = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

                                    // Group by week for calendar display
                                    const sortedData = [...filteredHeatmapData].sort((a, b) => a.date.localeCompare(b.date));

                                    // Calculate empty cells needed before the first date to align with correct day of week
                                    const firstDate = sortedData.length > 0 ? new Date(sortedData[0].date) : null;
                                    const startDayOfWeek = firstDate ? firstDate.getDay() : 0; // 0 = Sunday, 6 = Saturday

                                    // Build complete grid with empty cells before and after
                                    const allCells = [...Array(startDayOfWeek).fill(null), ...sortedData];

                                    // Group into weeks
                                    const weeks = [];
                                    for (let i = 0; i < allCells.length; i += 7) {
                                        weeks.push(allCells.slice(i, i + 7));
                                    }

                                    return (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                            {weeks.map((week, weekIndex) => {
                                                // Check if this week starts a new month
                                                const firstCellWithData = week.find(cell => cell !== null);
                                                const isFirstWeek = weekIndex === 0;
                                                let showMonthLabel = false;
                                                let monthLabel = '';

                                                if (firstCellWithData) {
                                                    const currentDate = new Date(firstCellWithData.date);
                                                    const currentMonth = currentDate.getMonth();

                                                    // Show month label on first week or when month changes
                                                    if (isFirstWeek) {
                                                        showMonthLabel = true;
                                                        monthLabel = currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
                                                    } else {
                                                        // Check if previous week had a different month
                                                        const prevWeek = weeks[weekIndex - 1];
                                                        const prevCellWithData = prevWeek.find(cell => cell !== null);
                                                        if (prevCellWithData) {
                                                            const prevMonth = new Date(prevCellWithData.date).getMonth();
                                                            if (currentMonth !== prevMonth) {
                                                                showMonthLabel = true;
                                                                monthLabel = currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
                                                            }
                                                        }
                                                    }
                                                }

                                                return (
                                                    <div key={weekIndex} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                                        {/* Month label when month changes */}
                                                        {showMonthLabel && (
                                                            <div style={{
                                                                fontSize: '0.8rem',
                                                                fontWeight: 600,
                                                                color: 'var(--fg-primary)',
                                                                marginTop: weekIndex > 0 ? '0.5rem' : 0,
                                                                marginBottom: '0.25rem'
                                                            }}>
                                                                {monthLabel}
                                                            </div>
                                                        )}
                                                        {/* Day headers with day names and numbers for this week */}
                                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, fontSize: '0.65rem', color: 'var(--muted)' }}>
                                                            {week.map((cell, dayIndex) => {
                                                                const dayName = dayLabels[dayIndex];
                                                                const dayNum = cell ? new Date(cell.date).getDate() : '';
                                                                return (
                                                                    <div key={dayIndex} style={{ textAlign: 'center' }}>
                                                                        <div>{dayName}</div>
                                                                        {dayNum && <div style={{ fontSize: '0.6rem', color: 'var(--fg-secondary)' }}>{dayNum}</div>}
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    {/* Calendar cells for this week */}
                                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
                                                        {week.map((cell, dayIndex) => {
                                                            if (!cell) {
                                                                // Empty cell
                                                                return (
                                                                    <div
                                                                        key={`empty-${weekIndex}-${dayIndex}`}
                                                                        style={{
                                                                            aspectRatio: '1',
                                                                            background: 'var(--bg)',
                                                                            borderRadius: 4,
                                                                            border: '1px solid var(--surface-alt)',
                                                                            opacity: 0.3
                                                                        }}
                                                                    />
                                                                );
                                                            }

                                                            const isSelected = activityDateFilter === cell.date;
                                                            return (
                                                                <div
                                                                    key={cell.date}
                                                                    onClick={() => {
                                                                        const clickedDate = cell.date;
                                                                        setActivityDateFilter(activityDateFilter === clickedDate ? 'all' : clickedDate);
                                                                        setActivityPage(1);
                                                                    }}
                                                                    style={{
                                                                        aspectRatio: '1',
                                                                        background: getHeatColor(cell.requests),
                                                                        borderRadius: 4,
                                                                        cursor: 'pointer',
                                                                        border: isSelected ? '2px solid #60a5fa' : '1px solid #0f172a',
                                                                        display: 'flex',
                                                                        flexDirection: 'column',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        fontSize: '0.7rem',
                                                                        color: cell.requests > 0 ? '#fff' : 'var(--muted)',
                                                                        fontWeight: cell.requests > 0 ? 600 : 400,
                                                                        boxShadow: isSelected ? '0 0 0 2px rgba(96, 165, 250, 0.3)' : 'none',
                                                                        padding: '2px',
                                                                        gap: '1px'
                                                                    }}
                                                                    title={`${cell.date}\n${cell.requests} requests\n${dailyUniqueUsers[cell.date] || 0} unique users\n${fmt2(cell.cost)}`}
                                                                >
                                                                    <div style={{ fontSize: '0.68rem', fontWeight: 700, lineHeight: 1.1 }}>
                                                                        {cell.requests} {cell.requests === 1 ? 'Request' : 'Requests'}
                                                                    </div>
                                                                    {dailyUniqueUsers[cell.date] > 0 && (
                                                                        <div style={{ fontSize: '0.55rem', opacity: 0.8, lineHeight: 1.1, marginTop: '2px' }}>
                                                                            {dailyUniqueUsers[cell.date]} Unique {dailyUniqueUsers[cell.date] === 1 ? 'User' : 'Users'}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                    </div>
                                                );
                                            })}
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border)', fontSize: '0.7rem', color: 'var(--fg-secondary)' }}>
                                                <span style={{ fontWeight: 600 }}>Request Volume:</span>
                                                <span>0</span>
                                                <div style={{ width: 16, height: 16, background: 'var(--bg)', borderRadius: 3, border: '1px solid var(--border)' }} title="0 requests" />
                                                <div style={{ width: 16, height: 16, background: '#3b82f6', borderRadius: 3 }} title={`1-${Math.round(maxRequests * 0.25)} requests`} />
                                                <div style={{ width: 16, height: 16, background: '#8b5cf6', borderRadius: 3 }} title={`${Math.round(maxRequests * 0.25) + 1}-${Math.round(maxRequests * 0.5)} requests`} />
                                                <div style={{ width: 16, height: 16, background: '#ec4899', borderRadius: 3 }} title={`${Math.round(maxRequests * 0.5) + 1}-${Math.round(maxRequests * 0.75)} requests`} />
                                                <div style={{ width: 16, height: 16, background: '#f43f5e', borderRadius: 3 }} title={`${Math.round(maxRequests * 0.75) + 1}-${maxRequests} requests`} />
                                                <span>{maxRequests}</span>
                                            </div>
                                        </div>
                                    );
                                })()}
                    </div>

                    {activityLoading && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {[1, 2, 3, 4, 5].map(i => <Skeleton key={i} height={32} />)}
                        </div>
                    )}
                    {activityError && <div style={{ color: '#f87171', fontSize: '0.85rem' }}>{activityError}</div>}

                    {!activityLoading && activityLogs.length > 0 && (
                        <>
                            <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
                                <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr 1.5fr 70px 90px 80px', padding: '0.5rem 1rem', background: 'var(--bg)', borderBottom: '1px solid var(--border)' }}>
                                    {['Date / Time', 'User', 'Model', 'Auto-Router', 'Cost', 'Tokens'].map(h => (
                                        <div key={h} style={{ fontSize: '0.73rem', fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase' }}>{h}</div>
                                    ))}
                                </div>
                                <div style={{ maxHeight: '55vh', overflowY: 'auto' }}>
                                    {activityLogs
                                        .filter(r => {
                                            // Filter by selected models - client-side for real-time UI filtering
                                            if (selectedModels.length > 0 && !selectedModels.includes(r.model)) {
                                                return false;
                                            }
                                            // Date filtering is handled by backend via start_date/end_date parameters
                                            // No need to filter again here - causes double-filtering bug
                                            return true;
                                        })
                                        .map((r, i) => {
                                            const isBlocked = r.model === 'unknown' && r.spend === 0 && r.totalTokens === 0;
                                            const blockedTeam = isBlocked ? (r.teamAlias || getTeamName(r.teamId)) : null;
                                            return (
                                            <div key={r.id || i} style={{ display: 'grid', gridTemplateColumns: '160px 1fr 1.5fr 70px 90px 80px', padding: '0.45rem 1rem', background: isBlocked ? 'var(--critical-bg)' : i % 2 === 0 ? 'var(--bg)' : 'transparent', alignItems: 'center', opacity: isBlocked ? 0.75 : 1 }}>
                                                <div style={{ fontSize: '0.78rem', color: 'var(--muted)' }}>{fmtDate(r.startTime || r.endTime)}</div>
                                                <div style={{ fontSize: '0.82rem', color: 'var(--fg-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.endUser || '—'}</div>
                                                <div style={{ fontSize: '0.78rem', color: 'var(--fg-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                                                    {isBlocked ? (
                                                        <span style={{ fontSize: '0.65rem', fontWeight: 700, background: 'var(--critical-bg-hover)', color: 'var(--critical)', border: '1px solid rgba(229,72,77,0.35)', borderRadius: 4, padding: '1px 5px', flexShrink: 0 }}>
                                                            Blocked{blockedTeam && blockedTeam !== '—' ? ` · ${blockedTeam}` : ''}
                                                        </span>
                                                    ) : (
                                                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{resolveModelName(r.model) || '—'}</span>
                                                    )}
                                                </div>
                                                <div style={{ fontSize: '0.78rem' }}>
                                                    {r.autoRouted ? (
                                                        <span style={{ fontSize: '0.65rem', fontWeight: 700, background: 'var(--accent-bg)', color: '#818cf8', border: '1px solid rgba(99,102,241,0.35)', borderRadius: 4, padding: '1px 5px' }}>
                                                            {r.routerTier || 'Yes'}
                                                        </span>
                                                    ) : (
                                                        <span style={{ fontSize: '0.78rem', color: 'var(--muted)' }}>—</span>
                                                    )}
                                                </div>
                                                <div style={{ fontSize: '0.82rem', color: isBlocked ? 'var(--muted)' : 'var(--fg-primary)', fontWeight: 600 }}>{isBlocked ? '—' : fmt2(r.spend)}</div>
                                                <div style={{ fontSize: '0.82rem', color: 'var(--fg-secondary)' }}>{isBlocked ? '—' : fmtTokens(r.totalTokens)}</div>
                                            </div>
                                            );
                                        })}
                                </div>
                            </div>
                            {/* Pagination removed - backend returns all filtered records, table is scrollable */}
                        </>
                    )}
                    {!activityLoading && activityLogs.length === 0 && !activityError && (
                        <div style={{ textAlign: 'center', color: 'var(--border)', padding: '3rem' }}>
                            {activityTotal > 0 ? (
                                <>
                                    <div style={{ marginBottom: '1rem' }}>Found {activityTotal} records but data did not load correctly.</div>
                                    <button style={btnPrimary} onClick={() => fetchActivity(1)}>Retry</button>
                                </>
                            ) : (
                                <div>No activity logs found for the selected period.</div>
                            )}
                        </div>
                    )}
                </div>
                </>
                )}

                {/* KEYS SUB-TAB */}
                {analyticsSubTab === 'keys' && isAdmin && (
                <>
                {/* Team Filter Dropdown */}
                <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, padding: '0.75rem 1rem', marginBottom: '1rem', display: 'flex', gap: '1rem', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                        <div>
                            <label style={{ ...filterLabel, marginBottom: '0.5rem', display: 'block' }}>Filter by Team</label>
                            <select
                                style={{ ...filterInput, minWidth: '200px' }}
                                value={selectedTeamForKeysAnalytics}
                                onChange={(e) => setSelectedTeamForKeysAnalytics(e.target.value)}
                            >
                                <option value="all">All Teams</option>
                                {visibleTeams.map(t => (
                                    <option key={t.team_id} value={t.team_id}>
                                        {t.team_alias || t.team_id}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <button
                        style={{ ...btnPrimary, fontSize: '0.8rem', padding: '0.4rem 0.8rem' }}
                        onClick={exportKeysAnalytics}
                        title="Export API keys data to CSV"
                    >
                        Export to CSV
                    </button>
                </div>

                {/* Team API Spend Overview */}
                <div style={chartCard}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                        <div style={chartTitle}>Team API Spend Overview</div>
                        <div style={{ display: 'flex', gap: 4 }}>
                            {[['spend', 'Spend'], ['requests', 'Requests'], ['tokens', 'Tokens']].map(([k, lbl]) => (
                                <button key={k} onClick={() => setTeamSpendMetric(k)} style={{ fontSize: '0.72rem', padding: '2px 8px', borderRadius: 4, border: 'none', cursor: 'pointer', background: teamSpendMetric === k ? '#6366f1' : 'var(--bg)', color: teamSpendMetric === k ? '#fff' : 'var(--fg-secondary)' }}>{lbl}</button>
                            ))}
                        </div>
                    </div>
                    {aggregatedActivity.loading
                        ? <Skeleton height={280} />
                        : teamApiKeySpend.length === 0
                            ? <div style={{ color: 'var(--border)', fontSize: '0.85rem', textAlign: 'center', paddingTop: 80 }}>No team API spend data available</div>
                            : (
                                <ResponsiveContainer width="100%" height={280}>
                                    <BarChart data={teamApiKeySpend} layout="vertical" margin={{ top: 5, right: 20, bottom: 5, left: 5 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                                        <XAxis
                                            type="number"
                                            tick={{ fontSize: 10, fill: 'var(--fg-secondary)' }}
                                            tickFormatter={v => teamSpendMetric === 'spend' ? `$${v.toFixed(0)}` : teamSpendMetric === 'tokens' ? fmtTokens(v) : v}
                                        />
                                        <YAxis type="category" dataKey="teamName" tick={{ fontSize: 9, fill: 'var(--fg-secondary)' }} width={120} />
                                        <ReTooltip
                                            contentStyle={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 6, fontSize: 11, color: 'var(--fg-primary)', padding: '8px 12px', maxWidth: '250px', whiteSpace: 'normal', wordWrap: 'break-word' }}
                                            labelStyle={{ color: 'var(--fg-primary)', fontWeight: '600', marginBottom: '4px' }}
                                            itemStyle={{ color: 'var(--fg-primary)' }}
                                            wrapperStyle={{ zIndex: 1000, pointerEvents: 'none' }}
                                            cursor={{ fill: 'rgba(100, 116, 139, 0.1)' }}
                                            formatter={(v, name) => {
                                                if (name === 'spend') return [fmt2(v), 'Spend'];
                                                if (name === 'tokens') return [fmtTokens(v), 'Tokens'];
                                                return [v.toLocaleString(), 'Requests'];
                                            }}
                                            labelFormatter={(teamName, payload) => {
                                                const team = payload[0]?.payload;
                                                if (!team) return teamName;
                                                const budgetPercent = team.maxBudget > 0 ? ((team.spend / team.maxBudget) * 100).toFixed(1) : 0;
                                                const budgetInfo = team.maxBudget > 0 ? `\nBudget: ${fmt2(team.spend)} / ${fmt2(team.maxBudget)} (${budgetPercent}%)` : '';
                                                const duration = team.budgetDuration ? ` (${fmtDuration(team.budgetDuration)})` : '';
                                                return `${teamName}${budgetInfo}${duration}`;
                                            }}
                                        />
                                        <Bar
                                            dataKey={teamSpendMetric}
                                            name={teamSpendMetric}
                                            radius={[0, 4, 4, 0]}
                                            onClick={(data) => {
                                                if (data && data.teamId) {
                                                    setSelectedTeamForKeysAnalytics(data.teamId);
                                                }
                                            }}
                                            cursor="pointer"
                                        >
                                            {teamApiKeySpend.map((entry, index) => {
                                                let fillColor = entry.color;
                                                if (entry.maxBudget > 0) {
                                                    const budgetPercent = (entry.spend / entry.maxBudget) * 100;
                                                    if (budgetPercent > 100) {
                                                        fillColor = '#ef4444';
                                                    } else if (budgetPercent > 90) {
                                                        fillColor = '#f97316';
                                                    } else if (budgetPercent > 75) {
                                                        fillColor = '#eab308';
                                                    } else {
                                                        fillColor = '#22c55e';
                                                    }
                                                }
                                                return (
                                                    <Cell
                                                        key={`cell-${index}`}
                                                        fill={selectedTeamForKeysAnalytics === entry.teamId ? '#60a5fa' : fillColor}
                                                        opacity={selectedTeamForKeysAnalytics !== 'all' && selectedTeamForKeysAnalytics !== entry.teamId ? 0.3 : 1}
                                                    />
                                                );
                                            })}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            )}
                </div>

                {/* API Key Spend Over Time */}
                <div style={chartCard}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                        <div style={chartTitle}>API Key Spend Over Time</div>
                        <div style={{ display: 'flex', gap: 4 }}>
                            <button onClick={() => setTimeSeriesView('total')} style={{ fontSize: '0.72rem', padding: '2px 8px', borderRadius: 4, border: 'none', cursor: 'pointer', background: timeSeriesView === 'total' ? '#6366f1' : 'var(--bg)', color: timeSeriesView === 'total' ? '#fff' : 'var(--fg-secondary)' }}>Total</button>
                            <button onClick={() => setTimeSeriesView('by_team')} style={{ fontSize: '0.72rem', padding: '2px 8px', borderRadius: 4, border: 'none', cursor: 'pointer', background: timeSeriesView === 'by_team' ? '#6366f1' : 'var(--bg)', color: timeSeriesView === 'by_team' ? '#fff' : 'var(--fg-secondary)' }}>By Team</button>
                        </div>
                    </div>
                    {aggregatedActivity.loading
                        ? <Skeleton height={280} />
                        : apiKeySpendTimeSeries.length === 0
                            ? <div style={{ color: 'var(--border)', fontSize: '0.85rem', textAlign: 'center', paddingTop: 80 }}>No time series data available</div>
                            : (
                                <ResponsiveContainer width="100%" height={280}>
                                    <AreaChart data={apiKeySpendTimeSeries} margin={{ top: 5, right: 20, bottom: 5, left: 5 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                                        <XAxis
                                            dataKey="date"
                                            tick={{ fontSize: 10, fill: 'var(--fg-secondary)' }}
                                            tickFormatter={(date) => {
                                                const d = new Date(date);
                                                return `${d.getMonth() + 1}/${d.getDate()}`;
                                            }}
                                        />
                                        <YAxis
                                            tick={{ fontSize: 10, fill: 'var(--fg-secondary)' }}
                                            tickFormatter={v => `$${v.toFixed(0)}`}
                                        />
                                        <ReTooltip
                                            contentStyle={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 6, fontSize: 11, color: 'var(--fg-primary)', padding: '8px 12px' }}
                                            labelStyle={{ color: 'var(--fg-primary)', fontWeight: '600', marginBottom: '4px' }}
                                            itemStyle={{ color: 'var(--fg-primary)' }}
                                            cursor={{ fill: 'rgba(100, 116, 139, 0.1)' }}
                                            formatter={(v) => fmt2(v)}
                                            labelFormatter={(date) => {
                                                const d = new Date(date);
                                                return d.toLocaleDateString();
                                            }}
                                        />
                                        {timeSeriesView === 'total' ? (
                                            <Area isAnimationActive={false}type="monotone" dataKey="total" stroke="#6366f1" fill="#6366f1" fillOpacity={0.6} />
                                        ) : (
                                            visibleTeams.map((team, index) => (
                                                <Area
                                                    key={team.team_id}
                                                    type="monotone"
                                                    dataKey={team.team_id}
                                                    stackId="1"
                                                    stroke={CHART_COLORS[index % CHART_COLORS.length]}
                                                    fill={CHART_COLORS[index % CHART_COLORS.length]}
                                                    fillOpacity={0.6}
                                                    name={team.team_alias || team.team_id}
                                                />
                                            ))
                                        )}
                                    </AreaChart>
                                </ResponsiveContainer>
                            )}
                </div>

                {/* API Key Performance */}
                <div style={chartCard}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                        <div style={chartTitle}>
                            API Key Performance
                            {selectedTeamForKeysAnalytics !== 'all' && (
                                <span style={{ color: 'var(--fg-secondary)', fontWeight: 400, fontSize: '0.85rem', marginLeft: '0.5rem' }}>
                                    ({visibleTeams.find(t => t.team_id === selectedTeamForKeysAnalytics)?.team_alias || selectedTeamForKeysAnalytics})
                                </span>
                            )}
                        </div>
                        <div style={{ display: 'flex', gap: 4 }}>
                            {[['spend', 'Spend'], ['requests', 'Requests'], ['tokens', 'Tokens']].map(([k, lbl]) => (
                                <button key={k} onClick={() => setApiKeyMetric(k)} style={{ fontSize: '0.72rem', padding: '2px 8px', borderRadius: 4, border: 'none', cursor: 'pointer', background: apiKeyMetric === k ? '#6366f1' : 'var(--bg)', color: apiKeyMetric === k ? '#fff' : 'var(--fg-secondary)' }}>{lbl}</button>
                            ))}
                        </div>
                    </div>
                    {aggregatedActivity.loading
                        ? <Skeleton height={280} />
                        : apiKeyPerformance.length === 0
                            ? <div style={{ color: 'var(--border)', fontSize: '0.85rem', textAlign: 'center', paddingTop: 80 }}>No API key data available</div>
                            : (
                                <ResponsiveContainer width="100%" height={280}>
                                    <BarChart data={apiKeyPerformance} layout="vertical" margin={{ top: 5, right: 20, bottom: 5, left: 5 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                                        <XAxis
                                            type="number"
                                            tick={{ fontSize: 10, fill: 'var(--fg-secondary)' }}
                                            tickFormatter={v => apiKeyMetric === 'spend' ? `$${v.toFixed(0)}` : apiKeyMetric === 'tokens' ? fmtTokens(v) : v}
                                        />
                                        <YAxis type="category" dataKey="alias" tick={{ fontSize: 9, fill: 'var(--fg-secondary)' }} width={120} />
                                        <ReTooltip
                                            contentStyle={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 6, fontSize: 11, color: 'var(--fg-primary)', padding: '8px 12px', maxWidth: '250px', whiteSpace: 'normal', wordWrap: 'break-word' }}
                                            labelStyle={{ color: 'var(--fg-primary)', fontWeight: '600', marginBottom: '4px' }}
                                            itemStyle={{ color: 'var(--fg-primary)' }}
                                            wrapperStyle={{ zIndex: 1000, pointerEvents: 'none' }}
                                            cursor={{ fill: 'rgba(100, 116, 139, 0.1)' }}
                                            formatter={(v, name) => {
                                                if (name === 'spend') return [fmt2(v), 'Spend'];
                                                if (name === 'tokens') return [fmtTokens(v), 'Tokens'];
                                                return [v.toLocaleString(), 'Requests'];
                                            }}
                                            labelFormatter={(alias, payload) => {
                                                const key = payload[0]?.payload;
                                                if (!key) return alias;
                                                return `${alias}\nTeam: ${key.teamId || 'N/A'}\nSuccess Rate: ${key.successfulRequests > 0 ? ((key.successfulRequests / key.requests) * 100).toFixed(1) : 0}%`;
                                            }}
                                        />
                                        <Bar
                                            dataKey={apiKeyMetric}
                                            name={apiKeyMetric}
                                            radius={[0, 4, 4, 0]}
                                            onClick={(data) => {
                                                if (data && data.keyHash) {
                                                    setSelectedApiKey(selectedApiKey === data.keyHash ? null : data.keyHash);
                                                }
                                            }}
                                            cursor="pointer"
                                        >
                                            {apiKeyPerformance.map((entry, index) => (
                                                <Cell
                                                    key={`cell-${index}`}
                                                    fill={selectedApiKey === entry.keyHash ? '#60a5fa' : entry.color}
                                                    opacity={selectedApiKey && selectedApiKey !== entry.keyHash ? 0.3 : 1}
                                                />
                                            ))}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            )}
                </div>

                {/* Model Breakdown for Selected API Key */}
                {selectedApiKey && selectedKeyModelBreakdown.length > 0 && (
                    <div style={chartCard}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                            <div style={chartTitle}>
                                Model Breakdown: {apiKeyPerformance.find(k => k.keyHash === selectedApiKey)?.alias || 'Unknown Key'}
                            </div>
                        </div>
                        <div style={{ overflow: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                                <thead style={{ background: 'var(--bg)' }}>
                                    <tr style={{ borderBottom: '1px solid var(--border)' }}>
                                        {['Model', 'Requests', 'Tokens', 'Cost', '% of Total'].map(h => (
                                            <th key={h} style={{ textAlign: 'left', padding: '0.4rem 0.6rem', color: 'var(--muted)', fontWeight: 700, fontSize: '0.73rem', textTransform: 'uppercase' }}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {selectedKeyModelBreakdown.map((model, i) => (
                                        <tr
                                            key={i}
                                            style={{
                                                background: i % 2 === 0 ? 'var(--bg)' : 'transparent',
                                            }}
                                        >
                                            <td style={{ padding: '0.4rem 0.6rem', color: 'var(--fg-secondary)', fontSize: '0.8rem' }} title={model.model}>
                                                {resolveModelName(model.model)}
                                            </td>
                                            <td style={{ padding: '0.4rem 0.6rem', color: 'var(--fg-secondary)', fontSize: '0.8rem' }}>
                                                {model.requests.toLocaleString()}
                                            </td>
                                            <td style={{ padding: '0.4rem 0.6rem', color: 'var(--fg-secondary)', fontSize: '0.8rem' }}>
                                                {fmtTokens(model.tokens)}
                                            </td>
                                            <td style={{ padding: '0.4rem 0.6rem', color: 'var(--fg-primary)', fontWeight: 600, fontSize: '0.8rem' }}>
                                                {fmt2(model.spend)}
                                            </td>
                                            <td style={{ padding: '0.4rem 0.6rem', color: 'var(--fg-secondary)', fontSize: '0.8rem' }}>
                                                {model.percentage.toFixed(1)}%
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* API Key Usage Logs Table */}
                <div style={chartCard}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                        <div style={chartTitle}>
                            {selectedApiKey
                                ? `Key Usage Logs: ${apiKeyPerformance.find(k => k.keyHash === selectedApiKey)?.alias || 'Unknown Key'}`
                                : 'Recent API Key Usage Logs'}
                        </div>
                        {selectedApiKey && (
                            <button
                                onClick={() => setSelectedApiKey(null)}
                                style={{
                                    padding: '0.3rem 0.6rem',
                                    fontSize: '0.7rem',
                                    fontWeight: 600,
                                    border: '1px solid var(--border)',
                                    borderRadius: 4,
                                    cursor: 'pointer',
                                    background: 'var(--bg)',
                                    color: 'var(--fg-secondary)',
                                    transition: 'all 0.2s'
                                }}
                            >Clear Filter</button>
                        )}
                    </div>
                    {activityLoading ? (
                        <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--fg-secondary)', fontSize: '0.85rem' }}>
                            Loading logs...
                        </div>
                    ) : activityLogs.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--muted)', fontSize: '0.85rem' }}>
                            No activity logs found for the selected period.
                        </div>
                    ) : (
                        <div style={{ overflow: 'auto', maxHeight: '400px' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                                <thead style={{ position: 'sticky', top: 0, background: 'var(--bg)', zIndex: 1 }}>
                                    <tr style={{ borderBottom: '1px solid var(--border)' }}>
                                        {['Time', 'User', 'Team', 'Model', 'Tokens', 'Cost', 'Status'].map(h => (
                                            <th key={h} style={{ textAlign: 'left', padding: '0.4rem 0.6rem', color: 'var(--muted)', fontWeight: 700, fontSize: '0.73rem', textTransform: 'uppercase' }}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {(selectedApiKey
                                        ? activityLogs.filter(log => log.apiKey === selectedApiKey)
                                        : activityLogs
                                    )
                                        .slice(0, 50)
                                        .map((log, i) => (
                                            <tr
                                                key={i}
                                                style={{
                                                    background: i % 2 === 0 ? 'var(--bg)' : 'transparent',
                                                }}
                                            >
                                                <td style={{ padding: '0.4rem 0.6rem', color: 'var(--fg-secondary)', fontSize: '0.75rem' }}>
                                                    {new Date(log.endTime || log.startTime).toLocaleString()}
                                                </td>
                                                <td style={{ padding: '0.4rem 0.6rem', color: 'var(--fg-secondary)', fontSize: '0.8rem' }}>
                                                    {log.endUser || '—'}
                                                </td>
                                                <td style={{ padding: '0.4rem 0.6rem', color: 'var(--fg-secondary)', fontSize: '0.8rem' }}>
                                                    {getTeamName(log.teamId)}
                                                </td>
                                                <td style={{ padding: '0.4rem 0.6rem', color: 'var(--fg-secondary)', fontSize: '0.8rem' }} title={log.model}>
                                                    {resolveModelName(log.model)}
                                                </td>
                                                <td style={{ padding: '0.4rem 0.6rem', color: 'var(--fg-secondary)', fontSize: '0.8rem' }}>
                                                    {fmtTokens(log.totalTokens || 0)}
                                                </td>
                                                <td style={{ padding: '0.4rem 0.6rem', color: 'var(--fg-primary)', fontWeight: 600, fontSize: '0.8rem' }}>
                                                    {fmt2(log.spend || 0)}
                                                </td>
                                                <td style={{ padding: '0.4rem 0.6rem', color: 'var(--fg-secondary)', fontSize: '0.8rem' }}>
                                                    {log.status || 'success'}
                                                </td>
                                            </tr>
                                        ))}
                                </tbody>
                            </table>
                            {selectedApiKey && activityLogs.filter(log => log.apiKey === selectedApiKey).length === 0 && (
                                <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--muted)', fontSize: '0.85rem' }}>
                                    No logs found for this API key in the current date range
                                </div>
                            )}
                        </div>
                    )}
                </div>
                </>
                )}

                {/* USERS SUB-TAB */}
                {analyticsSubTab === 'users' && isAdmin && (
                <>
                {/* Top Users by Spend/Usage */}
                <div style={chartCard}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                            <div style={chartTitle}>Top Users by Usage</div>
                            <div style={{ display: 'flex', gap: 4 }}>
                                {[['spend', 'Spend'], ['requests', 'Requests'], ['tokens', 'Tokens']].map(([k, lbl]) => (
                                    <button key={k} onClick={() => setTopUsersMetric(k)} style={{ fontSize: '0.72rem', padding: '2px 8px', borderRadius: 4, border: 'none', cursor: 'pointer', background: topUsersMetric === k ? '#6366f1' : 'var(--bg)', color: topUsersMetric === k ? '#fff' : 'var(--fg-secondary)' }}>{lbl}</button>
                                ))}
                            </div>
                        </div>
                        {!hasData || topUsersBySpend.length === 0
                            ? <Skeleton height={280} />
                            : (
                                <ResponsiveContainer width="100%" height={280}>
                                    <BarChart data={topUsersBySpend} layout="vertical" margin={{ top: 5, right: 20, bottom: 5, left: 5 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                                        <XAxis type="number" tick={{ fontSize: 10, fill: 'var(--fg-secondary)' }} tickFormatter={v => topUsersMetric === 'spend' ? `$${v.toFixed(0)}` : topUsersMetric === 'tokens' ? fmtTokens(v) : v} />
                                        <YAxis type="category" dataKey="email" tick={{ fontSize: 9, fill: 'var(--fg-secondary)' }} width={120} />
                                        <ReTooltip
                                            contentStyle={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 6, fontSize: 11, color: 'var(--fg-primary)', padding: '8px 12px' }}
                                            labelStyle={{ color: 'var(--fg-primary)', fontWeight: '600', marginBottom: '4px' }}
                                            itemStyle={{ color: 'var(--fg-primary)' }}
                                            cursor={{ fill: 'rgba(100, 116, 139, 0.1)' }}
                                            formatter={(v, name) => {
                                                if (name === 'spend') return [fmt2(v), 'Spend'];
                                                if (name === 'tokens') return [fmtTokens(v), 'Tokens'];
                                                return [v, 'Requests'];
                                            }}
                                            labelFormatter={(email, payload) => {
                                                const user = payload[0]?.payload;
                                                return user ? `${email}\nTeams: ${user.teams}` : email;
                                            }}
                                        />
                                        <Bar isAnimationActive={false}dataKey={topUsersMetric} name={topUsersMetric} radius={[0, 4, 4, 0]}>
                                            {topUsersBySpend.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={entry.color} />
                                            ))}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            )}
                </div>

                {/* User Details Table */}
                {topUsersBySpend.length > 0 && (
                    <div style={chartCard}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                <div style={chartTitle}>User Details</div>
                                {deptDrilldown && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', background: '#1e3a5f', border: '1px solid #3b82f6', borderRadius: 4, padding: '0.2rem 0.6rem', fontSize: '0.78rem', color: '#93c5fd' }}>
                                        <span>{deptDrilldown}</span>
                                        <button
                                            onClick={() => setDeptDrilldown(null)}
                                            style={{ background: 'none', border: 'none', color: '#93c5fd', cursor: 'pointer', padding: 0, fontSize: '0.85rem', lineHeight: 1 }}
                                        >x</button>
                                    </div>
                                )}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <span style={{ fontSize: '0.75rem', color: 'var(--muted)' }}>Spend range ($):</span>
                                <input
                                    type="number"
                                    min="0"
                                    placeholder="Min"
                                    value={userSpendMin}
                                    onChange={e => setUserSpendMin(e.target.value)}
                                    style={{ width: 70, padding: '0.25rem 0.4rem', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 4, color: 'var(--fg-primary)', fontSize: '0.78rem' }}
                                />
                                <span style={{ color: 'var(--border)', fontSize: '0.75rem' }}>–</span>
                                <input
                                    type="number"
                                    min="0"
                                    placeholder="Max"
                                    value={userSpendMax}
                                    onChange={e => setUserSpendMax(e.target.value)}
                                    style={{ width: 70, padding: '0.25rem 0.4rem', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 4, color: 'var(--fg-primary)', fontSize: '0.78rem' }}
                                />
                                {(userSpendMin !== '' || userSpendMax !== '') && (
                                    <button
                                        onClick={() => { setUserSpendMin(''); setUserSpendMax(''); }}
                                        style={{ fontSize: '0.72rem', padding: '2px 7px', borderRadius: 4, border: 'none', cursor: 'pointer', background: 'var(--border)', color: 'var(--fg-secondary)' }}
                                    >Clear</button>
                                )}
                            </div>
                        </div>
                        {(() => {
                            const minVal = userSpendMin !== '' ? parseFloat(userSpendMin) : null;
                            const maxVal = userSpendMax !== '' ? parseFloat(userSpendMax) : null;
                            const filteredUsers = topUsersBySpend.filter(u => {
                                if (minVal !== null && u.spend < minVal) return false;
                                if (maxVal !== null && u.spend > maxVal) return false;
                                if (deptDrilldown && u.department !== deptDrilldown) return false;
                                return true;
                            });
                            return (
                                <>
                                {(minVal !== null || maxVal !== null) && (
                                    <div style={{ fontSize: '0.75rem', color: 'var(--muted)', marginBottom: '0.5rem' }}>
                                        Showing {filteredUsers.length} of {topUsersBySpend.length} users
                                    </div>
                                )}
                                <div style={{ overflow: 'auto', maxHeight: '400px' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                                        <thead style={{ position: 'sticky', top: 0, background: 'var(--bg)', zIndex: 1 }}>
                                            <tr style={{ borderBottom: '1px solid var(--border)' }}>
                                                {['User Email', 'Organisation', 'Department', 'Spend', 'Requests', 'Tokens', 'Teams'].map(h => (
                                                    <th key={h} style={{ textAlign: 'left', padding: '0.4rem 0.6rem', color: 'var(--muted)', fontWeight: 700, fontSize: '0.73rem', textTransform: 'uppercase' }}>{h}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredUsers.length === 0
                                                ? (
                                                    <tr><td colSpan={7} style={{ padding: '1rem 0.6rem', color: 'var(--border)', textAlign: 'center', fontSize: '0.8rem' }}>No users match this spend range</td></tr>
                                                )
                                                : filteredUsers.map((user, i) => (
                                                    <tr key={user.email} style={{ background: i % 2 === 0 ? 'var(--bg)' : 'transparent' }}>
                                                        <td style={{ padding: '0.4rem 0.6rem', color: 'var(--fg-secondary)' }}>{user.email}</td>
                                                        <td style={{ padding: '0.4rem 0.6rem', color: 'var(--fg-secondary)', fontSize: '0.78rem' }}>{user.orgUnit || '—'}</td>
                                                        <td style={{ padding: '0.4rem 0.6rem', color: 'var(--fg-secondary)', fontSize: '0.78rem' }}>{user.department || '—'}</td>
                                                        <td style={{ padding: '0.4rem 0.6rem', color: 'var(--fg-primary)', fontWeight: 600 }}>{fmt2(user.spend)}</td>
                                                        <td style={{ padding: '0.4rem 0.6rem', color: 'var(--fg-secondary)' }}>{user.requests}</td>
                                                        <td style={{ padding: '0.4rem 0.6rem', color: 'var(--fg-secondary)' }}>{fmtTokens(user.tokens)}</td>
                                                        <td style={{ padding: '0.4rem 0.6rem', color: 'var(--fg-secondary)', fontSize: '0.75rem' }}>{user.teams}</td>
                                                    </tr>
                                                ))
                                            }
                                        </tbody>
                                    </table>
                                </div>
                                </>
                            );
                        })()}
                    </div>
                )}
                </>
                )}

                {/* DEPARTMENTS SUB-TAB */}
                {analyticsSubTab === 'departments' && isAdmin && (() => {
                    const totalSpend = aggregatedDepartments.reduce((s, d) => s + d.spend, 0);
                    const hasDepData = aggregatedDepartments.length > 0;
                    const deptChartData = aggregatedDepartments.map((d, i) => ({
                        ...d,
                        name: d.department,
                        color: CHART_COLORS[i % CHART_COLORS.length]
                    }));

                    return (
                        <>
                        {/* Spend by Department bar chart */}
                        <div style={chartCard}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                                <div style={chartTitle}>Spend by Department</div>
                                {isAdmin && (
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.35rem' }}>
                                        <button
                                            style={{ ...btnPrimary, fontSize: '0.78rem', padding: '0.3rem 0.8rem', opacity: deptSyncLoading ? 0.6 : 1 }}
                                            disabled={deptSyncLoading}
                                            title="Sync department data from Microsoft 365. Opens a consent popup on first use."
                                            onClick={async () => {
                                                setDeptSyncLoading(true);
                                                setDeptSyncError(null);
                                                try {
                                                    // Acquire Graph token using the logged-in user's session (delegated)
                                                    // Acquire Graph token silently using the already-signed-in account.
                                                    // Falls back to popup only if consent hasn't been granted yet.
                                                    const accounts = instance.getAllAccounts();
                                                    const account = accounts[0];
                                                    let graphToken;
                                                    try {
                                                        const tokenRes = await instance.acquireTokenSilent({
                                                            scopes: ['https://graph.microsoft.com/User.Read.All'],
                                                            account
                                                        });
                                                        graphToken = tokenRes.accessToken;
                                                    } catch {
                                                        const tokenRes = await instance.acquireTokenPopup({
                                                            scopes: ['https://graph.microsoft.com/User.Read.All'],
                                                            account
                                                        });
                                                        graphToken = tokenRes.accessToken;
                                                    }
                                                    const result = await triggerDepartmentSync(graphToken);
                                                    if (result.success) {
                                                        setDeptLastSync(result.data?.lastFullSync || null);
                                                        // Clear spend cache then immediately re-fetch so department
                                                        // mappings update without requiring a manual Refresh click
                                                        setSpendCache({});
                                                        refreshAnalytics();
                                                        // Re-fetch departments in App so SystemHealth stat card updates
                                                        if (onRefresh) onRefresh();
                                                    } else {
                                                        setDeptSyncError(result.error || 'Sync failed');
                                                    }
                                                } catch (e) {
                                                    if (e.errorCode !== 'user_cancelled') {
                                                        setDeptSyncError(e.message || 'Sync failed');
                                                    }
                                                }
                                                setDeptSyncLoading(false);
                                            }}
                                        >
                                            {deptSyncLoading ? 'Syncing...' : 'Sync Departments'}
                                        </button>
                                        {deptSyncError && (
                                            <div style={{ fontSize: '0.75rem', color: '#ef4444' }}>
                                                {deptSyncError}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                            {deptLastSync && (
                                <div style={{ fontSize: '0.72rem', color: 'var(--border)', marginBottom: '0.5rem' }}>
                                    Last synced: {fmtDate(deptLastSync)}
                                </div>
                            )}
                            {!hasDepData
                                ? <div style={{ color: 'var(--border)', fontSize: '0.85rem', padding: '2rem 0', textAlign: 'center' }}>No department data yet — sync is running in the background on first startup.</div>
                                : (
                                    <ResponsiveContainer width="100%" height={Math.max(200, deptChartData.length * 40)}>
                                        <BarChart
                                            data={deptChartData}
                                            layout="vertical"
                                            margin={{ top: 5, right: 60, bottom: 5, left: 10 }}
                                            style={{ cursor: 'pointer' }}
                                            onClick={e => {
                                                const dept = e?.activePayload?.[0]?.payload?.department;
                                                if (dept) {
                                                    setDeptDrilldown(dept);
                                                    setAnalyticsSubTab('users');
                                                }
                                            }}
                                        >
                                            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                                            <XAxis type="number" tick={{ fontSize: 10, fill: 'var(--fg-secondary)' }} tickFormatter={v => `$${v.toFixed(2)}`} />
                                            <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: 'var(--fg-secondary)' }} width={120} />
                                            <ReTooltip
                                                contentStyle={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 6, fontSize: 12, color: 'var(--fg-primary)', padding: '8px 12px' }}
                                                labelStyle={{ color: 'var(--fg-primary)', fontWeight: '600', marginBottom: '4px' }}
                                                itemStyle={{ color: 'var(--fg-primary)' }}
                                                cursor={{ fill: 'rgba(255,255,255,0.05)' }}
                                                formatter={(v) => [fmt2(v), 'Spend']}
                                            />
                                            <Bar isAnimationActive={false} dataKey="spend" radius={[0, 4, 4, 0]} label={{ position: 'right', fill: 'var(--fg-secondary)', fontSize: 10, formatter: v => v > 0 ? fmt2(v) : '' }}>
                                                {deptChartData.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={entry.color} />
                                                ))}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                )}
                        </div>

                        {/* Department breakdown table */}
                        {hasDepData && (
                            <div style={chartCard}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                                    <div style={chartTitle}>Department Breakdown</div>
                                    <span style={{ fontSize: '0.72rem', color: 'var(--border)' }}>Click a row to see users</span>
                                </div>
                                <div style={{ overflow: 'auto' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                                        <thead style={{ position: 'sticky', top: 0, background: 'var(--bg)', zIndex: 1 }}>
                                            <tr style={{ borderBottom: '1px solid var(--border)' }}>
                                                {['Department', 'Users', 'Requests', 'Tokens', 'Spend', '% of Total'].map(h => (
                                                    <th key={h} style={{ textAlign: 'left', padding: '0.4rem 0.6rem', color: 'var(--muted)', fontWeight: 700, fontSize: '0.73rem', textTransform: 'uppercase' }}>{h}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {aggregatedDepartments.map((d, i) => (
                                                <tr
                                                    key={d.department}
                                                    onClick={() => {
                                                        setDeptDrilldown(d.department);
                                                        setAnalyticsSubTab('users');
                                                    }}
                                                    style={{ background: i % 2 === 0 ? 'var(--bg)' : 'transparent', borderBottom: '1px solid var(--surface-alt)', cursor: 'pointer' }}
                                                    onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-hover)'}
                                                    onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? 'var(--bg)' : 'transparent'}
                                                >
                                                    <td style={{ padding: '0.45rem 0.6rem', color: 'var(--fg-primary)', fontWeight: 600 }}>{d.department}</td>
                                                    <td style={{ padding: '0.45rem 0.6rem', color: 'var(--fg-secondary)' }}>{d.users}</td>
                                                    <td style={{ padding: '0.45rem 0.6rem', color: 'var(--fg-secondary)' }}>{d.requests.toLocaleString()}</td>
                                                    <td style={{ padding: '0.45rem 0.6rem', color: 'var(--fg-secondary)' }}>{fmtTokens(d.tokens)}</td>
                                                    <td style={{ padding: '0.45rem 0.6rem', color: '#10b981', fontWeight: 600 }}>{fmt2(d.spend)}</td>
                                                    <td style={{ padding: '0.45rem 0.6rem', color: 'var(--fg-secondary)' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                            <div style={{ flex: 1, height: 6, background: 'var(--bg)', borderRadius: 3, overflow: 'hidden', minWidth: 60, border: '1px solid var(--border)' }}>
                                                                <div style={{ width: `${totalSpend > 0 ? Math.min((d.spend / totalSpend) * 100, 100) : 0}%`, height: '100%', background: CHART_COLORS[i % CHART_COLORS.length], borderRadius: 3 }} />
                                                            </div>
                                                            <span style={{ minWidth: 38, textAlign: 'right', fontSize: '0.78rem' }}>
                                                                {totalSpend > 0 ? ((d.spend / totalSpend) * 100).toFixed(1) : '0.0'}%
                                                            </span>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                        </>
                    );
                })()}

                {/* TEAMS SUB-TAB */}
                {analyticsSubTab === 'teams' && isAdmin && (
                <>
                {/* Budget vs Team Spend */}
                <div style={chartCard}>
                    <div style={chartTitle}>Budget vs Team Spend</div>
                    {barChartData.length === 0
                        ? <Skeleton height={220} />
                        : (
                            <ResponsiveContainer width="100%" height={280}>
                                <BarChart data={barChartData} margin={{ top: 20, right: 10, bottom: 60, left: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                                    <XAxis dataKey="name" tick={{ fontSize: 10, fill: 'var(--fg-secondary)' }} angle={0} textAnchor="middle" interval={0} height={55} />
                                    <YAxis tick={{ fontSize: 11, fill: 'var(--fg-secondary)' }} tickFormatter={v => `$${v}`} width={50} />
                                    <ReTooltip
                                        contentStyle={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 6, fontSize: 12, color: 'var(--fg-primary)', padding: '8px 12px' }}
                                        labelStyle={{ color: 'var(--fg-primary)', fontWeight: '600', marginBottom: '4px' }}
                                        itemStyle={{ color: 'var(--fg-primary)' }}
                                        cursor={{ fill: 'transparent' }}
                                        formatter={(v, name) => [fmt2(v), name === 'spend' ? 'Period Spend' : 'Budget']}
                                        labelFormatter={(_, payload) => payload?.[0]?.payload?.fullName || ''}
                                    />
                                    <Bar isAnimationActive={false} dataKey="budget" fill="#3b82f6" stroke="#60a5fa" strokeWidth={1} name="budget" radius={[4, 4, 0, 0]} label={{ position: 'top', fill: 'var(--fg-secondary)', fontSize: 10, formatter: (v) => v > 0 ? `$${v.toFixed(0)}` : '' }} />
                                    <Bar isAnimationActive={false} dataKey="spend" name="spend" radius={[4, 4, 0, 0]}
                                        label={(props) => {
                                            if (!props.value || props.value <= 0) return null;
                                            const yPos = props.y - 5;
                                            return (
                                                <text
                                                    x={props.x + props.width / 2}
                                                    y={yPos}
                                                    fill="var(--fg-primary)"
                                                    fontSize={10}
                                                    fontWeight={600}
                                                    textAnchor="middle"
                                                >
                                                    ${props.value.toFixed(0)}
                                                </text>
                                            );
                                        }}>
                                        {barChartData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={entry.color} stroke={entry.color} strokeWidth={1} />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                </div>

                {/* Top Users by Team Budget Consumption */}
                <div style={chartCard}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                        <div style={chartTitle}>Top Users by Team Budget Consumption</div>
                        <select
                            value={selectedTeamForUsers}
                            onChange={(e) => setSelectedTeamForUsers(e.target.value)}
                            style={{
                                padding: '0.4rem 0.8rem',
                                borderRadius: 6,
                                border: '1px solid var(--border)',
                                background: 'var(--bg)',
                                color: 'var(--fg-primary)',
                                fontSize: '0.85rem',
                                cursor: 'pointer',
                                minWidth: '200px'
                            }}
                        >
                            <option value="all">Select a team...</option>
                            {visibleTeams
                                .filter(t => t.max_budget > 0)
                                .sort((a, b) => (a.team_alias || a.team_id).localeCompare(b.team_alias || b.team_id))
                                .map(t => (
                                    <option key={t.team_id} value={t.team_id}>
                                        {t.team_alias || t.team_id}
                                    </option>
                                ))
                            }
                        </select>
                    </div>
                    {topUsersPerTeam === null ? (
                        // No team selected
                        <div style={{
                            padding: '2rem',
                            textAlign: 'center',
                            color: 'var(--fg-secondary)',
                            fontSize: '0.9rem',
                            fontStyle: 'italic'
                        }}>
                            Select a team to view top users and their budget consumption
                        </div>
                    ) : topUsersPerTeam === undefined ? (
                        // Data is loading
                        <Skeleton height={280} />
                    ) : topUsersPerTeam.length === 0 ? (
                        // No data for this team
                        <div style={{
                            padding: '2rem',
                            textAlign: 'center',
                            color: 'var(--fg-secondary)',
                            fontSize: '0.9rem'
                        }}>
                            No usage data available for this team
                        </div>
                    ) : (
                        <>
                            <ResponsiveContainer width="100%" height={280}>
                                <BarChart data={topUsersPerTeam} layout="vertical" margin={{ top: 5, right: 20, bottom: 5, left: 5 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                                    <XAxis type="number" tick={{ fontSize: 10, fill: 'var(--fg-secondary)' }} tickFormatter={v => `$${v.toFixed(0)}`} />
                                    <YAxis type="category" dataKey="email" tick={{ fontSize: 9, fill: 'var(--fg-secondary)' }} width={120} />
                                    <ReTooltip
                                        contentStyle={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 6, fontSize: 11, color: 'var(--fg-primary)', padding: '8px 12px' }}
                                        labelStyle={{ color: 'var(--fg-primary)', fontWeight: '600', marginBottom: '4px' }}
                                        itemStyle={{ color: 'var(--fg-primary)' }}
                                        cursor={{ fill: 'rgba(100, 116, 139, 0.1)' }}
                                        formatter={(v, name) => {
                                            if (name === 'spend') return [fmt2(v), 'Spend'];
                                            return [v, name];
                                        }}
                                        labelFormatter={(email, payload) => {
                                            const user = payload[0]?.payload;
                                            if (!user) return email;
                                            return `${email}\n% of Budget: ${user.budgetPercent.toFixed(2)}%`;
                                        }}
                                    />
                                    <Bar isAnimationActive={false}dataKey="spend" name="spend" radius={[0, 4, 4, 0]}>
                                        {topUsersPerTeam.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={entry.color} />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                            {/* User breakdown table */}
                            <div style={{ marginTop: '1rem', overflow: 'auto', maxHeight: '300px' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                                    <thead style={{ position: 'sticky', top: 0, background: 'var(--bg)', zIndex: 1 }}>
                                        <tr style={{ borderBottom: '1px solid var(--border)' }}>
                                            {['User Email', 'Spend', '% of Budget', 'Requests', 'Tokens'].map(h => (
                                                <th key={h} style={{ textAlign: 'left', padding: '0.4rem 0.6rem', color: 'var(--muted)', fontWeight: 700, fontSize: '0.73rem', textTransform: 'uppercase' }}>{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {topUsersPerTeam.map((user, i) => (
                                            <tr
                                                key={user.email}
                                                style={{
                                                    background: i % 2 === 0 ? 'var(--bg)' : 'transparent',
                                                }}
                                            >
                                                <td style={{ padding: '0.4rem 0.6rem', color: 'var(--fg-secondary)' }}>{user.email}</td>
                                                <td style={{ padding: '0.4rem 0.6rem', color: 'var(--fg-primary)', fontWeight: 600 }}>{fmt2(user.spend)}</td>
                                                <td style={{
                                                    padding: '0.4rem 0.6rem',
                                                    color: user.budgetPercent >= 10 ? '#ef4444' : user.budgetPercent >= 5 ? '#f59e0b' : '#10b981',
                                                    fontWeight: 600
                                                }}>{user.budgetPercent.toFixed(2)}%</td>
                                                <td style={{ padding: '0.4rem 0.6rem', color: 'var(--fg-secondary)' }}>{user.requests}</td>
                                                <td style={{ padding: '0.4rem 0.6rem', color: 'var(--fg-secondary)' }}>{fmtTokens(user.tokens)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </div>

                {/* Team Details Table */}
                {visibleTeams.length > 0 && (
                    <div style={chartCard}>
                        <div style={chartTitle}>Team Details</div>
                        <div style={{ overflow: 'auto', maxHeight: '400px' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                                <thead style={{ position: 'sticky', top: 0, background: 'var(--bg)', zIndex: 1 }}>
                                    <tr style={{ borderBottom: '1px solid var(--border)' }}>
                                        {['Team Name', 'Spend', 'Budget', 'Usage %', 'Members'].map(h => (
                                            <th key={h} style={{ textAlign: 'left', padding: '0.4rem 0.6rem', color: 'var(--muted)', fontWeight: 700, fontSize: '0.73rem', textTransform: 'uppercase' }}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {visibleTeams
                                        .filter(t => t.max_budget > 0 || t.spend > 0)
                                        .sort((a, b) => (b.spend || 0) - (a.spend || 0))
                                        .map((team, i) => {
                                            const usagePercent = team.max_budget > 0 ? Math.min((team.spend / team.max_budget) * 100, 100) : 0;
                                            return (
                                                <tr
                                                    key={team.team_id}
                                                    style={{
                                                        background: i % 2 === 0 ? 'var(--bg)' : 'transparent',
                                                    }}
                                                >
                                                    <td style={{ padding: '0.4rem 0.6rem', color: 'var(--fg-secondary)' }}>{team.team_alias || team.team_id}</td>
                                                    <td style={{ padding: '0.4rem 0.6rem', color: 'var(--fg-primary)', fontWeight: 600 }}>{fmt2(team.spend || 0)}</td>
                                                    <td style={{ padding: '0.4rem 0.6rem', color: 'var(--fg-secondary)' }}>{fmt2(team.max_budget || 0)}</td>
                                                    <td style={{
                                                        padding: '0.4rem 0.6rem',
                                                        color: usagePercent >= 90 ? '#ef4444' : usagePercent >= 70 ? '#f59e0b' : '#10b981',
                                                        fontWeight: 600
                                                    }}>{usagePercent.toFixed(1)}%</td>
                                                    <td style={{ padding: '0.4rem 0.6rem', color: 'var(--fg-secondary)' }}>{team.userCount || 0}</td>
                                                </tr>
                                            );
                                        })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
                </>
                )}

                {/* MY USAGE SUB-TAB */}
                {analyticsSubTab === 'my-usage' && (() => {
                    // Build my usage directly from spendCache — not topUsersBySpend (limited to top 10)
                    const myEmail = (userEmail || '').toLowerCase();
                    const myAgg = { spend: 0, requests: 0, tokens: 0, models: {}, department: null, orgUnit: null, teams: null };
                    visibleTeams.forEach(team => {
                        const cache = spendCache[team.team_id];
                        if (!cache || !cache.data) return;
                        cache.data.forEach(user => {
                            if ((user.email || '').toLowerCase() !== myEmail) return;
                            myAgg.spend += user.spend || 0;
                            myAgg.requests += user.requests || 0;
                            myAgg.tokens += user.tokens || 0;
                            if (!myAgg.department && user.department) myAgg.department = user.department;
                            if (!myAgg.orgUnit && user.orgUnit) myAgg.orgUnit = user.orgUnit;
                            if (user.models && typeof user.models === 'object') {
                                Object.entries(user.models).forEach(([modelName, m]) => {
                                    if (!myAgg.models[modelName]) myAgg.models[modelName] = { spend: 0, tokens: 0, requests: 0 };
                                    myAgg.models[modelName].spend += m.spend || 0;
                                    myAgg.models[modelName].tokens += m.tokens || 0;
                                    myAgg.models[modelName].requests += m.requests || 0;
                                });
                            }
                        });
                    });
                    // Team memberships from topUsersBySpend (already resolved)
                    const meFromTop = topUsersBySpend.find(u => (u.email || '').toLowerCase() === myEmail);
                    myAgg.teams = meFromTop?.teams || null;
                    const hasData = myAgg.requests > 0 || myAgg.spend > 0;
                    const me = hasData ? myAgg : null;
                    const fmtDateUK = iso => {
                        if (!iso) return '';
                        const [y, m, d] = iso.split('-');
                        return `${d}/${m}/${y}`;
                    };
                    const myModels = Object.keys(myAgg.models).length > 0 ? Object.entries(myAgg.models).map(([name, m], i) => ({
                        name, displayName: resolveModelName(name), spend: m.spend || 0, tokens: m.tokens || 0, requests: m.requests || 0,
                        color: CHART_COLORS[i % CHART_COLORS.length]
                    })).sort((a, b) => b.spend - a.spend) : [];
                    return (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                            {/* Summary cards */}
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem' }}>
                                {[
                                    { label: 'My Spend', value: fmtSpendDetail(me?.spend || 0), color: '#10b981' },
                                    { label: 'Requests', value: (me?.requests || 0).toLocaleString(), color: '#6366f1' },
                                    { label: 'Tokens Used', value: fmtTokens(me?.tokens || 0), color: '#f59e0b' },
                                    { label: 'Models Used', value: myModels.length, color: '#38bdf8' },
                                ].map(card => (
                                    <div key={card.label} style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, padding: '1rem 1.25rem' }}>
                                        <div style={{ fontSize: '0.75rem', color: 'var(--muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.35rem' }}>{card.label}</div>
                                        <div style={{ fontSize: '1.5rem', fontWeight: 700, color: card.color }}>{card.value}</div>
                                    </div>
                                ))}
                            </div>
                            {/* Department + team context */}
                            {me && (
                                <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, padding: '0.75rem 1.25rem', fontSize: '0.85rem', color: 'var(--fg-secondary)', display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
                                    <span>Organisation: <strong style={{ color: 'var(--fg-primary)' }}>{me.orgUnit || '—'}</strong></span>
                                    <span>Department: <strong style={{ color: 'var(--fg-primary)' }}>{me.department || '—'}</strong></span>
                                    <span>Teams: <strong style={{ color: 'var(--fg-primary)' }}>{me.teams || '—'}</strong></span>
                                    <span>Period: <strong style={{ color: 'var(--fg-primary)' }}>{analyticsTimeRange === 'all_time' ? 'All Time' : `${fmtDateUK(analyticsDateRange.start)} – ${fmtDateUK(analyticsDateRange.end)}`}</strong></span>
                                </div>
                            )}
                            {/* Models breakdown */}
                            {myModels.length > 0 && (
                                <div style={chartCard}>
                                    <div style={chartTitle}>My Usage by Model</div>
                                    <ResponsiveContainer width="100%" height={Math.max(160, myModels.length * 40)}>
                                        <BarChart data={myModels} layout="vertical" margin={{ top: 5, right: 80, bottom: 5, left: 10 }}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                                            <XAxis type="number" tick={{ fontSize: 10, fill: 'var(--fg-secondary)' }} tickFormatter={v => `$${v.toFixed(2)}`} />
                                            <YAxis type="category" dataKey="displayName" tick={{ fontSize: 11, fill: 'var(--fg-secondary)' }} width={160} />
                                            <ReTooltip
                                                contentStyle={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 6, fontSize: 12, color: 'var(--fg-primary)', padding: '8px 12px' }}
                                                labelStyle={{ color: 'var(--fg-primary)', fontWeight: '600', marginBottom: '4px' }}
                                                itemStyle={{ color: 'var(--fg-primary)' }}
                                                cursor={{ fill: 'rgba(255,255,255,0.05)' }}
                                                formatter={(v, name) => [name === 'spend' ? fmt2(v) : name === 'tokens' ? fmtTokens(v) : v, name]}
                                            />
                                            <Bar isAnimationActive={false} dataKey="spend" radius={[0, 4, 4, 0]} label={{ position: 'right', fill: 'var(--fg-secondary)', fontSize: 10, formatter: v => v > 0 ? fmt2(v) : '' }}>
                                                {myModels.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={entry.color} />
                                                ))}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                    <div style={{ marginTop: '1rem', overflow: 'auto' }}>
                                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                                            <thead>
                                                <tr style={{ borderBottom: '1px solid var(--border)' }}>
                                                    {['Model', 'Requested via', 'Requests', 'Tokens', 'Spend'].map(h => (
                                                        <th key={h} style={{ textAlign: 'left', padding: '0.4rem 0.6rem', color: 'var(--muted)', fontWeight: 700, fontSize: '0.73rem', textTransform: 'uppercase' }}>{h}</th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {myModels.map((m, i) => {
                                                    const requestedVia = myModelRouting[m.name];
                                                    return (
                                                    <tr key={m.name} style={{ background: i % 2 === 0 ? 'var(--bg)' : 'transparent', borderBottom: '1px solid var(--surface-alt)' }}>
                                                        <td style={{ padding: '0.45rem 0.6rem', color: 'var(--fg-primary)', fontWeight: 600 }}>{m.displayName}</td>
                                                        <td style={{ padding: '0.45rem 0.6rem' }}>
                                                            {requestedVia
                                                                ? <span style={{ fontSize: '0.72rem', fontWeight: 700, background: 'var(--accent-bg)', color: '#818cf8', border: '1px solid rgba(99,102,241,0.35)', borderRadius: 4, padding: '1px 6px' }}>{resolveModelName(requestedVia)}</span>
                                                                : <span style={{ color: 'var(--muted)', fontSize: '0.82rem' }}>Direct</span>
                                                            }
                                                        </td>
                                                        <td style={{ padding: '0.45rem 0.6rem', color: 'var(--fg-secondary)' }}>{m.requests.toLocaleString()}</td>
                                                        <td style={{ padding: '0.45rem 0.6rem', color: 'var(--fg-secondary)' }}>{fmtTokens(m.tokens)}</td>
                                                        <td style={{ padding: '0.45rem 0.6rem', color: '#10b981', fontWeight: 600 }}>{fmtSpendDetail(m.spend)}</td>
                                                    </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                            {!me && (
                                <div style={{ color: 'var(--border)', textAlign: 'center', padding: '3rem 0', fontSize: '0.9rem' }}>
                                    No usage data found for {userEmail} in this period.
                                </div>
                            )}
                        </div>
                    );
                })()}

                {/* MY TEAM SUB-TAB (managers + admins) */}
                {analyticsSubTab === 'my-team' && (() => {
                    // Include the manager themselves alongside their direct reports
                    const teamMembers = myDirectReports.some(r => r.email.toLowerCase() === userEmail.toLowerCase())
                        ? myDirectReports
                        : [{ email: userEmail, displayName: userEmail, orgUnit: '', department: '' }, ...myDirectReports];
                    const directReportEmails = new Set(teamMembers.map(r => r.email.toLowerCase()));
                    // Aggregate spend for each team member (manager + direct reports) from spendCache
                    const memberMap = {};
                    teamMembers.forEach(r => {
                        memberMap[r.email.toLowerCase()] = { email: r.email, displayName: r.displayName || r.email, orgUnit: r.orgUnit || '—', department: r.department || '—', spend: 0, requests: 0, tokens: 0, models: {} };
                    });
                    visibleTeams.forEach(team => {
                        const cache = spendCache[team.team_id];
                        if (!cache || !cache.data) return;
                        cache.data.forEach(user => {
                            const key = (user.email || '').toLowerCase();
                            if (!directReportEmails.has(key)) return;
                            const m = memberMap[key];
                            if (!m) return;
                            m.spend += user.spend || 0;
                            m.requests += user.requests || 0;
                            m.tokens += user.tokens || 0;
                            if (user.models && typeof user.models === 'object') {
                                Object.entries(user.models).forEach(([modelName, md]) => {
                                    if (!m.models[modelName]) m.models[modelName] = { spend: 0, tokens: 0, requests: 0 };
                                    m.models[modelName].spend += md.spend || 0;
                                    m.models[modelName].tokens += md.tokens || 0;
                                    m.models[modelName].requests += md.requests || 0;
                                });
                            }
                        });
                    });
                    const memberRows = Object.values(memberMap).sort((a, b) => b.spend - a.spend);
                    const activeRows = memberRows.filter(m => m.requests > 0 || m.spend > 0);
                    const totalSpend = memberRows.reduce((s, m) => s + m.spend, 0);
                    const totalRequests = memberRows.reduce((s, m) => s + m.requests, 0);
                    const totalTokens = memberRows.reduce((s, m) => s + m.tokens, 0);
                    const fmtDateUK = iso => { if (!iso) return ''; const [y, mo, d] = iso.split('-'); return `${d}/${mo}/${y}`; };
                    const handleSync = async () => {
                        if (!onSyncDepartments) return;
                        setMyTeamSyncing(true);
                        setMyTeamSyncError(null);
                        try {
                            await onSyncDepartments();
                            setMyTeamSyncDone(true);
                        } catch (e) {
                            setMyTeamSyncError('Sync failed. Please try again.');
                        } finally {
                            setMyTeamSyncing(false);
                        }
                    };
                    return (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                            {/* Summary KPI cards */}
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem' }}>
                                {[
                                    { label: 'Team Members', value: teamMembers.length },
                                    { label: 'Active This Period', value: activeRows.length },
                                    { label: 'Team Spend', value: fmt2(totalSpend), color: '#10b981' },
                                    { label: 'Total Requests', value: totalRequests.toLocaleString(), color: '#6366f1' },
                                    { label: 'Total Tokens', value: fmtTokens(totalTokens), color: '#f59e0b' },
                                ].map(card => (
                                    <div key={card.label} style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, padding: '1rem 1.25rem' }}>
                                        <div style={{ fontSize: '0.75rem', color: 'var(--muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.35rem' }}>{card.label}</div>
                                        <div style={{ fontSize: '1.5rem', fontWeight: 700, color: card.color || 'var(--fg-primary)' }}>{card.value}</div>
                                    </div>
                                ))}
                            </div>
                            {/* Period context bar */}
                            <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, padding: '0.75rem 1.25rem', fontSize: '0.85rem', color: 'var(--fg-secondary)', display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
                                <span>Period: <strong style={{ color: 'var(--fg-primary)' }}>{analyticsTimeRange === 'all_time' ? 'All Time' : `${fmtDateUK(analyticsDateRange.start)} – ${fmtDateUK(analyticsDateRange.end)}`}</strong></span>
                            </div>
                            {/* Spend by member bar chart */}
                            {memberRows.length > 0 && (() => {
                                const chartData = memberRows.map((m, i) => ({
                                    name: m.displayName,
                                    spend: m.spend,
                                    tokens: m.tokens,
                                    requests: m.requests,
                                    color: CHART_COLORS[i % CHART_COLORS.length],
                                }));
                                return (
                                    <div style={chartCard}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                                            <div style={chartTitle}>Spend by Team Member</div>
                                        </div>
                                        <ResponsiveContainer width="100%" height={Math.max(200, chartData.length * 44)}>
                                            <BarChart data={chartData} layout="vertical" margin={{ top: 5, right: 80, bottom: 5, left: 10 }}>
                                                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                                                <XAxis type="number" tick={{ fontSize: 10, fill: 'var(--fg-secondary)' }} tickFormatter={v => `$${v.toFixed(2)}`} />
                                                <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: 'var(--fg-secondary)' }} width={140} />
                                                <ReTooltip
                                                    contentStyle={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 6, fontSize: 12, color: 'var(--fg-primary)', padding: '8px 12px' }}
                                                    labelStyle={{ color: 'var(--fg-primary)', fontWeight: '600', marginBottom: '4px' }}
                                                    itemStyle={{ color: 'var(--fg-primary)' }}
                                                    cursor={{ fill: 'rgba(255,255,255,0.05)' }}
                                                    formatter={(v, name) => {
                                                        if (name === 'spend') return [fmt2(v), 'Spend'];
                                                        if (name === 'tokens') return [fmtTokens(v), 'Tokens'];
                                                        return [v.toLocaleString(), 'Requests'];
                                                    }}
                                                />
                                                <Bar isAnimationActive={false} dataKey="spend" radius={[0, 4, 4, 0]} label={{ position: 'right', fill: 'var(--fg-secondary)', fontSize: 10, formatter: v => v > 0 ? fmt2(v) : '' }}>
                                                    {chartData.map((entry, index) => (
                                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                                    ))}
                                                </Bar>
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>
                                );
                            })()}
                            {/* Member breakdown table */}
                            <div style={chartCard}>
                                <div style={chartTitle}>Team Member Breakdown</div>
                                {memberRows.length === 0
                                    ? (
                                        <div style={{ textAlign: 'center', padding: '2rem 0' }}>
                                            <div style={{ color: 'var(--muted)', fontSize: '0.85rem', marginBottom: '1rem' }}>
                                                {myTeamSyncDone
                                                    ? 'No direct reports found. You have no team members in this organisation\'s directory.'
                                                    : 'No direct reports found — a department sync is needed to load your team.'
                                                }
                                            </div>
                                            {onSyncDepartments && !myTeamSyncDone && (
                                                <>
                                                    <button
                                                        className="stat-card-action"
                                                        onClick={handleSync}
                                                        disabled={myTeamSyncing}
                                                        style={{ margin: '0 auto' }}
                                                    >
                                                        {myTeamSyncing ? 'Syncing...' : 'Sync Departments'}
                                                    </button>
                                                    {myTeamSyncError && <div style={{ color: 'var(--critical)', fontSize: '0.8rem', marginTop: '0.5rem' }}>{myTeamSyncError}</div>}
                                                </>
                                            )}
                                        </div>
                                    )
                                    : (
                                        <div style={{ overflow: 'auto', marginTop: '0.75rem' }}>
                                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                                                <thead style={{ position: 'sticky', top: 0, background: 'var(--bg)', zIndex: 1 }}>
                                                    <tr style={{ borderBottom: '1px solid var(--border)' }}>
                                                        {['Name', 'Org Unit', 'Department', 'Spend', 'Tokens', 'Requests', 'Top Model'].map(h => (
                                                            <th key={h} style={{ textAlign: 'left', padding: '0.4rem 0.6rem', color: 'var(--muted)', fontWeight: 700, fontSize: '0.73rem', textTransform: 'uppercase' }}>{h}</th>
                                                        ))}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {memberRows.map((m, i) => {
                                                        const topModel = Object.entries(m.models).sort((a, b) => b[1].spend - a[1].spend)[0];
                                                        return (
                                                            <tr key={m.email} style={{ background: i % 2 === 0 ? 'var(--bg)' : 'transparent', borderBottom: '1px solid var(--surface-alt)' }}>
                                                                <td style={{ padding: '0.45rem 0.6rem' }}>
                                                                    <div style={{ color: 'var(--fg-primary)', fontWeight: 600, fontSize: '0.82rem' }}>{m.displayName}</div>
                                                                    <div style={{ color: 'var(--muted)', fontSize: '0.73rem' }}>{m.email}</div>
                                                                </td>
                                                                <td style={{ padding: '0.45rem 0.6rem', color: 'var(--fg-secondary)' }}>{m.orgUnit}</td>
                                                                <td style={{ padding: '0.45rem 0.6rem', color: 'var(--fg-secondary)' }}>{m.department}</td>
                                                                <td style={{ padding: '0.45rem 0.6rem', color: '#10b981', fontWeight: 600 }}>{fmtSpendDetail(m.spend)}</td>
                                                                <td style={{ padding: '0.45rem 0.6rem', color: 'var(--fg-secondary)' }}>{fmtTokens(m.tokens)}</td>
                                                                <td style={{ padding: '0.45rem 0.6rem', color: 'var(--fg-secondary)' }}>{m.requests.toLocaleString()}</td>
                                                                <td style={{ padding: '0.45rem 0.6rem', color: 'var(--fg-secondary)' }}>{topModel ? resolveModelName(topModel[0]) : '—'}</td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    )
                                }
                            </div>
                        </div>
                    );
                })()}

                {/* ORGANISATIONS SUB-TAB (admin only) */}
                {analyticsSubTab === 'organisations' && isAdmin && (() => {
                    const totalSpend = aggregatedOrgUnits.reduce((s, o) => s + o.spend, 0);
                    const hasOuData = aggregatedOrgUnits.length > 0;
                    const ouChartData = aggregatedOrgUnits.map((o, i) => ({
                        ...o,
                        name: o.orgUnit,
                        color: CHART_COLORS[i % CHART_COLORS.length]
                    }));
                    return (
                        <>
                        <div style={chartCard}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                                <div style={chartTitle}>Spend by Organisation</div>
                            </div>
                            {!hasOuData
                                ? <div style={{ color: 'var(--border)', fontSize: '0.85rem', padding: '2rem 0', textAlign: 'center' }}>No organisation data yet — sync departments from MS365 to populate organisation data.</div>
                                : (
                                    <ResponsiveContainer width="100%" height={Math.max(200, ouChartData.length * 40)}>
                                        <BarChart
                                            data={ouChartData}
                                            layout="vertical"
                                            margin={{ top: 5, right: 60, bottom: 5, left: 10 }}
                                            isAnimationActive={false}
                                        >
                                            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                                            <XAxis type="number" tick={{ fontSize: 10, fill: 'var(--fg-secondary)' }} tickFormatter={v => `$${v.toFixed(2)}`} />
                                            <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: 'var(--fg-secondary)' }} width={120} />
                                            <ReTooltip
                                                contentStyle={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 6, fontSize: 12, color: 'var(--fg-primary)', padding: '8px 12px' }}
                                                labelStyle={{ color: 'var(--fg-primary)', fontWeight: '600', marginBottom: '4px' }}
                                                itemStyle={{ color: 'var(--fg-primary)' }}
                                                cursor={{ fill: 'rgba(255,255,255,0.05)' }}
                                                formatter={(v) => [fmt2(v), 'Spend']}
                                            />
                                            <Bar isAnimationActive={false} dataKey="spend" radius={[0, 4, 4, 0]} label={{ position: 'right', fill: 'var(--fg-secondary)', fontSize: 10, formatter: v => v > 0 ? fmt2(v) : '' }}>
                                                {ouChartData.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={entry.color} />
                                                ))}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                )}
                        </div>

                        {hasOuData && (
                            <div style={chartCard}>
                                <div style={{ marginBottom: '0.75rem' }}>
                                    <div style={chartTitle}>Organisation Breakdown</div>
                                </div>
                                <div style={{ overflow: 'auto' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                                        <thead style={{ position: 'sticky', top: 0, background: 'var(--bg)', zIndex: 1 }}>
                                            <tr style={{ borderBottom: '1px solid var(--border)' }}>
                                                {['Organisation', 'Users', 'Requests', 'Tokens', 'Spend', '% of Total'].map(h => (
                                                    <th key={h} style={{ textAlign: 'left', padding: '0.4rem 0.6rem', color: 'var(--muted)', fontWeight: 700, fontSize: '0.73rem', textTransform: 'uppercase' }}>{h}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {aggregatedOrgUnits.map((o, i) => (
                                                <tr
                                                    key={o.orgUnit}
                                                    style={{ background: i % 2 === 0 ? 'var(--bg)' : 'transparent', borderBottom: '1px solid var(--surface-alt)' }}
                                                >
                                                    <td style={{ padding: '0.45rem 0.6rem', color: 'var(--fg-primary)', fontWeight: 600 }}>{o.orgUnit}</td>
                                                    <td style={{ padding: '0.45rem 0.6rem', color: 'var(--fg-secondary)' }}>{o.users}</td>
                                                    <td style={{ padding: '0.45rem 0.6rem', color: 'var(--fg-secondary)' }}>{o.requests.toLocaleString()}</td>
                                                    <td style={{ padding: '0.45rem 0.6rem', color: 'var(--fg-secondary)' }}>{fmtTokens(o.tokens)}</td>
                                                    <td style={{ padding: '0.45rem 0.6rem', color: '#10b981', fontWeight: 600 }}>{fmt2(o.spend)}</td>
                                                    <td style={{ padding: '0.45rem 0.6rem', color: 'var(--fg-secondary)' }}>
                                                        {totalSpend > 0 ? `${((o.spend / totalSpend) * 100).toFixed(1)}%` : '—'}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                        </>
                    );
                })()}

                {/* OVERVIEW SUB-TAB (admin only) — new KPI dashboard */}
                {analyticsSubTab === 'overview' && isAdmin && renderAnalyticsDashboard()}

                {/* TEAM BUDGETS SUB-TAB (admin only) — old overview content */}
                {analyticsSubTab === 'team-budgets' && isAdmin && renderOverview()}

                {/* REPORTS SUB-TAB */}
                {analyticsSubTab === 'reports' && renderReport()}

                {/* ADMINS SUB-TAB (admin only) */}
                {analyticsSubTab === 'admins' && isAdmin && (() => {
                    const nonAdminUsers = topUsersBySpend
                        .map(u => u.email)
                        .filter(e => e && !adminList.includes(e.toLowerCase()));

                    const handleAddAdmin = async () => {
                        const email = adminAddEmail.trim().toLowerCase();
                        if (!email) return;
                        setAdminAddLoading(true);
                        setAdminAddError('');
                        try {
                            const result = await addAdminUser(email);
                            if (result.success) {
                                setAdminList(result.admins || [...adminList, email]);
                                if (result.owner !== undefined) setAdminOwner(result.owner);
                                setAdminAddEmail('');
                            } else {
                                setAdminAddError(result.error || 'Failed to add admin');
                            }
                        } catch (err) {
                            setAdminAddError(err.message || 'Failed to add admin');
                        } finally {
                            setAdminAddLoading(false);
                        }
                    };

                    const handleRemoveAdmin = async (email) => {
                        setAdminRemovingEmail(email);
                        setAdminAddError('');
                        try {
                            const result = await removeAdminUser(email);
                            if (result.success) {
                                setAdminList(result.admins || adminList.filter(a => a !== email));
                                if (result.owner !== undefined) setAdminOwner(result.owner);
                            } else {
                                setAdminAddError(result.error || 'Failed to remove admin');
                            }
                        } catch (err) {
                            setAdminAddError(err.message || 'Failed to remove admin');
                        } finally {
                            setAdminRemovingEmail(null);
                        }
                    };

                    return (
                        <div style={{ padding: '1.5rem 0' }}>
                            <div style={{ marginBottom: '1.5rem' }}>
                                <h3 style={{ color: 'var(--fg-primary)', fontSize: '1rem', fontWeight: 700, margin: '0 0 0.25rem 0' }}>Admin Users</h3>
                                <p style={{ color: 'var(--muted)', fontSize: '0.82rem', margin: 0 }}>
                                    Admins have access to all Analytics tabs. The Owner account cannot be removed.
                                </p>
                            </div>

                            {/* Current admin list */}
                            <div style={{ background: 'var(--bg)', borderRadius: '0.5rem', border: '1px solid var(--surface-alt)', marginBottom: '1.5rem', overflow: 'hidden' }}>
                                {adminList.length === 0 ? (
                                    <div style={{ padding: '1.5rem', color: 'var(--border)', textAlign: 'center', fontSize: '0.85rem' }}>No admins configured.</div>
                                ) : (
                                    adminList.map((email, i) => {
                                        const isOwnerEmail = email === adminOwner;
                                        const isSelf = email === userEmail?.toLowerCase();
                                        const isRemoving = adminRemovingEmail === email;
                                        const cannotRemove = isOwnerEmail || isSelf;
                                        return (
                                            <div key={email} style={{
                                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                                padding: '0.65rem 1rem',
                                                borderBottom: i < adminList.length - 1 ? '1px solid var(--surface-alt)' : 'none',
                                                background: i % 2 === 0 ? 'transparent' : 'var(--surface-alt)'
                                            }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                                                    <span style={{ color: 'var(--fg-primary)', fontSize: '0.85rem' }}>{email}</span>
                                                    {isOwnerEmail && (
                                                        <span style={{ fontSize: '0.7rem', background: 'var(--warning-bg-hover)', color: '#fbbf24', borderRadius: '0.25rem', padding: '0.1rem 0.4rem', fontWeight: 600 }}>Owner</span>
                                                    )}
                                                    {isSelf && (
                                                        <span style={{ fontSize: '0.7rem', background: 'var(--success-bg)', color: 'var(--success)', borderRadius: '0.25rem', padding: '0.1rem 0.4rem', fontWeight: 600 }}>You</span>
                                                    )}
                                                </div>
                                                <button
                                                    disabled={cannotRemove || isRemoving}
                                                    onClick={() => handleRemoveAdmin(email)}
                                                    style={{
                                                        background: 'none', border: '1px solid var(--border)', borderRadius: '0.3rem',
                                                        color: cannotRemove ? 'var(--border)' : '#ef4444',
                                                        cursor: cannotRemove ? 'not-allowed' : 'pointer',
                                                        fontSize: '0.75rem', padding: '0.25rem 0.6rem',
                                                        opacity: cannotRemove ? 0.4 : 1
                                                    }}
                                                >
                                                    {isRemoving ? 'Removing...' : 'Remove'}
                                                </button>
                                            </div>
                                        );
                                    })
                                )}
                            </div>

                            {/* Add admin form — searchable combobox from LiteLLM users (same source as dashboard) */}
                            {(() => {
                                const filteredUsers = users
                                    .filter(u => u.email)
                                    .filter(u =>
                                        !adminList.includes(u.email.toLowerCase()) &&
                                        u.email.toLowerCase().includes(adminSearchQuery.toLowerCase())
                                    ).slice(0, 40);
                                return (
                                    <div style={{ background: 'var(--bg)', borderRadius: '0.5rem', border: '1px solid var(--surface-alt)', padding: '1rem' }}>
                                        <h4 style={{ color: 'var(--fg-secondary)', fontSize: '0.82rem', fontWeight: 700, margin: '0 0 0.75rem 0', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Add Admin</h4>
                                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'flex-start' }}>
                                            <div style={{ flex: 1, minWidth: 240, position: 'relative' }}>
                                                <input
                                                    type="text"
                                                    placeholder="Search by email..."
                                                    value={adminAddEmail || adminSearchQuery}
                                                    onChange={e => {
                                                        setAdminSearchQuery(e.target.value);
                                                        setAdminAddEmail('');
                                                        setAdminDropdownOpen(true);
                                                    }}
                                                    onFocus={() => setAdminDropdownOpen(true)}
                                                    onBlur={() => setTimeout(() => setAdminDropdownOpen(false), 150)}
                                                    style={{
                                                        width: '100%', background: 'var(--bg)', color: 'var(--fg-primary)',
                                                        border: '1px solid var(--border)', borderRadius: '0.375rem',
                                                        padding: '0.5rem 0.75rem', fontSize: '0.85rem',
                                                        outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box'
                                                    }}
                                                />
                                                {adminDropdownOpen && (adminSearchQuery.length > 0 || adminAddEmail.length === 0) && filteredUsers.length > 0 && (
                                                    <div style={{
                                                        position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
                                                        background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: '0.375rem',
                                                        maxHeight: 220, overflowY: 'auto', boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
                                                        marginTop: 2
                                                    }}>
                                                        {filteredUsers.map(u => (
                                                            <div
                                                                key={u.email}
                                                                onMouseDown={() => {
                                                                    setAdminAddEmail(u.email);
                                                                    setAdminSearchQuery('');
                                                                    setAdminDropdownOpen(false);
                                                                }}
                                                                style={{
                                                                    padding: '0.5rem 0.75rem', cursor: 'pointer',
                                                                    borderBottom: '1px solid #0f172a',
                                                                    fontSize: '0.82rem',
                                                                }}
                                                                onMouseEnter={e => e.currentTarget.style.background = 'var(--bg)'}
                                                                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                                                            >
                                                                <div style={{ color: 'var(--fg-primary)', fontWeight: 600 }}>{u.email}</div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                            <button
                                                onClick={handleAddAdmin}
                                                disabled={adminAddLoading || !adminAddEmail.trim()}
                                                style={{
                                                    background: adminAddLoading || !adminAddEmail.trim() ? 'var(--bg)' : '#2563eb',
                                                    color: adminAddLoading || !adminAddEmail.trim() ? 'var(--border)' : '#fff',
                                                    border: 'none', borderRadius: '0.375rem',
                                                    padding: '0.5rem 1.25rem', fontSize: '0.85rem', fontWeight: 600,
                                                    cursor: adminAddLoading || !adminAddEmail.trim() ? 'not-allowed' : 'pointer',
                                                    whiteSpace: 'nowrap'
                                                }}
                                            >
                                                {adminAddLoading ? 'Adding...' : 'Add Admin'}
                                            </button>
                                        </div>
                                        {adminAddEmail && (
                                            <p style={{ color: 'var(--fg-secondary)', fontSize: '0.78rem', margin: '0.4rem 0 0 0' }}>
                                                Selected: <strong style={{ color: 'var(--fg-primary)' }}>{adminAddEmail}</strong>
                                            </p>
                                        )}
                                        {adminAddError && (
                                            <p style={{ color: '#f87171', fontSize: '0.8rem', margin: '0.5rem 0 0 0' }}>{adminAddError}</p>
                                        )}
                                    </div>
                                );
                            })()}
                        </div>
                    );
                })()}

            </div>
        );
    };

    const renderReport = () => {
        // ── helpers ────────────────────────────────────────────────────────────
        const today = new Date();
        const thisMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
        const todayStr = today.toISOString().split('T')[0];
        const thisMonthStartStr = thisMonthStart.toISOString().split('T')[0];

        const setReportPresetRange = (preset) => {
            if (preset === 'today') {
                setReportDateFrom(todayStr);
                setReportDateTo(todayStr);
                setAnalyticsDateFrom(todayStr);
                setAnalyticsDateTo(todayStr);
                setAnalyticsTimeRange('custom');
            } else if (preset === 'last7') {
                const d = new Date(today); d.setDate(d.getDate() - 7);
                const from = d.toISOString().split('T')[0];
                setReportDateFrom(from);
                setReportDateTo(todayStr);
                setAnalyticsDateFrom(from);
                setAnalyticsDateTo(todayStr);
                setAnalyticsTimeRange('custom');
            } else if (preset === 'last30') {
                const d = new Date(today); d.setDate(d.getDate() - 30);
                const from = d.toISOString().split('T')[0];
                setReportDateFrom(from);
                setReportDateTo(todayStr);
                setAnalyticsDateFrom(from);
                setAnalyticsDateTo(todayStr);
                setAnalyticsTimeRange('custom');
            } else if (preset === 'this_month') {
                setReportDateFrom(thisMonthStartStr);
                setReportDateTo(todayStr);
                setAnalyticsDateFrom(thisMonthStartStr);
                setAnalyticsDateTo(todayStr);
                setAnalyticsTimeRange('custom');
            }
        };

        const applyPreset = (type) => {
            setReportType(type);
            setReportPresetRange('this_month');
            setReportPreviewData(null);
        };

        const fmtDateLabel = (s) => {
            if (!s) return 'today';
            const d = new Date(s);
            return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`;
        };
        const dateRangeLabel = reportDateFrom
            ? `${fmtDateLabel(reportDateFrom)} — ${fmtDateLabel(reportDateTo || todayStr)}`
            : 'Current period';

        // ── data accuracy fix: only teams with an OpenWebUI group ──────────────
        const teamsForReport = (() => {
            const base = visibleTeams.filter(t => t.groupId !== null);
            if (reportTeams.size === 0) return base;
            return base.filter(t => reportTeams.has(t.team_id));
        })();

        // ── dept / org unit filters ────────────────────────────────────────────
        const deptsForReport = reportDepts.size === 0
            ? (aggregatedDepartments || [])
            : (aggregatedDepartments || []).filter(d => reportDepts.has(d.department));
        const orgUnitsForReport = reportOrgUnits.size === 0
            ? (aggregatedOrgUnits || [])
            : (aggregatedOrgUnits || []).filter(o => reportOrgUnits.has(o.orgUnit));

        // ── per-user aggregation ───────────────────────────────────────────────
        const perUserRows = (() => {
            const userMap = {};
            teamsForReport.forEach(t => {
                const cache = spendCache[t.team_id];
                (cache?.data || []).forEach(u => {
                    // Apply dept/org filters if active
                    if (reportDepts.size > 0 && !reportDepts.has(u.department || 'Unknown')) return;
                    if (reportOrgUnits.size > 0 && !reportOrgUnits.has(u.orgUnit || 'Unknown')) return;
                    if (!userMap[u.email]) {
                        userMap[u.email] = {
                            email: u.email,
                            department: u.department || 'Unknown',
                            orgUnit: u.orgUnit || null,
                            teams: [],
                            spend: 0, tokens: 0, requests: 0,
                            models: {}
                        };
                    }
                    const row = userMap[u.email];
                    if (!row.teams.includes(t.team_alias || t.team_id)) row.teams.push(t.team_alias || t.team_id);
                    row.spend += u.spend || 0;
                    row.tokens += u.tokens || 0;
                    row.requests += u.requests || 0;
                    Object.entries(u.models || {}).forEach(([m, v]) => {
                        if (!row.models[m]) row.models[m] = { spend: 0, requests: 0, tokens: 0 };
                        row.models[m].spend += v.spend || 0;
                        row.models[m].requests += v.requests || 0;
                        row.models[m].tokens += v.tokens || 0;
                    });
                });
            });
            return Object.values(userMap).sort((a, b) => b.spend - a.spend);
        })();

        // ── my-usage data ──────────────────────────────────────────────────────
        const myEmail = (userEmail || '').toLowerCase();
        const myData = (() => {
            const agg = { email: myEmail, spend: 0, tokens: 0, requests: 0, models: {}, teams: [] };
            teamsForReport.forEach(t => {
                const cache = spendCache[t.team_id];
                const u = (cache?.data || []).find(u => (u.email || '').toLowerCase() === myEmail);
                if (!u) return;
                agg.spend += u.spend || 0;
                agg.tokens += u.tokens || 0;
                agg.requests += u.requests || 0;
                if (!agg.teams.includes(t.team_alias || t.team_id)) agg.teams.push(t.team_alias || t.team_id);
                Object.entries(u.models || {}).forEach(([m, v]) => {
                    if (!agg.models[m]) agg.models[m] = { spend: 0, requests: 0, tokens: 0 };
                    agg.models[m].spend += v.spend || 0;
                    agg.models[m].requests += v.requests || 0;
                    agg.models[m].tokens += v.tokens || 0;
                });
            });
            return agg;
        })();
        const myModels = Object.entries(myData.models).map(([name, m]) => ({ name, spend: m.spend, requests: m.requests || 0, tokens: m.tokens || 0 })).sort((a, b) => b.spend - a.spend);

        // ── report type config ─────────────────────────────────────────────────
        const REPORT_DEFS = [
            { id: 'my-usage',    label: 'My Usage',                adminOnly: false, managerOnly: false },
            { id: 'my-team',     label: 'My Team Report',          adminOnly: false, managerOnly: true  },
            { id: 'team-budget', label: 'Team Budget Summary',      adminOnly: true,  managerOnly: false },
            { id: 'cost-by-dept',label: 'Cost by Department',       adminOnly: true,  managerOnly: false },
            { id: 'cost-by-org', label: 'Cost by Organisation',     adminOnly: true,  managerOnly: false },
            { id: 'per-user',    label: 'Per-User Breakdown',       adminOnly: true,  managerOnly: false },
            { id: 'management',  label: 'Full Management Report',   adminOnly: true,  managerOnly: false },
            { id: 'billing',     label: 'Billing Report',           adminOnly: true,  managerOnly: false },
        ];
        const visibleReportDefs = REPORT_DEFS.filter(d => {
            if (d.adminOnly && !isAdmin) return false;
            if (d.managerOnly && (!isManager || myDirectReports.length === 0)) return false;
            return true;
        });
        const activeReportDef = REPORT_DEFS.find(d => d.id === reportType) || null;
        const showTeamFilter = isAdmin && (reportType === 'team-budget' || reportType === 'per-user' || reportType === 'management');
        const showCsvBtn = (isAdmin || reportType === 'my-team' || reportType === 'my-usage') && reportType;

        // ── preview helpers ────────────────────────────────────────────────────
        const PreviewTable = ({ headers, rows, emptyMsg }) => (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                <thead>
                    <tr style={{ background: '#1e293b' }}>
                        {headers.map((h, i) => (
                            <th key={i} style={{ padding: '6px 8px', textAlign: 'left', color: '#e2e8f0', fontWeight: 600, fontSize: 10, textTransform: 'uppercase', borderBottom: '1px solid #334155' }}>{h}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.length === 0
                        ? <tr><td colSpan={headers.length} style={{ padding: '16px 8px', textAlign: 'center', color: '#64748b', fontSize: 11 }}>{emptyMsg || 'No data'}</td></tr>
                        : rows.map((row, i) => (
                            <tr key={i} style={{ background: i % 2 === 0 ? '#ffffff' : '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                                {row.map((cell, j) => (
                                    <td key={j} style={{ padding: '5px 8px', color: '#1e293b', fontSize: 11 }}>{cell}</td>
                                ))}
                            </tr>
                        ))
                    }
                </tbody>
            </table>
        );

        const SectionHeader = ({ title, subtitle }) => (
            <div style={{ padding: '10px 16px', background: '#f8fafc', borderLeft: '3px solid #C4261F', marginBottom: 8 }}>
                <div style={{ fontWeight: 700, fontSize: 12, color: '#1e293b' }}>{title}</div>
                {subtitle && <div style={{ fontSize: 10, color: '#64748b', marginTop: 2 }}>{subtitle}</div>}
            </div>
        );

        // ── build preview ──────────────────────────────────────────────────────
        const buildPreview = () => {
            if (!reportType) return null;

            const previewHeader = (
                <div style={{ background: 'var(--surface)', padding: '20px 24px', borderRadius: '8px 8px 0 0' }}>
                    <img src="/AwareAI-logo.png" alt="AwareAI" style={{ height: 28, marginBottom: 8 }} />
                    <div style={{ color: 'var(--fg-primary)', fontSize: 16, fontWeight: 700 }}>{activeReportDef ? activeReportDef.label : 'Report'}</div>
                    <div style={{ color: 'var(--fg-secondary)', fontSize: 11, marginTop: 4 }}>{dateRangeLabel}</div>
                </div>
            );

            if (reportType === 'management') {
                const userCapN = mgmtUserCap === 'all' ? Infinity : parseInt(mgmtUserCap, 10);
                const modelCapN = mgmtModelCap === 'all' ? Infinity : parseInt(mgmtModelCap, 10);
                const cappedUsers = userCapN === Infinity ? perUserRows : perUserRows.slice(0, userCapN);
                const cappedModels = modelCapN === Infinity ? (aggregatedModels || []) : (aggregatedModels || []).slice(0, modelCapN);
                const totalOrgSpend = orgUnitsForReport.reduce((s, o) => s + o.spend, 0);
                const totalDeptSpend = deptsForReport.reduce((s, d) => s + d.spend, 0);
                const totalModelSpend = (aggregatedModels || []).reduce((s, m) => s + m.spend, 0);
                return (
                    <div style={{ background: '#ffffff', borderRadius: 8, overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,0.3)' }}>
                        {previewHeader}
                        <div style={{ padding: '16px 20px', color: '#1e293b', fontSize: 13 }}>
                            {mgmtSections.execSummary && prediction && (
                                <div style={{ marginBottom: 20 }}>
                                    <SectionHeader title="Executive Summary" />
                                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                                        {[
                                            { label: 'Total Spend', value: fmt2(prediction.currentSpend || 0) },
                                            { label: 'Predicted EOM', value: fmt2(prediction.predictedEOM || 0) },
                                            { label: 'Active Users', value: String(perUserRows.length) },
                                            { label: 'Total Requests', value: perUserRows.reduce((s, u) => s + u.requests, 0).toLocaleString() },
                                        ].map(stat => (
                                            <div key={stat.label} style={{ background: '#1e293b', borderRadius: 6, padding: '10px 14px', minWidth: 110, flex: 1 }}>
                                                <div style={{ fontSize: 9, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{stat.label}</div>
                                                <div style={{ fontSize: 16, fontWeight: 700, color: '#f1f5f9', marginTop: 3 }}>{stat.value}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {mgmtSections.byOrgUnit && orgUnitsForReport.length > 0 && (
                                <div style={{ marginBottom: 20 }}>
                                    <SectionHeader title="By Organisation Unit" />
                                    <PreviewTable
                                        headers={['Org Unit', 'Users', 'Requests', 'Tokens', 'Spend', '% of Total']}
                                        rows={[...orgUnitsForReport].sort((a, b) => b.spend - a.spend).map(o => [
                                            o.orgUnit || 'Unknown', o.users, (o.requests || 0).toLocaleString(),
                                            fmtTokens(o.tokens || 0), fmt2(o.spend || 0),
                                            totalOrgSpend > 0 ? ((o.spend / totalOrgSpend) * 100).toFixed(1) + '%' : '—'
                                        ])}
                                    />
                                </div>
                            )}
                            {mgmtSections.byDepartment && deptsForReport.length > 0 && (
                                <div style={{ marginBottom: 20 }}>
                                    <SectionHeader title="By Department" />
                                    <PreviewTable
                                        headers={['Department', 'Users', 'Requests', 'Tokens', 'Spend', '% of Total']}
                                        rows={[...deptsForReport].sort((a, b) => b.spend - a.spend).map(d => [
                                            d.department || 'Unknown', d.users, (d.requests || 0).toLocaleString(),
                                            fmtTokens(d.tokens || 0), fmt2(d.spend || 0),
                                            totalDeptSpend > 0 ? ((d.spend / totalDeptSpend) * 100).toFixed(1) + '%' : '—'
                                        ])}
                                    />
                                </div>
                            )}
                            {mgmtSections.teamBudgets && teamsForReport.length > 0 && (
                                <div style={{ marginBottom: 20 }}>
                                    <SectionHeader title="Team Budgets" />
                                    <PreviewTable
                                        headers={['Team', 'Users', 'Spend', 'Budget', '% Used', 'Period', 'Status']}
                                        rows={teamsForReport.map(t => {
                                            const pct = t.max_budget > 0 ? ((t.spend / t.max_budget) * 100).toFixed(1) + '%' : '—';
                                            const status = t.blocked ? 'Blocked' : pct === '—' ? '—' : parseFloat(pct) >= 100 ? 'Over Budget' : parseFloat(pct) >= 80 ? 'High Usage' : 'Active';
                                            return [t.team_alias || t.team_id, t.userCount || 0, fmt2(t.spend), fmt2(t.max_budget), pct, fmtDuration(t.budget_duration), status];
                                        })}
                                    />
                                </div>
                            )}
                            {mgmtSections.topUsers && perUserRows.length > 0 && (
                                <div style={{ marginBottom: 20 }}>
                                    <SectionHeader title={`Top Users by Spend${userCapN !== Infinity ? ` (Top ${cappedUsers.length})` : ''}`} />
                                    <PreviewTable
                                        headers={['User', 'Department', 'Team(s)', 'Spend', 'Requests', 'Top Model']}
                                        rows={cappedUsers.map(u => {
                                            const topModel = Object.entries(u.models).sort((a, b) => b[1].spend - a[1].spend)[0];
                                            return [u.email, u.department || '—', u.teams.join(', '), fmt2(u.spend), u.requests.toLocaleString(), topModel ? resolveModelName(topModel[0]) : '—'];
                                        })}
                                    />
                                </div>
                            )}
                            {mgmtSections.modelDistribution && (aggregatedModels || []).length > 0 && (
                                <div style={{ marginBottom: 20 }}>
                                    <SectionHeader title={`Model Distribution${modelCapN !== Infinity ? ` (Top ${cappedModels.length})` : ''}`} />
                                    <PreviewTable
                                        headers={['Model', 'Spend', '% of Total', 'Requests', 'Tokens']}
                                        rows={cappedModels.map(m => [
                                            resolveModelName(m.model), fmt2(m.spend || 0),
                                            totalModelSpend > 0 ? ((m.spend / totalModelSpend) * 100).toFixed(1) + '%' : '—',
                                            (m.requests || 0).toLocaleString(), fmtTokens(m.tokens || 0)
                                        ])}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                );
            }

            if (reportType === 'billing') {
                // No Bill To selected — show placeholder, nothing to render yet
                if (!billingBillToDept) {
                    return (
                        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, minHeight: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--fg-secondary)' }}>
                                <div style={{ fontSize: '0.9rem' }}>Select a department to bill in Step 1</div>
                            </div>
                        </div>
                    );
                }

                // Build per-user rows scoped to Bill To dept, filtered by selected org units
                const billingUserRows = (() => {
                    const userMap = {};
                    Object.values(spendCache).forEach(cache => {
                        (cache?.data || []).forEach(u => {
                            if ((u.department || 'Unknown') !== billingBillToDept) return;
                            if (billingReportOrgUnits.size > 0 && !billingReportOrgUnits.has(u.orgUnit || '')) return;
                            if (!userMap[u.email]) {
                                userMap[u.email] = { email: u.email, department: u.department || 'Unknown', orgUnit: u.orgUnit || null, teams: [], spend: 0, requests: 0, tokens: 0 };
                            }
                            const row = userMap[u.email];
                            // find team alias from teamsForReport
                            const teamEntry = teamsForReport.find(t => spendCache[t.team_id]?.data?.some(x => x.email === u.email));
                            const alias = teamEntry ? (teamEntry.team_alias || teamEntry.team_id) : null;
                            if (alias && !row.teams.includes(alias)) row.teams.push(alias);
                            row.spend += u.spend || 0;
                            row.requests += u.requests || 0;
                            row.tokens += u.tokens || 0;
                        });
                    });
                    return Object.values(userMap).sort((a, b) => b.spend - a.spend);
                })();

                // Org unit breakdown derived from the filtered users
                const orgUnitMap = {};
                billingUserRows.forEach(u => {
                    const ou = u.orgUnit || 'Unknown';
                    if (!orgUnitMap[ou]) orgUnitMap[ou] = { orgUnit: ou, users: 0, spend: 0, requests: 0 };
                    orgUnitMap[ou].users++;
                    orgUnitMap[ou].spend += u.spend;
                    orgUnitMap[ou].requests += u.requests;
                });
                const billingOrgsFiltered = Object.values(orgUnitMap).sort((a, b) => b.spend - a.spend);
                const billingTotalOrgSpend = billingOrgsFiltered.reduce((s, o) => s + o.spend, 0);

                // Dept summary row (single dept = Bill To)
                const billingDeptsFiltered = (aggregatedDepartments || []).filter(d => d.department === billingBillToDept);

                const billingTotalCost = billingUserRows.reduce((s, u) => s + u.spend, 0);
                const billingChargeRate = Math.max(0, parseFloat(billingChargeAmount) || 0);
                const billingAddedAmt = (() => {
                    if (billingChargeMethod === 'markup') return billingTotalCost * (billingChargeRate / 100);
                    if (billingChargeMethod === 'margin') return billingChargeRate < 100 ? billingTotalCost * (billingChargeRate / (100 - billingChargeRate)) : 0;
                    if (billingChargeMethod === 'flat') return billingChargeRate;
                    return 0;
                })();
                const billingChargeLabel = (() => {
                    if (billingChargeMethod === 'markup') return `Markup (${billingChargeRate}%)`;
                    if (billingChargeMethod === 'margin') return `Margin (${billingChargeRate}%)`;
                    if (billingChargeMethod === 'flat') return `Service Fee`;
                    return null;
                })();
                const billingTotalCharge = billingTotalCost + billingAddedAmt;

                return (
                    <div style={{ background: '#ffffff', borderRadius: 8, overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,0.3)' }}>
                        {previewHeader}
                        <div style={{ padding: '16px 20px', color: '#1e293b', fontSize: 13 }}>
                            <div style={{ marginBottom: 20 }}>
                                <SectionHeader title="Billing Summary" />
                                <PreviewTable
                                    headers={['Item', 'Amount']}
                                    rows={[
                                        ['Period Cost', fmt2(billingTotalCost)],
                                        ...(billingChargeLabel ? [[billingChargeLabel, `+${fmt2(billingAddedAmt)}`]] : []),
                                        ['Total Charge', fmt2(billingTotalCharge)],
                                        ['Bill To', billingBillToDept],
                                    ]}
                                />
                            </div>
                            {billingOrgsFiltered.length > 0 && (
                                <div style={{ marginBottom: 20 }}>
                                    <SectionHeader title="Cost by Organisation Unit" />
                                    <PreviewTable
                                        headers={['Org Unit', 'Users', 'Requests', 'Spend', '% of Total']}
                                        rows={billingOrgsFiltered.map(o => [
                                            o.orgUnit, o.users,
                                            (o.requests || 0).toLocaleString(), fmt2(o.spend || 0),
                                            billingTotalOrgSpend > 0 ? ((o.spend / billingTotalOrgSpend) * 100).toFixed(1) + '%' : '—'
                                        ])}
                                    />
                                </div>
                            )}
                            {billingDeptsFiltered.length > 0 && (
                                <div style={{ marginBottom: 20 }}>
                                    <SectionHeader title="Cost by Department" />
                                    <PreviewTable
                                        headers={['Department', 'Users', 'Requests', 'Spend']}
                                        rows={billingDeptsFiltered.map(d => [
                                            d.department || 'Unknown', d.users,
                                            (d.requests || 0).toLocaleString(), fmt2(d.spend || 0),
                                        ])}
                                    />
                                </div>
                            )}
                            {billingUserRows.length > 0 && (
                                <div style={{ marginBottom: 20 }}>
                                    <SectionHeader title="User Breakdown" />
                                    <PreviewTable
                                        headers={['Org Unit', 'Department', 'User', 'Team(s)', 'Requests', 'Tokens', 'Spend']}
                                        rows={billingUserRows.map(u => [
                                            u.orgUnit || '—',
                                            u.department,
                                            u.email,
                                            u.teams.join(', ') || '—',
                                            (u.requests || 0).toLocaleString(),
                                            (u.tokens || 0).toLocaleString(),
                                            fmt2(u.spend || 0),
                                        ])}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                );
            }

            // Non-management reports
            const totalSpend = (aggregatedDepartments || []).reduce((s, d) => s + (d.spend || 0), 0);

            let tableContent = null;
            if (reportType === 'my-usage') {
                tableContent = (
                    <div>
                        <SectionHeader title="My Usage Summary" />
                        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
                            {[
                                { label: 'Spend', value: fmt2(myData.spend) },
                                { label: 'Requests', value: myData.requests.toLocaleString() },
                                { label: 'Tokens', value: fmtTokens(myData.tokens) },
                                { label: 'Models Used', value: myModels.length },
                            ].map(m => (
                                <div key={m.label} style={{ background: '#1e293b', borderRadius: 6, padding: '8px 14px', minWidth: 110, flex: 1 }}>
                                    <div style={{ fontSize: 9, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{m.label}</div>
                                    <div style={{ fontSize: 16, fontWeight: 700, color: '#f1f5f9', marginTop: 2 }}>{m.value}</div>
                                </div>
                            ))}
                        </div>
                        <div style={{ marginBottom: 10, fontSize: 11, color: '#475569' }}>
                            Teams: <strong style={{ color: '#1e293b' }}>{myData.teams.join(', ') || '—'}</strong>
                        </div>
                        {myModels.length > 0 && (
                            <div>
                                <SectionHeader title="Model Breakdown" />
                                <PreviewTable
                                    headers={['Model', 'Requested Via', 'Requests', 'Tokens', 'Spend']}
                                    rows={myModels.map(m => {
                                        const via = myModelRouting[m.name];
                                        return [resolveModelName(m.name), via ? resolveModelName(via) : 'Direct', m.requests.toLocaleString(), fmtTokens(m.tokens), fmt2(m.spend)];
                                    })}
                                />
                            </div>
                        )}
                    </div>
                );
            } else if (reportType === 'team-budget') {
                tableContent = (
                    <div>
                        <SectionHeader title="Team Budget Summary" />
                        <PreviewTable
                            headers={['Team', 'Users', 'Spend', 'Budget', '% Used', 'Period', 'Status']}
                            rows={teamsForReport.map(t => {
                                const pct = t.max_budget > 0 ? ((t.spend / t.max_budget) * 100).toFixed(1) + '%' : '—';
                                const status = t.blocked ? 'Blocked' : pct === '—' ? '—' : parseFloat(pct) >= 100 ? 'Over Budget' : parseFloat(pct) >= 80 ? 'High Usage' : 'Active';
                                return [t.team_alias || t.team_id, t.userCount || 0, fmt2(t.spend), fmt2(t.max_budget), pct, fmtDuration(t.budget_duration), status];
                            })}
                        />
                    </div>
                );
            } else if (reportType === 'cost-by-dept') {
                tableContent = (
                    <div>
                        <SectionHeader title="Cost by Department" />
                        <PreviewTable
                            headers={['Department', 'Users', 'Requests', 'Tokens', 'Spend', '% of Total']}
                            rows={[...(aggregatedDepartments || [])].sort((a, b) => b.spend - a.spend).map(d => [
                                d.department || 'Unknown', d.users || 0, (d.requests || 0).toLocaleString(),
                                fmtTokens(d.tokens || 0), fmt2(d.spend || 0),
                                totalSpend > 0 ? ((d.spend / totalSpend) * 100).toFixed(1) + '%' : '—'
                            ])}
                        />
                    </div>
                );
            } else if (reportType === 'cost-by-org') {
                const totalOrgSpend = (aggregatedOrgUnits || []).reduce((s, o) => s + o.spend, 0);
                tableContent = (
                    <div>
                        <SectionHeader title="Cost by Organisation Unit" />
                        <PreviewTable
                            headers={['Org Unit', 'Users', 'Requests', 'Tokens', 'Spend', '% of Total']}
                            rows={[...(aggregatedOrgUnits || [])].sort((a, b) => b.spend - a.spend).map(o => [
                                o.orgUnit || 'Unknown', o.users || 0, (o.requests || 0).toLocaleString(),
                                fmtTokens(o.tokens || 0), fmt2(o.spend || 0),
                                totalOrgSpend > 0 ? ((o.spend / totalOrgSpend) * 100).toFixed(1) + '%' : '—'
                            ])}
                        />
                    </div>
                );
            } else if (reportType === 'per-user') {
                tableContent = (
                    <div>
                        <SectionHeader title="Per-User Breakdown" />
                        <PreviewTable
                            headers={['User', 'Department', 'Team(s)', 'Spend', 'Tokens', 'Requests', 'Top Model']}
                            rows={perUserRows.map(u => {
                                const topModel = Object.entries(u.models).sort((a, b) => b[1].spend - a[1].spend)[0];
                                return [u.email, u.department || '—', u.teams.join(', '), fmt2(u.spend), fmtTokens(u.tokens), u.requests.toLocaleString(), topModel ? resolveModelName(topModel[0]) : '—'];
                            })}
                        />
                    </div>
                );
            } else if (reportType === 'my-team') {
                const teamMembersForReport = myDirectReports.some(r => r.email.toLowerCase() === userEmail.toLowerCase())
                    ? myDirectReports
                    : [{ email: userEmail, displayName: '', orgUnit: '', department: '' }, ...myDirectReports];
                const directReportEmails = new Set(teamMembersForReport.map(r => r.email.toLowerCase()));
                const teamRows = perUserRows.filter(u => directReportEmails.has(u.email.toLowerCase()));
                const teamTotalSpend = teamRows.reduce((s, u) => s + u.spend, 0);
                const teamTotalRequests = teamRows.reduce((s, u) => s + u.requests, 0);
                const teamTotalTokens = teamRows.reduce((s, u) => s + u.tokens, 0);
                tableContent = (
                    <div>
                        <SectionHeader title="My Team Summary" />
                        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
                            {[
                                { label: 'Team Members', value: teamMembersForReport.length },
                                { label: 'Total Spend', value: fmt2(teamTotalSpend) },
                                { label: 'Total Requests', value: teamTotalRequests.toLocaleString() },
                                { label: 'Total Tokens', value: fmtTokens(teamTotalTokens) },
                            ].map(m => (
                                <div key={m.label} style={{ background: '#1e293b', borderRadius: 6, padding: '8px 14px', minWidth: 110, flex: 1 }}>
                                    <div style={{ fontSize: 9, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{m.label}</div>
                                    <div style={{ fontSize: 16, fontWeight: 700, color: '#f1f5f9', marginTop: 2 }}>{m.value}</div>
                                </div>
                            ))}
                        </div>
                        <SectionHeader title="Team Member Breakdown" />
                        {teamRows.length === 0
                            ? <div style={{ fontSize: 11, color: '#6b7280', padding: '8px 0' }}>No usage data found for your direct reports in this period.</div>
                            : <PreviewTable
                                headers={['User', 'Org Unit', 'Department', 'Spend', 'Tokens', 'Requests', 'Top Model']}
                                rows={teamRows.map(u => {
                                    const topModel = Object.entries(u.models).sort((a, b) => b[1].spend - a[1].spend)[0];
                                    const dr = teamMembersForReport.find(r => r.email.toLowerCase() === u.email.toLowerCase());
                                    return [u.email, dr?.orgUnit || '—', dr?.department || u.department || '—', fmt2(u.spend), fmtTokens(u.tokens), u.requests.toLocaleString(), topModel ? resolveModelName(topModel[0]) : '—'];
                                })}
                            />
                        }
                    </div>
                );
            }

            return (
                <div style={{ background: '#ffffff', borderRadius: 8, overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,0.3)' }}>
                    {previewHeader}
                    <div style={{ padding: '16px 20px', color: '#1e293b', fontSize: 13 }}>
                        {tableContent}
                    </div>
                </div>
            );
        };

        // ── CSV export ─────────────────────────────────────────────────────────
        // All exports are flat tables (header row + data rows only) for Power BI compatibility.
        // The management report downloads one file per enabled section since each section
        // is a different grain and cannot be combined into a single flat table.
        const toCSV = rows => rows.map(r => r.map(c => `"${String(c === null || c === undefined ? '' : c).replace(/"/g, '""')}"`).join(',')).join('\n');

        const downloadCSV = (csv, filename) => {
            const blob = new Blob([csv], { type: 'text/csv' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            a.click();
            URL.revokeObjectURL(url);
        };

        const exportCSV = () => {
            const now = new Date();
            const ukDate = `${String(now.getDate()).padStart(2, '0')}-${String(now.getMonth() + 1).padStart(2, '0')}-${now.getFullYear()}`;

            if (reportType === 'my-usage') {
                const rows = [['Model', 'Requested Via', 'Requests', 'Tokens', 'Spend']];
                myModels.forEach(m => {
                    const via = myModelRouting[m.name];
                    rows.push([resolveModelName(m.name), via ? resolveModelName(via) : 'Direct', m.requests, m.tokens, m.spend.toFixed(2)]);
                });
                downloadCSV(toCSV(rows), `ask-aware-my-usage-${ukDate}.csv`);

            } else if (reportType === 'team-budget') {
                const rows = [['Team', 'Users', 'Spend', 'Budget', '% Used', 'Reset Period', 'Status']];
                teamsForReport.forEach(t => {
                    const pct = t.max_budget > 0 ? ((t.spend / t.max_budget) * 100).toFixed(1) : '';
                    const status = t.blocked ? 'Blocked' : !pct ? '' : parseFloat(pct) >= 100 ? 'Over Budget' : parseFloat(pct) >= 80 ? 'High Usage' : 'Active';
                    rows.push([t.team_alias || t.team_id, t.userCount || 0, t.spend.toFixed(2), t.max_budget.toFixed(2), pct, fmtDuration(t.budget_duration), status]);
                });
                downloadCSV(toCSV(rows), `ask-aware-team-budget-${ukDate}.csv`);

            } else if (reportType === 'cost-by-dept') {
                const totalDeptSpend = (aggregatedDepartments || []).reduce((s, d) => s + (d.spend || 0), 0);
                const rows = [['Department', 'Users', 'Requests', 'Tokens', 'Spend', 'Pct of Total']];
                (aggregatedDepartments || []).forEach(d => {
                    rows.push([d.department || 'Unknown', d.users || 0, d.requests || 0, d.tokens || 0, (d.spend || 0).toFixed(2), totalDeptSpend > 0 ? ((d.spend / totalDeptSpend) * 100).toFixed(1) : '0']);
                });
                downloadCSV(toCSV(rows), `ask-aware-cost-by-dept-${ukDate}.csv`);

            } else if (reportType === 'cost-by-org') {
                const totalOrgSpend = (aggregatedOrgUnits || []).reduce((s, o) => s + (o.spend || 0), 0);
                const rows = [['Org Unit', 'Users', 'Requests', 'Tokens', 'Spend', 'Pct of Total']];
                (aggregatedOrgUnits || []).forEach(o => {
                    rows.push([o.orgUnit || 'Unknown', o.users || 0, o.requests || 0, o.tokens || 0, (o.spend || 0).toFixed(2), totalOrgSpend > 0 ? ((o.spend / totalOrgSpend) * 100).toFixed(1) : '0']);
                });
                downloadCSV(toCSV(rows), `ask-aware-cost-by-org-${ukDate}.csv`);

            } else if (reportType === 'per-user') {
                const rows = [['User', 'Org Unit', 'Department', 'Team(s)', 'Spend', 'Tokens', 'Requests', 'Top Model']];
                perUserRows.forEach(u => {
                    const topModel = Object.entries(u.models).sort((a, b) => b[1].spend - a[1].spend)[0];
                    rows.push([u.email, u.orgUnit || '', u.department || '', u.teams.join('; '), u.spend.toFixed(2), u.tokens, u.requests, topModel ? resolveModelName(topModel[0]) : '']);
                });
                downloadCSV(toCSV(rows), `ask-aware-per-user-${ukDate}.csv`);

            } else if (reportType === 'my-team') {
                const csvTeamMembers = myDirectReports.some(r => r.email.toLowerCase() === userEmail.toLowerCase())
                    ? myDirectReports
                    : [{ email: userEmail, displayName: '', orgUnit: '', department: '' }, ...myDirectReports];
                const csvTeamEmails = new Set(csvTeamMembers.map(r => r.email.toLowerCase()));
                const teamRows = perUserRows.filter(u => csvTeamEmails.has(u.email.toLowerCase()));
                const rows = [['Manager', 'User', 'Org Unit', 'Department', 'Period From', 'Period To', 'Spend', 'Tokens', 'Requests', 'Top Model']];
                teamRows.forEach(u => {
                    const topModel = Object.entries(u.models).sort((a, b) => b[1].spend - a[1].spend)[0];
                    const dr = csvTeamMembers.find(r => r.email.toLowerCase() === u.email.toLowerCase());
                    rows.push([userEmail, u.email, dr?.orgUnit || '', dr?.department || u.department || '', reportDateFrom, reportDateTo, u.spend.toFixed(2), u.tokens, u.requests, topModel ? resolveModelName(topModel[0]) : '']);
                });
                downloadCSV(toCSV(rows), `ask-aware-my-team-${ukDate}.csv`);

            } else if (reportType === 'management') {
                const userCapN2 = mgmtUserCap === 'all' ? Infinity : parseInt(mgmtUserCap, 10);
                const modelCapN2 = mgmtModelCap === 'all' ? Infinity : parseInt(mgmtModelCap, 10);

                if (mgmtSections.byOrgUnit && (aggregatedOrgUnits || []).length > 0) {
                    const totalOrgSpend = aggregatedOrgUnits.reduce((s, o) => s + o.spend, 0);
                    const rows = [['Org Unit', 'Users', 'Requests', 'Tokens', 'Spend', 'Pct of Total']];
                    [...aggregatedOrgUnits].sort((a, b) => b.spend - a.spend).forEach(o => {
                        rows.push([o.orgUnit || 'Unknown', o.users, o.requests, o.tokens, (o.spend || 0).toFixed(2), totalOrgSpend > 0 ? ((o.spend / totalOrgSpend) * 100).toFixed(1) : '0']);
                    });
                    downloadCSV(toCSV(rows), `ask-aware-mgmt-by-org-${ukDate}.csv`);
                }

                if (mgmtSections.byDepartment && (aggregatedDepartments || []).length > 0) {
                    const totalDeptSpend = aggregatedDepartments.reduce((s, d) => s + d.spend, 0);
                    const rows = [['Department', 'Users', 'Requests', 'Tokens', 'Spend', 'Pct of Total']];
                    [...aggregatedDepartments].sort((a, b) => b.spend - a.spend).forEach(d => {
                        rows.push([d.department || 'Unknown', d.users, d.requests, d.tokens, (d.spend || 0).toFixed(2), totalDeptSpend > 0 ? ((d.spend / totalDeptSpend) * 100).toFixed(1) : '0']);
                    });
                    downloadCSV(toCSV(rows), `ask-aware-mgmt-by-dept-${ukDate}.csv`);
                }

                if (mgmtSections.teamBudgets && teamsForReport.length > 0) {
                    const rows = [['Team', 'Users', 'Spend', 'Budget', 'Pct Used', 'Reset Period', 'Status']];
                    teamsForReport.forEach(t => {
                        const pct = t.max_budget > 0 ? ((t.spend / t.max_budget) * 100).toFixed(1) : '';
                        const status = t.blocked ? 'Blocked' : !pct ? '' : parseFloat(pct) >= 100 ? 'Over Budget' : parseFloat(pct) >= 80 ? 'High Usage' : 'Active';
                        rows.push([t.team_alias || t.team_id, t.userCount || 0, t.spend.toFixed(2), t.max_budget.toFixed(2), pct, fmtDuration(t.budget_duration), status]);
                    });
                    downloadCSV(toCSV(rows), `ask-aware-mgmt-team-budgets-${ukDate}.csv`);
                }

                if (mgmtSections.topUsers && perUserRows.length > 0) {
                    const cappedUsers = userCapN2 === Infinity ? perUserRows : perUserRows.slice(0, userCapN2);
                    const rows = [['User', 'Org Unit', 'Department', 'Team(s)', 'Spend', 'Requests', 'Top Model']];
                    cappedUsers.forEach(u => {
                        const topModel = Object.entries(u.models).sort((a, b) => b[1].spend - a[1].spend)[0];
                        rows.push([u.email, u.orgUnit || '', u.department || '', u.teams.join('; '), u.spend.toFixed(2), u.requests, topModel ? resolveModelName(topModel[0]) : '']);
                    });
                    downloadCSV(toCSV(rows), `ask-aware-mgmt-per-user-${ukDate}.csv`);
                }

                if (mgmtSections.modelDistribution && (aggregatedModels || []).length > 0) {
                    const totalModelSpend = aggregatedModels.reduce((s, m) => s + m.spend, 0);
                    const cappedModels = modelCapN2 === Infinity ? aggregatedModels : aggregatedModels.slice(0, modelCapN2);
                    const rows = [['Model', 'Spend', 'Pct of Total', 'Requests', 'Tokens']];
                    cappedModels.forEach(m => {
                        rows.push([resolveModelName(m.model), (m.spend || 0).toFixed(2), totalModelSpend > 0 ? ((m.spend / totalModelSpend) * 100).toFixed(1) : '0', m.requests, m.tokens]);
                    });
                    downloadCSV(toCSV(rows), `ask-aware-mgmt-models-${ukDate}.csv`);
                }
            }
        };

        // ── PDF export ─────────────────────────────────────────────────────────
        const exportPDF = async () => {
            setGeneratingPdf(true);
            try {
                const { default: jsPDF } = await import('jspdf');
                const { default: autoTable } = await import('jspdf-autotable');
                const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
                const pageW = doc.internal.pageSize.getWidth();
                const pageH = doc.internal.pageSize.getHeight();
                const margin = 14;

                // Load logo
                let logoBase64 = null;
                try {
                    const resp = await fetch('/AwareAI-logo.png');
                    if (resp.ok) {
                        const blob = await resp.blob();
                        logoBase64 = await new Promise(resolve => {
                            const reader = new FileReader();
                            reader.onloadend = () => resolve(reader.result);
                            reader.readAsDataURL(blob);
                        });
                    }
                } catch (_) {}

                let logoAspect = 3.0; // fallback aspect ratio width/height
                if (logoBase64) {
                    try {
                        const tmpImg = new Image();
                        tmpImg.src = logoBase64;
                        await new Promise(r => { tmpImg.onload = r; tmpImg.onerror = r; });
                        if (tmpImg.width && tmpImg.height) logoAspect = tmpImg.width / tmpImg.height;
                    } catch (_) {}
                }

                // Page footer added after all content
                const addFooters = () => {
                    const n = doc.getNumberOfPages();
                    for (let i = 1; i <= n; i++) {
                        doc.setPage(i);
                        doc.setDrawColor(203, 213, 225);
                        doc.line(margin, pageH - 12, pageW - margin, pageH - 12);
                        doc.setFontSize(7);
                        doc.setTextColor(148, 163, 184);
                        doc.text('AwareAI — Confidential. For management use only.', margin, pageH - 7);
                        doc.text(`Page ${i} of ${n}`, pageW - margin, pageH - 7, { align: 'right' });
                    }
                };

                // Standard page header (for subsequent pages)
                const addPageHeader = (title) => {
                    doc.setFillColor(10, 22, 40);
                    doc.rect(0, 0, pageW, 22, 'F');
                    if (logoBase64) {
                        try {
                            const lh = 12;
                            const lw = lh * logoAspect;
                            doc.addImage(logoBase64, 'PNG', margin, (22 - lh) / 2, lw, lh);
                        } catch (_) {}
                    }
                    doc.setTextColor(238, 242, 255);
                    doc.setFontSize(9);
                    doc.setFont('helvetica', 'bold');
                    doc.text(title, pageW - margin, 11, { align: 'right' });
                    doc.setFontSize(7);
                    doc.setFont('helvetica', 'normal');
                    doc.setTextColor(148, 163, 184);
                    doc.text(dateRangeLabel, pageW - margin, 17, { align: 'right' });
                };

                const headStyles = { fillColor: [10, 22, 40], textColor: [238, 242, 255], fontSize: 8, fontStyle: 'bold' };
                const altRowStyles = { fillColor: [248, 250, 252] };

                const addSection = (title, head, body, opts) => {
                    // Title block is ~18mm tall (10pt text + underline + gap); table needs at least 30mm.
                    // If not enough space for title + at least one row, break early.
                    const neededY = (doc.lastAutoTable ? doc.lastAutoTable.finalY + 12 : 30) + 48;
                    if (doc.lastAutoTable && neededY > pageH - 15) {
                        doc.addPage();
                        addPageHeader(activeReportDef ? activeReportDef.label : 'Report');
                        doc.lastAutoTable = null;
                    }
                    const startY = doc.lastAutoTable ? doc.lastAutoTable.finalY + 12 : 30;
                    doc.setTextColor(10, 22, 40);
                    doc.setFontSize(10);
                    doc.setFont('helvetica', 'bold');
                    doc.text(title, margin, startY);
                    doc.setDrawColor(196, 38, 31);
                    doc.line(margin, startY + 2, margin + 40, startY + 2);
                    autoTable(doc, Object.assign({
                        startY: startY + 5,
                        head: [head],
                        body: body,
                        headStyles: headStyles,
                        alternateRowStyles: altRowStyles,
                        margin: { left: margin, right: margin },
                        styles: { fontSize: 8, cellPadding: 2.5 },
                    }, opts || {}));
                };

                if (reportType === 'management') {
                    // Cover page
                    doc.setFillColor(10, 22, 40);
                    doc.rect(0, 0, pageW, pageH, 'F');

                    if (logoBase64) {
                        try {
                            const img = new Image();
                            img.src = logoBase64;
                            await new Promise(r => { img.onload = r; });
                            const logoH = 14;
                            const logoW = logoH * (img.width / img.height);
                            doc.addImage(logoBase64, 'PNG', (pageW - logoW) / 2, 80, logoW, logoH);
                        } catch (_) {}
                    }

                    doc.setTextColor(238, 242, 255);
                    doc.setFontSize(22);
                    doc.setFont('helvetica', 'bold');
                    doc.text('Ask Aware', pageW / 2, 110, { align: 'center' });
                    doc.setFontSize(14);
                    doc.setFont('helvetica', 'normal');
                    doc.setTextColor(148, 163, 184);
                    doc.text('AI Usage & Cost Report', pageW / 2, 120, { align: 'center' });

                    doc.setDrawColor(196, 38, 31);
                    doc.setLineWidth(0.5);
                    doc.line(pageW / 2 - 30, 128, pageW / 2 + 30, 128);
                    doc.setLineWidth(0.2);

                    doc.setFontSize(9);
                    doc.setTextColor(148, 163, 184);
                    doc.text('Prepared for: AwareAI', pageW / 2, 138, { align: 'center' });
                    doc.text(`Period: ${dateRangeLabel}`, pageW / 2, 146, { align: 'center' });
                    doc.text(`Generated: ${new Date().toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}`, pageW / 2, 154, { align: 'center' });

                    doc.setFontSize(7.5);
                    doc.setTextColor(71, 85, 105);
                    doc.text('CONFIDENTIAL — For management use only', pageW / 2, 175, { align: 'center' });

                    // Content pages
                    doc.addPage();
                    addPageHeader('Full Management Report');
                    doc.lastAutoTable = null;

                    const userCapN = mgmtUserCap === 'all' ? Infinity : parseInt(mgmtUserCap, 10);
                    const modelCapN = mgmtModelCap === 'all' ? Infinity : parseInt(mgmtModelCap, 10);
                    if (mgmtSections.execSummary && prediction) {
                        addSection('Executive Summary', ['Metric', 'Value'], [
                            ['Total Spend (Period)', fmt2(prediction.currentSpend || 0)],
                            ['Predicted End of Month', fmt2(prediction.predictedEOM || 0)],
                            ['Daily Average', fmt2(prediction.dailyAvg || 0)],
                            ['Days Remaining', String(prediction.daysRemaining ?? '—')],
                            ['Active Users', String(perUserRows.length)],
                            ['Total Requests', perUserRows.reduce((s, u) => s + u.requests, 0).toLocaleString()],
                            ['Teams Active', String(teamsForReport.length)],
                        ]);
                    }

                    if (mgmtSections.byOrgUnit && orgUnitsForReport.length > 0) {
                        const totalOrgSpend = orgUnitsForReport.reduce((s, o) => s + o.spend, 0);
                        addSection('By Organisation Unit', ['Org Unit', 'Users', 'Requests', 'Tokens', 'Spend', '% of Total'],
                            [...orgUnitsForReport].sort((a, b) => b.spend - a.spend).map(o => [
                                o.orgUnit || 'Unknown', o.users, o.requests.toLocaleString(),
                                fmtTokens(o.tokens), fmt2(o.spend),
                                totalOrgSpend > 0 ? ((o.spend / totalOrgSpend) * 100).toFixed(1) + '%' : '—'
                            ])
                        );
                    }

                    if (mgmtSections.byDepartment && deptsForReport.length > 0) {
                        const totalDeptSpend = deptsForReport.reduce((s, d) => s + d.spend, 0);
                        addSection('By Department', ['Department', 'Users', 'Requests', 'Tokens', 'Spend', '% of Total'],
                            [...deptsForReport].sort((a, b) => b.spend - a.spend).map(d => [
                                d.department || 'Unknown', d.users, d.requests.toLocaleString(),
                                fmtTokens(d.tokens), fmt2(d.spend),
                                totalDeptSpend > 0 ? ((d.spend / totalDeptSpend) * 100).toFixed(1) + '%' : '—'
                            ])
                        );
                    }

                    if (mgmtSections.teamBudgets && teamsForReport.length > 0) {
                        addSection('Team Budgets', ['Team', 'Users', 'Spend', 'Budget', '% Used', 'Period', 'Status'],
                            teamsForReport.map(t => {
                                const pct = t.max_budget > 0 ? ((t.spend / t.max_budget) * 100).toFixed(1) + '%' : '—';
                                const status = t.blocked ? 'Blocked' : pct === '—' ? '—' : parseFloat(pct) >= 100 ? 'Over Budget' : parseFloat(pct) >= 80 ? 'High Usage' : 'Active';
                                return [t.team_alias || t.team_id, t.userCount || 0, fmt2(t.spend), fmt2(t.max_budget), pct, fmtDuration(t.budget_duration), status];
                            })
                        );
                    }

                    if (mgmtSections.topUsers && perUserRows.length > 0) {
                        const cappedUsers = userCapN === Infinity ? perUserRows : perUserRows.slice(0, userCapN);
                        addSection(`Top Users by Spend${userCapN !== Infinity ? ` (Top ${cappedUsers.length})` : ''}`,
                            ['User', 'Department', 'Team(s)', 'Spend', 'Requests', 'Top Model'],
                            cappedUsers.map(u => {
                                const topModel = Object.entries(u.models).sort((a, b) => b[1].spend - a[1].spend)[0];
                                return [u.email, u.department || '—', u.teams.join(', '), fmt2(u.spend), u.requests.toLocaleString(), topModel ? resolveModelName(topModel[0]) : '—'];
                            }),
                            { columnStyles: { 0: { cellWidth: 40 }, 1: { cellWidth: 24 }, 2: { cellWidth: 24 } } }
                        );
                    }

                    if (mgmtSections.modelDistribution && (aggregatedModels || []).length > 0) {
                        const totalModelSpend = aggregatedModels.reduce((s, m) => s + m.spend, 0);
                        const cappedModels = modelCapN === Infinity ? aggregatedModels : aggregatedModels.slice(0, modelCapN);
                        addSection(`Model Distribution${modelCapN !== Infinity ? ` (Top ${cappedModels.length})` : ''}`,
                            ['Model', 'Spend', '% of Total', 'Requests', 'Tokens'],
                            cappedModels.map(m => [
                                resolveModelName(m.model), fmt2(m.spend),
                                totalModelSpend > 0 ? ((m.spend / totalModelSpend) * 100).toFixed(1) + '%' : '—',
                                m.requests.toLocaleString(), fmtTokens(m.tokens)
                            ])
                        );
                    }

                } else if (reportType === 'billing') {
                    addPageHeader('Billing Report');
                    doc.lastAutoTable = null;
                    // Build user rows the same way as preview: scoped to Bill To dept + org unit filter
                    const pdfBillingUsers = (() => {
                        const userMap = {};
                        Object.values(spendCache).forEach(cache => {
                            (cache?.data || []).forEach(u => {
                                if ((u.department || 'Unknown') !== billingBillToDept) return;
                                if (billingReportOrgUnits.size > 0 && !billingReportOrgUnits.has(u.orgUnit || '')) return;
                                if (!userMap[u.email]) {
                                    userMap[u.email] = { email: u.email, department: u.department || 'Unknown', orgUnit: u.orgUnit || null, teams: [], spend: 0, requests: 0, tokens: 0 };
                                }
                                const row = userMap[u.email];
                                const teamEntry = teamsForReport.find(t => spendCache[t.team_id]?.data?.some(x => x.email === u.email));
                                const alias = teamEntry ? (teamEntry.team_alias || teamEntry.team_id) : null;
                                if (alias && !row.teams.includes(alias)) row.teams.push(alias);
                                row.spend += u.spend || 0;
                                row.requests += u.requests || 0;
                                row.tokens += u.tokens || 0;
                            });
                        });
                        return Object.values(userMap).sort((a, b) => b.spend - a.spend);
                    })();
                    const pdfBillingTotal = pdfBillingUsers.reduce((s, u) => s + u.spend, 0);
                    const pdfChargeRate = Math.max(0, parseFloat(billingChargeAmount) || 0);
                    const pdfAddedAmt = (() => {
                        if (billingChargeMethod === 'markup') return pdfBillingTotal * (pdfChargeRate / 100);
                        if (billingChargeMethod === 'margin') return pdfChargeRate < 100 ? pdfBillingTotal * (pdfChargeRate / (100 - pdfChargeRate)) : 0;
                        if (billingChargeMethod === 'flat') return pdfChargeRate;
                        return 0;
                    })();
                    const pdfChargeLabel = (() => {
                        if (billingChargeMethod === 'markup') return `Markup (${pdfChargeRate}%)`;
                        if (billingChargeMethod === 'margin') return `Margin (${pdfChargeRate}%)`;
                        if (billingChargeMethod === 'flat') return `Service Fee`;
                        return null;
                    })();
                    const pdfBillingCharge = pdfBillingTotal + pdfAddedAmt;
                    // Org unit breakdown derived from filtered users
                    const pdfOrgMap = {};
                    pdfBillingUsers.forEach(u => {
                        const ou = u.orgUnit || 'Unknown';
                        if (!pdfOrgMap[ou]) pdfOrgMap[ou] = { orgUnit: ou, users: 0, spend: 0, requests: 0 };
                        pdfOrgMap[ou].users++;
                        pdfOrgMap[ou].spend += u.spend;
                        pdfOrgMap[ou].requests += u.requests;
                    });
                    const pdfBillingOrgs = Object.values(pdfOrgMap).sort((a, b) => b.spend - a.spend);
                    addSection('Billing Summary', ['Item', 'Amount'], [
                        ['Period Cost', fmt2(pdfBillingTotal)],
                        ...(pdfChargeLabel ? [[pdfChargeLabel, `+${fmt2(pdfAddedAmt)}`]] : []),
                        ['Total Charge', fmt2(pdfBillingCharge)],
                        ['Bill To', billingBillToDept],
                    ]);
                    if (pdfBillingOrgs.length > 0) {
                        const totalOrgSpend = pdfBillingOrgs.reduce((s, o) => s + o.spend, 0);
                        addSection('Cost by Organisation Unit', ['Org Unit', 'Users', 'Requests', 'Spend', '% of Total'],
                            pdfBillingOrgs.map(o => [
                                o.orgUnit || 'Unknown', o.users || 0, (o.requests || 0).toLocaleString(),
                                fmt2(o.spend || 0),
                                totalOrgSpend > 0 ? ((o.spend / totalOrgSpend) * 100).toFixed(1) + '%' : '—'
                            ])
                        );
                    }
                    const pdfDeptRow = (aggregatedDepartments || []).filter(d => d.department === billingBillToDept);
                    if (pdfDeptRow.length > 0) {
                        addSection('Cost by Department', ['Department', 'Users', 'Requests', 'Spend'],
                            pdfDeptRow.map(d => [d.department || 'Unknown', d.users || 0, (d.requests || 0).toLocaleString(), fmt2(d.spend || 0)])
                        );
                    }
                    if (pdfBillingUsers.length > 0) {
                        addSection('User Breakdown', ['Org Unit', 'Department', 'User', 'Team(s)', 'Requests', 'Tokens', 'Spend'],
                            pdfBillingUsers.map(u => [
                                u.orgUnit || '—', u.department, u.email, u.teams.join(', ') || '—',
                                (u.requests || 0).toLocaleString(), (u.tokens || 0).toLocaleString(), fmt2(u.spend || 0)
                            ]),
                            { columnStyles: { 0: { cellWidth: 22 }, 1: { cellWidth: 22 }, 2: { cellWidth: 40 } } }
                        );
                    }
                } else {
                    // Non-management, non-billing: standard header + single table
                    addPageHeader(activeReportDef ? activeReportDef.label : 'Report');
                    doc.lastAutoTable = null;

                    if (reportType === 'my-usage') {
                        addSection('My Usage Summary', ['Metric', 'Value'], [
                            ['Spend', fmt2(myData.spend)],
                            ['Requests', myData.requests.toLocaleString()],
                            ['Tokens', fmtTokens(myData.tokens)],
                            ['Teams', myData.teams.join(', ') || '—'],
                        ]);
                        if (myModels.length > 0) {
                            addSection('Model Breakdown', ['Model', 'Requested Via', 'Requests', 'Tokens', 'Spend'],
                                myModels.map(m => {
                                    const via = myModelRouting[m.name];
                                    return [resolveModelName(m.name), via ? resolveModelName(via) : 'Direct', m.requests.toLocaleString(), fmtTokens(m.tokens), fmt2(m.spend)];
                                })
                            );
                        }
                    } else if (reportType === 'team-budget') {
                        addSection('Team Budget Summary', ['Team', 'Users', 'Spend', 'Budget', '% Used', 'Period', 'Status'],
                            teamsForReport.map(t => {
                                const pct = t.max_budget > 0 ? ((t.spend / t.max_budget) * 100).toFixed(1) + '%' : '—';
                                const status = t.blocked ? 'Blocked' : pct === '—' ? '—' : parseFloat(pct) >= 100 ? 'Over Budget' : parseFloat(pct) >= 80 ? 'High Usage' : 'Active';
                                return [t.team_alias || t.team_id, t.userCount || 0, fmt2(t.spend), fmt2(t.max_budget), pct, fmtDuration(t.budget_duration), status];
                            })
                        );
                    } else if (reportType === 'cost-by-dept') {
                        const total = (aggregatedDepartments || []).reduce((s, d) => s + (d.spend || 0), 0);
                        addSection('Cost by Department', ['Department', 'Users', 'Requests', 'Tokens', 'Spend', '% of Total'],
                            [...(aggregatedDepartments || [])].sort((a, b) => b.spend - a.spend).map(d => [
                                d.department || 'Unknown', d.users || 0, (d.requests || 0).toLocaleString(),
                                fmtTokens(d.tokens || 0), fmt2(d.spend || 0),
                                total > 0 ? ((d.spend / total) * 100).toFixed(1) + '%' : '—'
                            ])
                        );
                    } else if (reportType === 'cost-by-org') {
                        const total = (aggregatedOrgUnits || []).reduce((s, o) => s + (o.spend || 0), 0);
                        addSection('Cost by Organisation', ['Org Unit', 'Users', 'Requests', 'Tokens', 'Spend', '% of Total'],
                            [...(aggregatedOrgUnits || [])].sort((a, b) => b.spend - a.spend).map(o => [
                                o.orgUnit || 'Unknown', o.users || 0, (o.requests || 0).toLocaleString(),
                                fmtTokens(o.tokens || 0), fmt2(o.spend || 0),
                                total > 0 ? ((o.spend / total) * 100).toFixed(1) + '%' : '—'
                            ])
                        );
                    } else if (reportType === 'per-user') {
                        addSection('Per-User Spend Breakdown', ['User', 'Department', 'Team(s)', 'Spend', 'Tokens', 'Requests', 'Top Model'],
                            perUserRows.map(u => {
                                const topModel = Object.entries(u.models).sort((a, b) => b[1].spend - a[1].spend)[0];
                                return [u.email, u.department || '—', u.teams.join(', '), fmt2(u.spend), fmtTokens(u.tokens), u.requests.toLocaleString(), topModel ? resolveModelName(topModel[0]) : '—'];
                            }),
                            { columnStyles: { 0: { cellWidth: 44 } } }
                        );
                    }
                }

                addFooters();
                const now = new Date();
                const ukDate = `${String(now.getDate()).padStart(2,'0')}-${String(now.getMonth()+1).padStart(2,'0')}-${now.getFullYear()}`;
                doc.save(`ask-aware-${reportType}-${ukDate}.pdf`);
            } catch (err) {
                console.error('PDF export failed:', err);
                alert('Failed to generate PDF: ' + err.message);
            } finally {
                setGeneratingPdf(false);
            }
        };

        // ── render ─────────────────────────────────────────────────────────────
        return (
            <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'flex-start', height: 'calc(100vh - 220px)', minHeight: 500 }}>

                {/* LEFT — config panel */}
                <div style={{ flex: '0 0 320px', minWidth: 260, height: '100%', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '1rem', paddingRight: 4 }}>

                    {/* Presets */}
                    <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: '1rem' }}>
                        <div style={{ ...filterLabel, marginBottom: '0.6rem' }}>Presets</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                            {visibleReportDefs.map(rt => (
                                <button
                                    key={rt.id}
                                    onClick={() => applyPreset(rt.id)}
                                    style={{
                                        padding: '5px 12px',
                                        borderRadius: 20,
                                        border: `1px solid ${reportType === rt.id ? '#C4261F' : 'var(--border)'}`,
                                        background: reportType === rt.id ? 'rgba(196,38,31,0.15)' : 'var(--surface)',
                                        color: reportType === rt.id ? '#C4261F' : 'var(--fg-secondary)',
                                        fontSize: '0.78rem',
                                        fontWeight: 600,
                                        cursor: 'pointer',
                                        fontFamily: 'inherit',
                                        transition: 'all 0.15s',
                                    }}
                                >
                                    {rt.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Date range */}
                    <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: '1rem' }}>
                        <div style={{ ...filterLabel, marginBottom: '0.5rem' }}>Date Range</div>
                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.6rem' }}>
                            {[
                                { label: 'Today', preset: 'today', from: todayStr, to: todayStr },
                                { label: 'Last 7 days', preset: 'last7', from: (() => { const d = new Date(today); d.setDate(d.getDate() - 7); return d.toISOString().split('T')[0]; })(), to: todayStr },
                                { label: 'Last 30 days', preset: 'last30', from: (() => { const d = new Date(today); d.setDate(d.getDate() - 30); return d.toISOString().split('T')[0]; })(), to: todayStr },
                                { label: 'This Month', preset: 'this_month', from: thisMonthStartStr, to: todayStr },
                            ].map(opt => (
                                <button
                                    key={opt.label}
                                    onClick={() => setReportPresetRange(opt.preset)}
                                    style={{
                                        padding: '4px 10px', borderRadius: 5,
                                        border: `1px solid ${reportDateFrom === opt.from && reportDateTo === opt.to ? '#C4261F' : 'var(--border)'}`,
                                        background: reportDateFrom === opt.from && reportDateTo === opt.to ? 'rgba(196,38,31,0.15)' : 'var(--surface)',
                                        color: reportDateFrom === opt.from && reportDateTo === opt.to ? '#C4261F' : 'var(--fg-secondary)',
                                        fontSize: '0.76rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                                    }}
                                >
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <div style={{ flex: 1 }}>
                                <label style={{ ...filterLabel, marginBottom: '0.25rem', display: 'block' }}>From</label>
                                <input type="date" style={{ ...filterInput, width: '100%', boxSizing: 'border-box' }} value={reportDateFrom} onChange={e => setReportDateFrom(e.target.value)} />
                            </div>
                            <div style={{ flex: 1 }}>
                                <label style={{ ...filterLabel, marginBottom: '0.25rem', display: 'block' }}>To</label>
                                <input type="date" style={{ ...filterInput, width: '100%', boxSizing: 'border-box' }} value={reportDateTo} onChange={e => setReportDateTo(e.target.value)} />
                            </div>
                        </div>
                    </div>

                    {/* Team filter */}
                    {showTeamFilter && (
                        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: '1rem' }}>
                            <div style={{ ...filterLabel, marginBottom: '0.4rem' }}>Include Teams</div>
                            <div style={{ fontSize: '0.72rem', color: 'var(--muted)', marginBottom: '0.5rem' }}>Uncheck teams to exclude them from the report</div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem', maxHeight: 180, overflowY: 'auto' }}>
                                {visibleTeams.filter(t => t.groupId !== null).map(t => {
                                    const checked = reportTeams.has(t.team_id);
                                    return (
                                        <label key={t.team_id} style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer', fontSize: '0.82rem', color: checked ? 'var(--fg-primary)' : 'var(--fg-secondary)', userSelect: 'none' }}>
                                            <input
                                                type="checkbox"
                                                checked={checked}
                                                onChange={() => {
                                                    setReportTeams(prev => {
                                                        const next = new Set(prev);
                                                        checked ? next.delete(t.team_id) : next.add(t.team_id);
                                                        return next;
                                                    });
                                                }}
                                                style={{ accentColor: '#C4261F' }}
                                            />
                                            {t.team_alias || t.team_id}
                                        </label>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Management sections config */}
                    {reportType === 'management' && (
                        <>
                        {/* Department filter */}
                        {(aggregatedDepartments || []).length > 0 && (
                            <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: '1rem' }}>
                                <div style={{ ...filterLabel, marginBottom: '0.4rem' }}>Include Departments</div>
                                <div style={{ fontSize: '0.72rem', color: 'var(--muted)', marginBottom: '0.5rem' }}>Uncheck departments to exclude them from the report</div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem', maxHeight: 150, overflowY: 'auto' }}>
                                    {(aggregatedDepartments || []).map(d => {
                                        const checked = reportDepts.size === 0 || reportDepts.has(d.department);
                                        return (
                                            <label key={d.department} style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer', fontSize: '0.82rem', color: checked ? 'var(--fg-primary)' : 'var(--fg-secondary)', userSelect: 'none' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={checked}
                                                    onChange={() => {
                                                        setReportDepts(prev => {
                                                            // On first uncheck, seed from all departments minus this one
                                                            const base = prev.size === 0
                                                                ? new Set((aggregatedDepartments || []).map(x => x.department))
                                                                : new Set(prev);
                                                            checked ? base.delete(d.department) : base.add(d.department);
                                                            // If all are checked again, reset to empty (= all)
                                                            const allDepts = (aggregatedDepartments || []).map(x => x.department);
                                                            return allDepts.every(dep => base.has(dep)) ? new Set() : base;
                                                        });
                                                    }}
                                                    style={{ accentColor: '#C4261F' }}
                                                />
                                                {d.department}
                                            </label>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Org Unit filter */}
                        {(aggregatedOrgUnits || []).length > 0 && (
                            <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: '1rem' }}>
                                <div style={{ ...filterLabel, marginBottom: '0.4rem' }}>Include Organisation Units</div>
                                <div style={{ fontSize: '0.72rem', color: 'var(--muted)', marginBottom: '0.5rem' }}>Uncheck org units to exclude them from the report</div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem', maxHeight: 150, overflowY: 'auto' }}>
                                    {(aggregatedOrgUnits || []).map(o => {
                                        const checked = reportOrgUnits.size === 0 || reportOrgUnits.has(o.orgUnit);
                                        return (
                                            <label key={o.orgUnit} style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer', fontSize: '0.82rem', color: checked ? 'var(--fg-primary)' : 'var(--fg-secondary)', userSelect: 'none' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={checked}
                                                    onChange={() => {
                                                        setReportOrgUnits(prev => {
                                                            const base = prev.size === 0
                                                                ? new Set((aggregatedOrgUnits || []).map(x => x.orgUnit))
                                                                : new Set(prev);
                                                            checked ? base.delete(o.orgUnit) : base.add(o.orgUnit);
                                                            const allOrgs = (aggregatedOrgUnits || []).map(x => x.orgUnit);
                                                            return allOrgs.every(ou => base.has(ou)) ? new Set() : base;
                                                        });
                                                    }}
                                                    style={{ accentColor: '#C4261F' }}
                                                />
                                                {o.orgUnit}
                                            </label>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Report sections + caps */}
                        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: '1rem' }}>
                            <div style={{ ...filterLabel, marginBottom: '0.6rem' }}>Report Sections</div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', marginBottom: '0.8rem' }}>
                                {[
                                    { key: 'execSummary',       label: 'Executive Summary', locked: true },
                                    { key: 'byOrgUnit',         label: 'By Organisation Unit' },
                                    { key: 'byDepartment',      label: 'By Department' },
                                    { key: 'teamBudgets',       label: 'Team Budgets' },
                                    { key: 'topUsers',          label: 'Top Users' },
                                    { key: 'modelDistribution', label: 'Model Distribution' },
                                ].map(section => (
                                    <label key={section.key} style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: section.locked ? 'default' : 'pointer', fontSize: '0.82rem', color: mgmtSections[section.key] ? 'var(--fg-primary)' : 'var(--fg-secondary)', userSelect: 'none' }}>
                                        <input
                                            type="checkbox"
                                            checked={mgmtSections[section.key]}
                                            disabled={section.locked}
                                            onChange={() => {
                                                if (section.locked) return;
                                                setMgmtSections(prev => ({ ...prev, [section.key]: !prev[section.key] }));
                                            }}
                                            style={{ accentColor: '#C4261F' }}
                                        />
                                        {section.label}
                                        {section.locked && <span style={{ fontSize: '0.68rem', color: 'var(--muted)', marginLeft: 4 }}>(always included)</span>}
                                    </label>
                                ))}
                            </div>
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                                <div style={{ flex: 1 }}>
                                    <label style={{ ...filterLabel, marginBottom: '0.25rem', display: 'block' }}>User cap</label>
                                    <select value={mgmtUserCap} onChange={e => setMgmtUserCap(e.target.value)} style={{ ...filterInput, width: '100%', boxSizing: 'border-box' }}>
                                        <option value="10">Top 10</option>
                                        <option value="25">Top 25</option>
                                        <option value="50">Top 50</option>
                                        <option value="all">All</option>
                                    </select>
                                </div>
                                <div style={{ flex: 1 }}>
                                    <label style={{ ...filterLabel, marginBottom: '0.25rem', display: 'block' }}>Model cap</label>
                                    <select value={mgmtModelCap} onChange={e => setMgmtModelCap(e.target.value)} style={{ ...filterInput, width: '100%', boxSizing: 'border-box' }}>
                                        <option value="10">Top 10</option>
                                        <option value="20">Top 20</option>
                                        <option value="all">All</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        </>
                    )}

                    {/* Billing report config — progressive disclosure */}
                    {reportType === 'billing' && (() => {
                        // Derive org units that belong to the selected Bill To department
                        const billingOrgUnitsForDept = (() => {
                            if (!billingBillToDept) return [];
                            const orgSet = new Set();
                            Object.values(spendCache).forEach(cache => {
                                (cache?.data || []).forEach(u => {
                                    if ((u.department || 'Unknown') === billingBillToDept && u.orgUnit) {
                                        orgSet.add(u.orgUnit);
                                    }
                                });
                            });
                            return [...orgSet].sort();
                        })();
                        const showOrgStep = billingBillToDept && billingOrgUnitsForDept.length > 1;

                        return (
                            <>
                            {/* Step 1: Bill To */}
                            <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: '1rem' }}>
                                <div style={{ ...filterLabel, marginBottom: '0.25rem' }}>Step 1: Bill To</div>
                                <div style={{ fontSize: '0.72rem', color: 'var(--muted)', marginBottom: '0.5rem' }}>Select the department to invoice</div>
                                <select
                                    value={billingBillToDept}
                                    onChange={e => {
                                        setBillingBillToDept(e.target.value);
                                        setBillingReportOrgUnits(new Set());
                                    }}
                                    style={{ ...filterInput, width: '100%', boxSizing: 'border-box' }}
                                >
                                    <option value="">— Select department —</option>
                                    {(aggregatedDepartments || []).map(d => (
                                        <option key={d.department} value={d.department}>{d.department}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Step 2: Org Units (only if Bill To is set and dept has >1 org unit) */}
                            {showOrgStep && (
                                <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: '1rem' }}>
                                    <div style={{ ...filterLabel, marginBottom: '0.25rem' }}>Step 2: Organisation Units</div>
                                    <div style={{ fontSize: '0.72rem', color: 'var(--muted)', marginBottom: '0.5rem' }}>Uncheck to exclude from this invoice</div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                                        {billingOrgUnitsForDept.map(ou => {
                                            const checked = billingReportOrgUnits.size === 0 || billingReportOrgUnits.has(ou);
                                            return (
                                                <label key={ou} style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer', fontSize: '0.82rem', color: checked ? 'var(--fg-primary)' : 'var(--fg-secondary)', userSelect: 'none' }}>
                                                    <input type="checkbox" checked={checked}
                                                        onChange={() => {
                                                            setBillingReportOrgUnits(prev => {
                                                                const base = prev.size === 0 ? new Set(billingOrgUnitsForDept) : new Set(prev);
                                                                checked ? base.delete(ou) : base.add(ou);
                                                                return billingOrgUnitsForDept.every(o => base.has(o)) ? new Set() : base;
                                                            });
                                                        }}
                                                        style={{ accentColor: '#C4261F' }}
                                                    />
                                                    {ou}
                                                </label>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Step 2/3: Charge method — only shown after Bill To is set */}
                            {billingBillToDept && (
                                <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: '1rem' }}>
                                    <div style={{ ...filterLabel, marginBottom: '0.25rem' }}>{showOrgStep ? 'Step 3: Additional Charge' : 'Step 2: Additional Charge'}</div>
                                    <div style={{ marginBottom: '0.6rem' }}>
                                        <label style={{ ...filterLabel, marginBottom: '0.25rem', display: 'block', fontWeight: 400 }}>Method</label>
                                        <select
                                            value={billingChargeMethod}
                                            onChange={e => { setBillingChargeMethod(e.target.value); setBillingChargeAmount(''); }}
                                            style={{ ...filterInput, width: '100%', boxSizing: 'border-box' }}
                                        >
                                            <option value="none">None — bill at cost</option>
                                            <option value="markup">Markup — add % on top of cost</option>
                                            <option value="margin">Margin — target % profit margin</option>
                                            <option value="flat">Flat fee — fixed amount added</option>
                                        </select>
                                    </div>
                                    {billingChargeMethod !== 'none' && (() => {
                                        const descriptions = {
                                            markup: 'Cost × (1 + rate). E.g. $10 cost + 20% markup = $12.00',
                                            margin: 'Cost ÷ (1 − rate). E.g. $10 cost at 20% margin = $12.50',
                                            flat:   'Cost + fixed fee. E.g. $10 cost + $5.00 fee = $15.00',
                                        };
                                        const isFlat = billingChargeMethod === 'flat';
                                        return (
                                            <div>
                                                <div style={{ fontSize: '0.72rem', color: 'var(--muted)', marginBottom: '0.4rem' }}>{descriptions[billingChargeMethod]}</div>
                                                <label style={{ ...filterLabel, marginBottom: '0.25rem', display: 'block', fontWeight: 400 }}>
                                                    {isFlat ? 'Amount ($)' : 'Rate (%)'}
                                                </label>
                                                <input
                                                    type="number" min="0" max={isFlat ? undefined : '99'} step={isFlat ? '0.01' : '0.5'}
                                                    value={billingChargeAmount}
                                                    onChange={e => setBillingChargeAmount(e.target.value)}
                                                    placeholder={isFlat ? '0.00' : '0'}
                                                    style={{ ...filterInput, width: '100%', boxSizing: 'border-box' }}
                                                />
                                            </div>
                                        );
                                    })()}
                                </div>
                            )}
                            </>
                        );
                    })()}

                    {/* Download buttons */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        <button
                            onClick={exportPDF}
                            disabled={!reportType || generatingPdf}
                            style={{ width: '100%', padding: '10px 16px', background: reportType && !generatingPdf ? '#C4261F' : 'var(--border)', border: 'none', borderRadius: 8, color: reportType && !generatingPdf ? '#fff' : 'var(--muted)', fontSize: '0.84rem', fontWeight: 700, cursor: reportType && !generatingPdf ? 'pointer' : 'default', fontFamily: 'inherit', opacity: generatingPdf ? 0.7 : 1 }}
                        >
                            {generatingPdf ? 'Generating PDF...' : 'Download PDF'}
                        </button>
                        {showCsvBtn && (
                            <button
                                onClick={exportCSV}
                                disabled={!reportType}
                                style={{ width: '100%', padding: '10px 16px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--fg-primary)', fontSize: '0.84rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
                            >
                                Download CSV
                            </button>
                        )}
                    </div>

                </div>

                {/* RIGHT — live preview panel */}
                <div style={{ flex: 1, minWidth: 0, height: '100%' }}>
                    {!reportType ? (
                        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--fg-secondary)' }}>
                                <svg width="40" height="40" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block', margin: '0 auto 12px', opacity: 0.4 }}>
                                    <rect x="3" y="1" width="10" height="14" rx="1.5" />
                                    <line x1="6" y1="5" x2="10" y2="5" />
                                    <line x1="6" y1="8" x2="10" y2="8" />
                                    <line x1="6" y1="11" x2="8.5" y2="11" />
                                </svg>
                                <div style={{ fontWeight: 600, color: 'var(--border)', marginBottom: 6 }}>No report selected</div>
                                <div style={{ fontSize: '0.78rem' }}>Select a preset to see a live preview here</div>
                            </div>
                        </div>
                    ) : (
                        <div style={{ height: '100%', overflowY: 'auto', overscrollBehavior: 'contain' }}>
                            {buildPreview()}
                        </div>
                    )}
                </div>

            </div>
        );
    };

    const renderKeys = () => {
        // Special "No Team" option for unassigned keys
        const NO_TEAM_OPTION = { team_id: '__no_team__', team_alias: 'No Team (Unassigned)' };

        // Initialize selectedTeamForKeys to "No Team" option if not set
        const teamForKeys = selectedTeamForKeys || NO_TEAM_OPTION;

        return (
            <div>
                {/* Key management tab component - includes team selector */}
                <KeyManagementTab
                    team={teamForKeys}
                    allModels={allModels}
                    allTeams={teams}
                    allUsers={litellmUsers}
                    visibleTeams={visibleTeams}
                    onTeamChange={(team) => setSelectedTeamForKeys(team)}
                    NO_TEAM_OPTION={NO_TEAM_OPTION}
                    fmt2={(val) => val == null ? '$0.00' : `$${Number(val).toFixed(2)}`}
                    fmtDuration={(duration) => {
                        if (!duration) return 'Never';
                        const map = { '1d': 'Daily', '7d': 'Weekly', '30d': 'Monthly', '90d': 'Quarterly' };
                        return map[duration] || duration;
                    }}
                    onRefresh={onRefresh}
                />
            </div>
        );
    };

    // ── Page render ───────────────────────────────────────────────────────────

    const SUB_TAB_LABELS = { 'my-usage': 'My Usage', 'my-team': 'My Team', activity: 'Recent Activity', models: 'Models', users: 'Users', teams: 'Teams', keys: 'Keys', departments: 'Departments', organisations: 'Organisations', overview: 'Overview', reports: 'Reports', admins: 'Admins' };
    const activeTabLabel = SUB_TAB_LABELS[analyticsSubTab] || 'Analytics';

    // Nav icons (16×16 inline SVGs, stroke currentColor)
    const navIcons = {
        overview: (
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="1" y="1" width="6" height="6" rx="1" />
                <rect x="9" y="1" width="6" height="6" rx="1" />
                <rect x="1" y="9" width="6" height="6" rx="1" />
                <rect x="9" y="9" width="6" height="6" rx="1" />
            </svg>
        ),
        analytics: (
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="1,11 4,7 7,9 10,4 13,6 15,3" />
            </svg>
        ),
        keys: (
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="6" cy="7" r="3.5" />
                <line x1="9" y1="9.5" x2="15" y2="15" />
                <line x1="12" y1="12" x2="13.5" y2="10.5" />
            </svg>
        ),
        report: (
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="1" width="10" height="14" rx="1.5" />
                <line x1="6" y1="5" x2="10" y2="5" />
                <line x1="6" y1="8" x2="10" y2="8" />
                <line x1="6" y1="11" x2="8.5" y2="11" />
            </svg>
        ),
    };

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
            background: 'var(--bg)',
        }}>
                {/* Top bar */}
                <div style={{
                    height: 60,
                    flexShrink: 0,
                    background: 'var(--surface)',
                    borderBottom: '1px solid var(--border)',
                    padding: '0 24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}>
                    {/* Back button + Page title */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                        <button onClick={onClose} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--fg-secondary)', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 500, padding: '6px 12px', fontFamily: 'inherit', whiteSpace: 'nowrap', flexShrink: 0 }}>
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="m15 18-6-6 6-6"/>
                            </svg>
                            Dashboard
                        </button>
                        <div>
                            <div style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--fg-primary)' }}>{activeTabLabel}</div>
                            <div style={{ fontSize: '0.75rem', color: 'var(--fg-secondary)', marginTop: 2 }}>Monitor your Ask Engine usage and costs</div>
                        </div>
                    </div>

                    {/* Right controls */}
                    <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                        {/* Search */}
                        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                            <svg
                                width="14" height="14"
                                viewBox="0 0 16 16" fill="none"
                                stroke="var(--fg-secondary)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
                                style={{ position: 'absolute', left: 10, pointerEvents: 'none', zIndex: 1 }}
                            >
                                <circle cx="7" cy="7" r="5" />
                                <line x1="11" y1="11" x2="15" y2="15" />
                            </svg>
                            <input
                                type="text"
                                placeholder="Search..."
                                value={headerSearch}
                                onChange={e => setHeaderSearch(e.target.value)}
                                style={{
                                    background: 'var(--border)',
                                    border: '1px solid var(--border-hover)',
                                    borderRadius: 8,
                                    padding: '7px 12px 7px 32px',
                                    color: 'var(--fg-primary)',
                                    fontSize: '0.82rem',
                                    width: 180,
                                    outline: 'none',
                                    fontFamily: 'inherit',
                                }}
                            />
                            {/* Search results dropdown */}
                            {searchResults && searchResults.length > 0 && (
                                <div style={{
                                    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
                                    background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8,
                                    boxShadow: '0 8px 24px rgba(0,0,0,0.6)', marginTop: 4, overflow: 'hidden',
                                    minWidth: 280,
                                }}>
                                    {searchResults.map((r, i) => (
                                        <div
                                            key={i}
                                            onClick={() => {
                                                const tabMap = { activity: 'activity', models: 'models', users: 'users', teams: 'teams' };
                                                if (tabMap[r.section]) setAnalyticsSubTab(tabMap[r.section]);
                                                setHeaderSearch('');
                                            }}
                                            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', borderBottom: '1px solid var(--border)', cursor: 'pointer' }}
                                            onMouseEnter={e => e.currentTarget.style.background = 'var(--border)'}
                                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                                        >
                                            <div>
                                                <div style={{ fontSize: '0.82rem', color: 'var(--fg-primary)', fontWeight: 600 }}>{r.label}</div>
                                                <div style={{ fontSize: '0.7rem', color: 'var(--fg-secondary)' }}>{r.section} {r.sub ? `· ${r.sub}` : ''}</div>
                                            </div>
                                            <div style={{ fontSize: '0.8rem', color: 'var(--fg-secondary)', fontWeight: 600 }}>{r.value}</div>
                                        </div>
                                    ))}
                                    <div
                                        onClick={() => setHeaderSearch('')}
                                        style={{ padding: '6px 12px', fontSize: '0.72rem', color: 'var(--muted)', textAlign: 'center', cursor: 'pointer' }}
                                    >
                                        Clear
                                    </div>
                                </div>
                            )}
                            {searchResults && searchResults.length === 0 && headerSearch.length > 1 && (
                                <div style={{
                                    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
                                    background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8,
                                    boxShadow: '0 8px 24px rgba(0,0,0,0.6)', marginTop: 4,
                                    padding: '10px 12px', fontSize: '0.8rem', color: 'var(--muted)', minWidth: 200,
                                }}>
                                    No results for "{headerSearch}"
                                </div>
                            )}
                        </div>

                        {/* Notifications icon */}
                        <div style={{ position: 'relative' }}>
                            <button
                                onClick={() => { setNotificationsOpen(v => !v); setHasUnread(false); }}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, display: 'flex', alignItems: 'center', position: 'relative' }}
                            >
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="var(--fg-secondary)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M10 2a6 6 0 0 1 6 6c0 3.5 1.5 5 1.5 5h-15s1.5-1.5 1.5-5a6 6 0 0 1 6-6z" />
                                    <path d="M8.5 17a1.5 1.5 0 0 0 3 0" />
                                </svg>
                                {hasUnread && <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#C4261F', position: 'absolute', top: 0, right: 0 }} />}
                            </button>
                            {notificationsOpen && (
                                <>
                                    <div style={{ position: 'fixed', inset: 0, zIndex: 98 }} onClick={() => setNotificationsOpen(false)} />
                                    <div style={{
                                        position: 'absolute', top: '100%', right: 0, zIndex: 99,
                                        background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10,
                                        boxShadow: '0 8px 24px rgba(0,0,0,0.6)', marginTop: 6,
                                        width: 320, maxHeight: 360, overflowY: 'auto',
                                    }}>
                                        <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', fontSize: '0.82rem', fontWeight: 700, color: 'var(--fg-primary)' }}>Alerts</div>
                                        {notifications.length === 0 ? (
                                            <div style={{ padding: '16px', color: 'var(--muted)', fontSize: '0.82rem', textAlign: 'center' }}>No recent alerts</div>
                                        ) : notifications.map((n, i) => {
                                            const actionLabels = {
                                                team_blocked: 'Team blocked',
                                                team_unblocked: 'Team unblocked',
                                                user_blocked: 'User blocked',
                                                team_budget_updated: 'Budget updated',
                                            };
                                            const ts = n.timestamp ? new Date(n.timestamp).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '';
                                            const dotColor = n.action === 'team_blocked' || n.action === 'user_blocked' ? '#F87171' : '#FBBF24';
                                            return (
                                                <div key={i} style={{ display: 'flex', gap: 10, padding: '10px 16px', borderBottom: '1px solid var(--border)', alignItems: 'flex-start' }}>
                                                    <div style={{ width: 7, height: 7, borderRadius: '50%', background: dotColor, flexShrink: 0, marginTop: 5 }} />
                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                        <div style={{ fontSize: '0.8rem', color: 'var(--fg-primary)', fontWeight: 600 }}>{actionLabels[n.action] || n.action}</div>
                                                        {n.details && <div style={{ fontSize: '0.72rem', color: 'var(--fg-secondary)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n.details}</div>}
                                                        {ts && <div style={{ fontSize: '0.68rem', color: 'var(--muted)', marginTop: 2 }}>{ts}</div>}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Date range select — hidden on Reports view (has its own date controls) */}
                        {analyticsSubTab !== 'reports' && <select
                            value={analyticsTimeRange}
                            onChange={(e) => setAnalyticsTimeRange(e.target.value)}
                            style={{
                                background: 'var(--border)',
                                border: '1px solid var(--border-hover)',
                                borderRadius: 8,
                                padding: '7px 14px',
                                color: 'var(--fg-primary)',
                                fontSize: '0.82rem',
                                cursor: 'pointer',
                                fontFamily: 'inherit',
                                outline: 'none',
                            }}
                        >
                            <option value="last_7_days">Last 7 Days</option>
                            <option value="last_30_days">Last 30 Days</option>
                            <option value="current_month">Current Month</option>
                            <option value="all_time">All Time</option>
                            <option value="custom">Custom Range</option>
                        </select>}

                        {/* Actions dropdown */}
                        {headerActions.length > 0 && (
                            <div style={{ position: 'relative' }}>
                                <button
                                    onClick={() => setActionsOpen(v => !v)}
                                    style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'var(--border)', border: '1px solid var(--border-hover)', borderRadius: 8, color: 'var(--fg-primary)', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600, padding: '7px 14px', fontFamily: 'inherit', whiteSpace: 'nowrap' }}
                                >
                                    Actions
                                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transition: 'transform 0.15s', transform: actionsOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                                        <path d="M2 4l4 4 4-4"/>
                                    </svg>
                                </button>
                                {actionsOpen && (
                                    <>
                                        <div style={{ position: 'fixed', inset: 0, zIndex: 98 }} onClick={() => setActionsOpen(false)} />
                                        <div style={{ position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 99, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.6)', minWidth: 220, overflow: 'hidden' }}>
                                            {headerActions.map((action, i) => (
                                                <button
                                                    key={i}
                                                    onClick={() => { action.onClick(); setActionsOpen(false); }}
                                                    style={{ display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none', borderBottom: i < headerActions.length - 1 ? '1px solid var(--border)' : 'none', color: 'var(--fg-primary)', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 500, padding: '10px 16px', fontFamily: 'inherit' }}
                                                    onMouseEnter={e => e.currentTarget.style.background = 'var(--border)'}
                                                    onMouseLeave={e => e.currentTarget.style.background = 'none'}
                                                >
                                                    {action.label}
                                                </button>
                                            ))}
                                        </div>
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Scrollable content */}
                <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
                    {renderAnalytics()}
                </div>

            <style>{`
                @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
                @keyframes spin { to { transform: rotate(360deg); } }
            `}</style>
        </div>
    );
};

// ── Shared sub-components ─────────────────────────────────────────────────────

const NavItem = ({ isActive, onClick, icon, label }) => {
    const [hovered, setHovered] = React.useState(false);
    const bg = isActive
        ? 'rgba(196,38,31,0.15)'
        : hovered
        ? 'rgba(255,255,255,0.04)'
        : 'none';
    const color = (isActive || hovered) ? 'var(--fg-primary)' : 'var(--fg-secondary)';
    return (
        <button
            onClick={onClick}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            style={{
                width: '100%',
                padding: '9px 20px',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                fontSize: '0.85rem',
                fontWeight: 500,
                borderRadius: 0,
                textAlign: 'left',
                fontFamily: 'inherit',
                background: bg,
                color: color,
            }}
        >
            {icon}
            {label}
        </button>
    );
};

const SectionHeading = ({ children }) => (
    <div style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--fg-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.7rem' }}>{children}</div>
);

// ── Shared styles ─────────────────────────────────────────────────────────────

const pageStyle = {}; // no longer used — kept to avoid reference errors

const filterLabel = {
    fontSize: '0.73rem',
    fontWeight: 600,
    color: 'var(--fg-secondary)',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    marginBottom: '0.25rem',
};

const filterInput = {
    background: 'var(--border)',
    border: '1px solid var(--border-hover)',
    borderRadius: 6,
    color: 'var(--fg-primary)',
    padding: '6px 10px',
    fontSize: '0.82rem',
    fontFamily: 'inherit',
    outline: 'none',
};

const cellStyle = {
    fontSize: '0.85rem',
    color: 'var(--fg-secondary)',
};

const chartCard = {
    background: 'var(--bg)',
    border: '1px solid var(--border)',
    borderRadius: 10,
    padding: '1.1rem 1.25rem',
};

const chartTitle = {
    fontSize: '0.88rem',
    fontWeight: 700,
    color: 'var(--fg-primary)',
    marginBottom: '0.5rem',
};

const btnPrimary = {
    background: '#6366f1',
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    cursor: 'pointer',
    padding: '0.45rem 1rem',
    fontSize: '0.85rem',
    fontWeight: 600,
};

const btnSecondary = {
    background: 'var(--bg)',
    color: 'var(--fg-primary)',
    border: '1px solid var(--border)',
    borderRadius: 6,
    cursor: 'pointer',
    padding: '0.45rem 1rem',
    fontSize: '0.85rem',
};

const btnGhost = {
    background: 'none',
    color: 'var(--muted)',
    border: '1px solid var(--border)',
    borderRadius: 6,
    cursor: 'pointer',
    padding: '0.45rem 0.75rem',
    fontSize: '0.85rem',
};

// ── PropTypes ─────────────────────────────────────────────────────────────────

UsageCostDashboard.propTypes = {
    isAdmin: PropTypes.bool.isRequired,
    teams: PropTypes.array,
    groups: PropTypes.array,
    users: PropTypes.array,
    allModels: PropTypes.array,
    onClose: PropTypes.func.isRequired,
    userEmail: PropTypes.string,
    hiddenTeamIds: PropTypes.array,
    onHiddenTeamIdsChange: PropTypes.func,
    initialTab: PropTypes.oneOf(['overview', 'analytics', 'activity', 'report']),
    onAnalyticsSubTabChange: PropTypes.func,
    onSwitchToReports: PropTypes.func,
    initialReportType: PropTypes.string,
};

export default UsageCostDashboard;

